<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PlaceMap                                              |
| Section: components                                              |
| Purpose: Interactive map/globe view for place-based records.     |
+------------------------------------------------------------------+
-->
<template>
    <div class="PlaceMap">
        <!-- Loader -->
        <div class="map-loader overlay"
            v-show="(!isSurnameMode && loading) || (isSurnameMode && surnameLoading && !surnameLabels.length)">
            <div class="skeleton skeleton-map"></div>
        </div>

        <section v-if="!hideFilters && !tourActive" ref="mapLayerControl" class="map-mode-overlay"
            :class="{ 'map-mode-overlay--open': mapLayerMenuOpen }"
            :aria-label="$t('placeMap.mapStyle')">
            <button type="button" class="map-mode-trigger" :aria-label="$t('placeMap.mapStyle')"
                :aria-expanded="mapLayerMenuOpen ? 'true' : 'false'" @click.stop="toggleMapLayerMenu">
                <span class="map-mode-tile__preview"
                    :class="{ 'map-mode-tile__preview--globe': useGlobeProjection && !useSatelliteStyle }">
                    <img :src="useSatelliteStyle ? '/assets/satellite.png' : '/assets/standard.png'"
                        alt="" />
                </span>
                <span class="map-mode-tile__label">{{ $t('placeMap.mapStyle') }}</span>
            </button>

            <div v-if="mapLayerMenuOpen" class="map-layer-menu">
                <p class="map-layer-menu__title">{{ $t('placeMap.mapStyle') }}</p>
                <div class="map-layer-menu__tiles" role="group" :aria-label="$t('placeMap.mapStyle')">
                    <button type="button" class="map-layer-option"
                        :class="{ 'map-layer-option--active': !useSatelliteStyle }"
                        :aria-pressed="!useSatelliteStyle" @click.stop="selectMapLayerStyle(false)">
                        <span class="map-layer-option__preview">
                            <img src="/assets/standard.png" alt="" />
                        </span>
                        <span>{{ $t('placeMap.standard') }}</span>
                    </button>
                    <button type="button" class="map-layer-option"
                        :class="{ 'map-layer-option--active': useSatelliteStyle }"
                        :aria-pressed="useSatelliteStyle" @click.stop="selectMapLayerStyle(true)">
                        <span class="map-layer-option__preview">
                            <img src="/assets/satellite.png" alt="" />
                        </span>
                        <span>{{ $t('placeMap.satellite') }}</span>
                    </button>
                </div>

                <label class="map-layer-menu__toggle">
                    <input type="checkbox" :checked="useGlobeProjection"
                        @change="setGlobeProjection($event.target.checked)" />
                    <span class="toggle-slider"></span>
                    <span>{{ $t('placeMap.globe') }}</span>
                </label>
            </div>
        </section>

        <div v-if="!hideSearch" class="place-search-overlay">
            <div :class="['search-box', { focused: placeSearchFocused }]" ref="placeSearchBox" @click.stop="onMapSearchFocus">
                <div class="search-icon">
                    <img src="@/assets/icons/magnifier.svg" alt="" />
                </div>
                <input v-model="placeSearchQuery" type="text" :placeholder="$t('placeMap.searchPlaceholder')"
                    :aria-label="$t('placeMap.searchPlaceAriaLabel')" @input="onPlaceSearchInput"
                    @focus="onMapSearchFocus" @blur="onPlaceSearchBlur"
                    @keyup.enter="selectFirstSearchSuggestion" />
                <button v-if="placeSearchQuery.trim()" type="button" class="reset-button"
                    :aria-label="$t('placeMap.clear')" @click="clearPlaceSearch">
                    <span aria-hidden="true">&#x2715;</span>
                </button>
                <button v-else type="button" @click="selectFirstSearchSuggestion">
                    <img src="@/assets/icons/search_submit.svg" alt="" />
                </button>

                <ul v-if="showSearchPanel" class="search-suggestions map-search-suggestions">
                    <template v-if="isMapSearchEmpty">
                        <li v-if="searchPresetsLoading" class="map-search-suggestions__status">
                            <span>{{ $t('placeMap.searchPresets.loading') }}</span>
                        </li>
                        <template v-if="topPlacePresets.length">
                            <li class="map-search-suggestions__heading">{{ $t('placeMap.searchPresets.topPlaces') }}</li>
                            <li v-for="place in topPlacePresets" :key="`preset-place-${place.ID || place.place}`"
                                @mousedown.prevent @click="selectPlaceFromSuggestion(place)" tabindex="0" role="button"
                                @keydown.enter.prevent="selectPlaceFromSuggestion(place)"
                                @keydown.space.prevent="selectPlaceFromSuggestion(place)">
                                <span>{{ place.place }}</span>
                                <span>{{ $t('placeMap.searchPresets.eventCount', { count: place.eventCount }) }}</span>
                            </li>
                        </template>
                        <template v-if="presetPeople.length">
                            <li v-if="topPlacePresets.length" class="map-search-suggestions__separator" role="separator"
                                aria-hidden="true"></li>
                            <li class="map-search-suggestions__heading">{{ $t('placeMap.searchPresets.people') }}</li>
                            <li v-for="person in presetPeople" :key="`preset-person-${person.personID}`"
                                @mousedown.prevent @click="selectPersonFromSuggestion(person)" tabindex="0" role="button"
                                @keydown.enter.prevent="selectPersonFromSuggestion(person)"
                                @keydown.space.prevent="selectPersonFromSuggestion(person)">
                                <span>{{ person.firstname }} {{ person.lastname }}</span>
                                <span>{{ formatPersonDates(person) }}</span>
                            </li>
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="placeSuggestions.length">
                            <li class="map-search-suggestions__heading">{{ $t('placeMap.searchGroups.places') }}</li>
                            <li v-for="(place, index) in placeSuggestions" :key="`place-${place.ID || index}-${place.place}`"
                                @mousedown.prevent @click="selectPlaceFromSuggestion(place)" tabindex="0" role="button"
                                @keydown.enter.prevent="selectPlaceFromSuggestion(place)"
                                @keydown.space.prevent="selectPlaceFromSuggestion(place)">
                                <span>{{ place.place }}</span>
                                <span>{{ $t('placeMap.searchResultTypes.place') }}</span>
                            </li>
                        </template>
                        <template v-if="personSuggestions.length">
                            <li v-if="placeSuggestions.length" class="map-search-suggestions__separator"
                                role="separator" aria-hidden="true"></li>
                            <li class="map-search-suggestions__heading">{{ $t('placeMap.searchGroups.people') }}</li>
                            <li v-for="person in personSuggestions" :key="`person-${person.personID}`"
                                @mousedown.prevent @click="selectPersonFromSuggestion(person)" tabindex="0" role="button"
                                @keydown.enter.prevent="selectPersonFromSuggestion(person)"
                                @keydown.space.prevent="selectPersonFromSuggestion(person)">
                                <span>{{ person.firstname }} {{ person.lastname }}</span>
                                <span>{{ formatPersonDates(person) }} · {{ $t('placeMap.searchResultTypes.person') }}</span>
                            </li>
                        </template>
                    </template>
                </ul>
            </div>
        </div>

        <section v-if="!hideFilters && !tourActive" class="map-event-filter-overlay"
            :aria-label="$t('placeMap.events')">
            <div class="map-event-filter-row">
                <div class="map-event-filter-group map-event-filter-group--display" role="group"
                    :aria-label="$t('placeMap.surnameLandscape.viewSelector')">
                    <button type="button" class="map-filter-pill"
                        :class="{ 'map-filter-pill--active': !isSurnameMode }"
                        :aria-pressed="!isSurnameMode" @click="setDisplayMode('places')">
                        {{ $t('placeMap.surnameLandscape.places') }}
                    </button>
                    <button type="button" class="map-filter-pill"
                        :class="{ 'map-filter-pill--active': isSurnameMode }"
                        :aria-pressed="isSurnameMode" @click="setDisplayMode('surnames')">
                        {{ $t('placeMap.surnameLandscape.surnames') }}
                    </button>
                </div>

                <div class="map-event-filter-group map-event-filter-group--scope" role="radiogroup"
                    :aria-label="$t('placeMap.familyMembers')">
                    <label class="map-filter-pill" :class="{ 'map-filter-pill--active': onlyCorePeople === false }">
                        <input type="radio" name="globeFamilyFilter" class="map-filter-pill__input"
                            v-model="onlyCorePeople" :value="false" />
                        <span>{{ $t('placeMap.everyone') }}</span>
                    </label>
                    <label class="map-filter-pill" :class="{ 'map-filter-pill--active': onlyCorePeople === true }">
                        <input type="radio" name="globeFamilyFilter" class="map-filter-pill__input"
                            v-model="onlyCorePeople" :value="true" />
                        <span>{{ $t('placeMap.ancestors') }}</span>
                    </label>
                </div>

                <div class="map-event-filter-group" role="radiogroup" :aria-label="$t('placeMap.events')">
                    <label class="map-filter-pill" :class="{ 'map-filter-pill--active': isAllSelected }">
                        <input type="radio" name="globeTypeFilter" value="" v-model="selectedType"
                            class="map-filter-pill__input" />
                        <span>{{ $t('placeMap.all') }}</span>
                    </label>
                    <label v-for="t in availableTypes" :key="`globe-filter-${t}`" class="map-filter-pill"
                        :class="{ 'map-filter-pill--active': selectedType === t }">
                        <input type="radio" :value="t" v-model="selectedType" name="globeTypeFilter"
                            class="map-filter-pill__input" />
                        <span>{{ localizedTypeLabels[t] || t }}</span>
                    </label>
                </div>

                <div v-if="personIDFilter" class="map-event-filter-group map-event-filter-group--person">
                    <span class="map-person-filter-chip">
                        <span>{{ personSearchQuery || personIDFilter }}</span>
                        <button type="button" :aria-label="$t('placeMap.clear')" @click="clearPersonFilter">
                            <span aria-hidden="true">&#x2715;</span>
                        </button>
                    </span>
                </div>
            </div>
        </section>

        <div v-if="canShowTourControls && !tourActive && !isSurnameMode" class="tour-launch-overlay">
            <div class="tour-mode-picker" role="radiogroup" :aria-label="$t('placeMap.tour.modeSelector')">
                <button v-for="mode in tourModes" :key="mode.id" type="button" class="tour-mode-picker__button"
                    :class="{ 'tour-mode-picker__button--active': tourSelectedModeId === mode.id }"
                    @click="selectTourMode(mode.id)" :aria-pressed="tourSelectedModeId === mode.id">
                    {{ $t(mode.labelKey) }}
                </button>
            </div>
            <button type="button" class="tour-launch-button" @click="startPlaceTour"
                :aria-label="$t('placeMap.tour.startAria')" :title="$t('placeMap.tour.startAria')">
                <img src="@/assets/icons/airplane-outline.svg" alt="" />
                <span>{{ $t('placeMap.tour.start') }}</span>
            </button>
        </div>

        <section v-if="tourActive && currentTourStop" class="place-tour-panel" aria-live="polite">
            <div class="place-tour-panel__hud">
                <div>
                    <span class="place-tour-panel__mode">{{ currentTourModeLabel }}</span>
                    <span class="place-tour-panel__phase">{{ tourPhaseLabel }}</span>
                </div>
                <div class="place-tour-panel__readouts">
                    <span>{{ $t('placeMap.tour.altitude') }} {{ tourAltitudeLabel }}</span>
                    <span>{{ $t('placeMap.tour.leg') }} {{ tourDistanceLabel }}</span>
                </div>
            </div>
            <div class="place-tour-panel__kicker">
                {{ $t('placeMap.tour.kicker', { current: tourCurrentIndex + 1, total: resolvedTourStops.length }) }}
            </div>
            <h2 class="place-tour-panel__title">{{ currentTourTitle }}</h2>
            <div class="place-tour-panel__placenames">
                <span>{{ currentTourOldName }}</span>
                <span>{{ currentTourEra }}</span>
            </div>
            <p class="place-tour-panel__mode-description">{{ currentTourModeDescription }}</p>
            <p class="place-tour-panel__summary">{{ currentTourSummary }}</p>

            <div class="place-tour-panel__stats" :aria-busy="tourStatsLoading ? 'true' : 'false'">
                <div class="place-tour-panel__stat">
                    <span class="place-tour-panel__stat-value">{{ tourTotalEvents }}</span>
                    <span class="place-tour-panel__stat-label">{{ $t('placeMap.tour.eventsCount') }}</span>
                </div>
                <div class="place-tour-panel__stat">
                    <span class="place-tour-panel__stat-value">{{ tourSubPlaceCount }}</span>
                    <span class="place-tour-panel__stat-label">{{ $t('placeMap.tour.subPlacesCount') }}</span>
                </div>
                <div class="place-tour-panel__stat place-tour-panel__stat--wide">
                    <span class="place-tour-panel__stat-value">{{ currentTourStatsLabel }}</span>
                    <span class="place-tour-panel__stat-label">{{ $t('placeMap.tour.focus') }}</span>
                </div>
            </div>

            <div class="place-tour-panel__route-bar" aria-hidden="true">
                <span :style="{ width: `${tourProgressPct}%` }"></span>
            </div>
            <div class="place-tour-panel__progress" aria-hidden="true">
                <span v-for="(stop, index) in resolvedTourStops" :key="stop.id" class="place-tour-panel__dot"
                    :class="{ 'place-tour-panel__dot--active': index === tourCurrentIndex }"></span>
            </div>

            <div class="place-tour-panel__actions">
                <button type="button" class="place-tour-panel__icon-button" @click="goToPreviousTourStop"
                    :aria-label="$t('placeMap.tour.previous')" :title="$t('placeMap.tour.previous')">
                    <span aria-hidden="true">&lt;</span>
                </button>
                <button type="button" class="place-tour-panel__primary-button" @click="toggleTourPlayback">
                    {{ tourPaused ? $t('placeMap.tour.resume') : $t('placeMap.tour.pause') }}
                </button>
                <button type="button" class="place-tour-panel__icon-button" @click="goToNextTourStop"
                    :aria-label="$t('placeMap.tour.next')" :title="$t('placeMap.tour.next')">
                    <span aria-hidden="true">&gt;</span>
                </button>
                <button type="button" class="place-tour-panel__secondary-button" @click="viewCurrentTourStopEvents">
                    {{ $t('placeMap.tour.viewEvents') }}
                </button>
                <button type="button" class="place-tour-panel__icon-button" @click="closePlaceTour"
                    :aria-label="$t('placeMap.tour.close')" :title="$t('placeMap.tour.close')">
                    <span aria-hidden="true">x</span>
                </button>
            </div>
        </section>

        <section v-if="showMapStatsLine" class="place-stats-line" aria-live="polite">
            <span class="place-stats-line__label">{{ continentStatsLabel }}</span>
            <span v-for="continent in activeContinentBreakdown" :key="continent.id" class="place-stats-line__item">
                {{ continent.label }} {{ continent.percentage }}%
            </span>
        </section>

        <div v-if="isSurnameMode && !surnameLoading && surnameError" class="surname-landscape-state" role="alert">
            {{ $t('placeMap.surnameLandscape.error') }}
        </div>
        <div v-else-if="isSurnameMode && !surnameLoading && !surnameLabels.length"
            class="surname-landscape-state" role="status">
            {{ $t('placeMap.surnameLandscape.empty') }}
        </div>

        <!-- Mapbox Globe -->
        <div ref="mapContainer" class="map-container"></div>

        <!-- Filter Info Banner (temporarily hidden) -->
        <!-- <FilterInfoBanner :visible="filterBannerVisible" :title="filterBannerInfo.title"
            :description="filterBannerInfo.description" @close="closeBanner" /> -->

        <!-- Place Events Side Panel -->
        <!-- Teleported into mapContainer when fullscreen so it appears above the fullscreen layer -->
        <Teleport :to="mapContainerEl" :disabled="!isFullscreen || !mapContainerEl">
            <PlaceEventsPanel :visible="sidePanelVisible" :placeId="selectedPlaceId" :placeName="selectedPlaceName"
                :placeIds="selectedPlaceIds" :counts="selectedPlaceCounts" :subPlaces="selectedPlaceSubPlaces"
                :countsLoading="selectedPlaceCountsLoading" :subPlacesLoading="selectedPlaceSubPlacesLoading"
                :inline="hideFilters || isFullscreen" @select-sub-place="selectPlaceFromSuggestion" @close="closeSidePanel" />
        </Teleport>

        <Teleport :to="mapContainerEl" :disabled="!isFullscreen || !mapContainerEl">
            <SurnameProfilePanel :visible="surnameProfileVisible" :loading="surnameProfileLoading"
                :error="surnameProfileError" :profile="surnameProfile" :selectedLabel="selectedSurnameLabel"
                :inline="hideFilters || isFullscreen" @select-area="focusSurnameArea" @close="closeSurnameProfile" />
        </Teleport>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { markRaw } from 'vue';
import { useAuth } from '@/composables/useAuth';
import {
    fetchPeopleDashboardStats,
    fetchPlaceSurnameProfile,
    fetchPlaceSurnames,
    fetchPlaces,
    fetchPlaceEvents,
} from "@/api/apiService.js";
import { getEventLocations, searchPeopleSuggestions } from "@/api/personService.js";
import PlaceEventsPanel from '@/components/PlaceEventsPanel.vue';
import SurnameProfilePanel from '@/components/SurnameProfilePanel.vue';
// import FilterInfoBanner from '@/components/FilterInfoBanner.vue';
import dnaData from '@/data/dna-results.json';
import haplogroupData from '@/data/haplogroups.json';
import placeTourStops, { placeTourModes } from '@/data/placeTourHighlights';

// Set Mapbox access token
mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;

export default {
    name: "PlaceMap",
    props: {
        hideFilters: {
            type: Boolean,
            default: false
        },
        hideSearch: {
            type: Boolean,
            default: false
        },
        initialDisplayMode: {
            type: String,
            default: 'places',
            validator: value => ['places', 'surnames'].includes(value)
        },
        initialCenter: {
            type: Array,
            default: () => [20, 10],
            validator: value => (
                value.length === 2
                && value.every(coordinate => Number.isFinite(coordinate))
                && value[0] >= -180
                && value[0] <= 180
                && value[1] >= -90
                && value[1] <= 90
            )
        },
        initialZoom: {
            type: Number,
            default: 1.5,
            validator: value => Number.isFinite(value) && value >= 0 && value <= 22
        },
        forceGlobeView: {
            type: Boolean,
            default: false
        },
        immediateRotation: {
            type: Boolean,
            default: true
        },
        cooperativeGestures: {
            type: Boolean,
            default: false
        }
    },
    components: {
        PlaceEventsPanel,
        SurnameProfilePanel,
        // FilterInfoBanner,
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            // Map instances
            mapInstance: null,

            // View toggle
            useGlobeView: true, // Keep /plaatsen on Mapbox.
            useGlobeProjection: true,

            // data
            places: [],
            placeGroups: [],
            placeGroupByPlaceId: {},
            placeGroupByPlaceName: {},
            placeGroupByAlias: {},
            mapCenter: [0.0, 0.0],
            zoom: 2,
            loading: true,

            // Surname landscape
            displayMode: this.initialDisplayMode,
            surnameLabels: [],
            surnameLabelById: {},
            surnameLandscapeMeta: null,
            surnameLoading: false,
            surnameError: null,
            surnameZoomTier: Math.max(1, Math.min(10, Math.floor(this.initialZoom))),
            surnameRequestController: null,
            surnameRequestPromise: null,
            surnameRequestSignature: null,
            surnameLoadedSignature: null,
            surnameProfileVisible: false,
            surnameProfileLoading: false,
            surnameProfileError: null,
            surnameProfile: null,
            selectedSurnameLabel: null,
            surnameProfileRequestId: 0,
            hoveredSurnameLabel: null,
            surnameHoverProfile: null,
            surnameHoverTimer: null,
            surnameHoverRequestId: 0,
            surnameProfileCache: markRaw(new Map()),
            surnameProfileInFlight: markRaw(new Map()),

            // filters
            availableTypes: ["birth", "baptism", "education", "marriage", "death", "burial", "address"],
            selectedType: "",
            typeLabels: {
                birth: "Geboorte",
                death: "Overlijden",
                baptism: "Doop",
                marriage: "Huwelijk",
                burial: "Begrafenis",
                address: "Adres",
                residence: "Woonplaats",
            },

            yearFrom: null,
            yearTo: null,
            dateDebounce: null,

            periods: [
                // INDONESIË
                {
                    id: 'voc',
                    region: 'indonesia',
                    label: '1602–1800 • Tijd van de VOC',
                    fromYear: 1602,
                    toYear: 1800,
                },
                {
                    id: 'id_french',
                    region: 'indonesia',
                    label: '1806–1811 • Frans interregnum in Nederlands-Indië',
                    fromYear: 1806,
                    toYear: 1811,
                },
                {
                    id: 'id_british',
                    region: 'indonesia',
                    label: '1811–1816 • Brits interregnum in Nederlands-Indië',
                    fromYear: 1811,
                    toYear: 1816,
                },
                {
                    id: 'ned_indie',
                    region: 'indonesia',
                    label: '1800–1942 • Nederlands-Indië (excl. 1806–1816)',
                    fromYear: 1800,
                    toYear: 1942,
                },
                {
                    id: 'japanese',
                    region: 'indonesia',
                    label: '1942–1945 • Japanse bezetting',
                    fromYear: 1942,
                    toYear: 1945
                },
                {
                    id: 'ri',
                    region: 'indonesia',
                    label: '1945–heden • Republiek Indonesië',
                    fromYear: 1945,
                    toYear: new Date().getFullYear(),
                },

                // NEDERLAND
                {
                    id: 'rep_zvn',
                    region: 'netherlands',
                    label: '1588–1795 • Republiek der Zeven Verenigde Nederlanden',
                    fromYear: 1588,
                    toYear: 1795
                },
                {
                    id: 'bataaf',
                    region: 'netherlands',
                    label: '1795–1806 • Bataafse Republiek',
                    fromYear: 1795,
                    toYear: 1806
                },
                {
                    id: 'kon_holland',
                    region: 'netherlands',
                    label: '1806–1810 • Koninkrijk Holland (Lodewijk Napoleon)',
                    fromYear: 1806,
                    toYear: 1810
                },
                {
                    id: 'french_empire',
                    region: 'netherlands',
                    label: '1810–1813 • Inlijving bij het Franse Keizerrijk',
                    fromYear: 1810,
                    toYear: 1813
                },
                {
                    id: 'vk_nl',
                    region: 'netherlands',
                    label: '1815–1830 • Verenigd Koninkrijk der Nederlanden',
                    fromYear: 1815,
                    toYear: 1830
                },
                {
                    id: 'koninkrijk',
                    region: 'netherlands',
                    label: '1830–1940 • Koninkrijk der Nederlanden (19e & begin 20e eeuw)',
                    fromYear: 1830,
                    toYear: 1940
                },
                {
                    id: 'ww2',
                    region: 'netherlands',
                    label: '1940–1945 • Duitse bezetting',
                    fromYear: 1940,
                    toYear: 1945
                },
                {
                    id: 'modern_nl',
                    region: 'netherlands',
                    label: '1945–heden • Modern Nederland',
                    fromYear: 1945,
                    toYear: new Date().getFullYear()
                },
            ],

            selectedCentury: "",

            regionViews: {
                indonesia: {
                    center: [-2, 118],
                    zoom: 5
                },
                netherlands: {
                    center: [52.2, 5.3],
                    zoom: 6
                }
            },

            activeRegion: null,

            corePersonIds: [
                'I3', 'I1', 'I10', 'I42', 'I45', 'I48', 'I51', 'I77', 'I100', 'I101', 'I78', 'I102', 'I103', 'I52', 'I79', 'I104', 'I105', 'I80', 'I106', 'I107', 'I47', 'I49', 'I53', 'I69', 'I108', 'I109', 'I9', 'I11', 'I70', 'I110', 'I111', 'I54', 'I71', 'I112', 'I113', 'I50', 'I55', 'I73', 'I114', 'I115', 'I74', 'I116', 'I117', 'I2', 'I16', 'I18', 'I1171', 'I1172', 'I1173', 'I1174', 'I1175', 'I1176', 'I1177', 'I1179', 'I1170', 'I1178', 'I56', 'I75', 'I118', 'I1180', 'I1183', 'I1185', 'I1186', 'I119', 'I12', 'I76', 'I120', 'I121', 'I122', 'I123', 'I124', 'I125', 'I126', 'I127', 'I128', 'I129', 'I13', 'I130', 'I15', 'I19', 'I20', 'I23', 'I1305', 'I1306', 'I131', 'I132', 'I133', 'I134', 'I135', 'I136', 'I137', 'I72', 'I138', 'I139', 'I14', 'I22', 'I1404', 'I1405', 'I17', 'I294', 'I680', 'I1463', 'I1464', 'I1628', 'I1629', 'I1630', 'I1631', 'I28', 'I30', 'I35', 'I176', 'I177', 'I31', 'I37', 'I179', 'I180', 'I29', 'I32', 'I39', 'I185', 'I186', 'I33', 'I41', 'I187', 'I188', 'I40', 'I190', 'I191', 'I36', 'I193', 'I194', 'I38', 'I195', 'I196', 'I46', 'I202', 'I203', 'I21', 'I219', 'I220', 'I221', 'I222', 'I223', 'I224', 'I225', 'I226', 'I231', 'I232', 'I233', 'I234', 'I235', 'I236', 'I237', 'I238', 'I239', 'I24', 'I240', 'I241', 'I242', 'I243', 'I244', 'I245', 'I246', 'I247', 'I248', 'I249', 'I25', 'I250', 'I2500', 'I2501', 'I251', 'I252', 'I253', 'I254', 'I255', 'I256', 'I257', 'I258', 'I259', 'I26', 'I260', 'I261', 'I262', 'I263', 'I264', 'I265', 'I266', 'I267', 'I268', 'I269', 'I27', 'I270', 'I271', 'I272', 'I273', 'I34', 'I280', 'I281', 'I282', 'I283', 'I284', 'I285', 'I286', 'I98', 'I287', 'I288', 'I293', 'I303', 'I304', 'I322', 'I323', 'I324', 'I325', 'I326', 'I327', 'I328', 'I329', 'I330', 'I331', 'I332', 'I333', 'I334', 'I3341', 'I3342', 'I335', 'I336', 'I337', 'I338', 'I339', 'I340', 'I341', 'I342', 'I343', 'I344', 'I345', 'I346', 'I347', 'I348', 'I349', 'I350', 'I351', 'I352', 'I353', 'I354', 'I355', 'I356', 'I357', 'I358', 'I359', 'I360', 'I361', 'I362', 'I363', 'I364', 'I365', 'I366', 'I367', 'I368', 'I369', 'I370', 'I371', 'I372', 'I373', 'I384', 'I385', 'I386', 'I387', 'I393', 'I394', 'I395', 'I396', 'I397', 'I398', 'I410', 'I411', 'I412', 'I413', 'I414', 'I415', 'I418', 'I419', 'I423', 'I424', 'I425', 'I426', 'I622', 'I623', 'I629', 'I630', 'I638', 'I639', 'I667', 'I668', 'I669', 'I670', 'I681', 'I696', 'I697', 'I700', 'I701', 'I711', 'I712', 'I739', 'I740',
            ],
            onlyCorePeople: false,

            // Person filter for globe view
            personIDFilter: "",
            personIDErrorMsg: "",
            personEventLocations: [], // Event locations for the filtered person
            personChronologicalSegments: [], // Arcs connecting places chronologically

            // Person autocomplete
            personSearchQuery: "",
            personSuggestions: [],
            showPersonSuggestions: false,
            suggestionSearchTimeout: null,

            // Place autocomplete (globe overlay)
            placeSearchQuery: "",
            placeSuggestions: [],
            showPlaceSuggestions: false,
            placeSearchFocused: false,
            placeSuggestionSearchTimeout: null,
            placePresetStats: [],
            presetPeople: [],
            searchPresetsLoading: false,
            searchPresetsLoaded: false,

            // Curated administrative place areas
            placeAreaFeatureCollection: { type: 'FeatureCollection', features: [] },
            placeAreaFeatureById: {},
            placeAreaFeatureByAlias: {},
            placeAreaGeometryIndexCache: markRaw(new Map()),
            hoveredPlaceAreaFeatureId: null,
            hoveredMapboxCountryFeatureId: null,
            areaLayerFadeStartZoom: 6,
            areaLayerFadeEndZoom: 8,

            // Mapbox specific
            mapboxMarkers: [],
            mapboxPopups: [],
            hoveredUnclusteredFeatureId: null,
            popupEventCountCache: {},
            mapboxNavigationControl: null,
            mapboxFullscreenControl: null,
            mapboxAttributionControl: null,

            // Fullscreen tracking
            isFullscreen: false,
            mapContainerEl: null,

            // Auto-rotation
            rotationAnimation: null,
            idleTimeout: null,
            userInteracting: false,
            rotationSpeed: 2, // degrees per second for smooth rotation
            idleDelay: 10000, // 10 seconds
            enableIdleRotation: false, // Temporarily disable idle rotation animation

            // Cinematic intro
            enableGlobeIntro: false, // Temporarily disable intro animation
            hasPlayedIntro: false,
            introDurationMs: 8800,

            // Side panel for globe view
            sidePanelVisible: false,
            selectedPlaceId: null,
            selectedPlaceIds: [],
            selectedPlaceName: '',
            selectedPlaceCounts: null,
            selectedPlaceSubPlaces: [],
            selectedPlaceCountsLoading: false,
            selectedPlaceSubPlacesLoading: false,
            selectedPlacePanelRequestId: 0,

            // DNA Results
            showDNAResults: false,
            dnaTestSubjects: [],
            dnaCompanies: [],
            selectedDNASubjectId: 'person_main',
            selectedDNACompanyId: 'ancestry',
            dnaLayerIds: [], // Track which DNA layers are currently on the map
            dnaCountryGeometryByName: {},

            // Haplogroups
            selectedHaplogroupPersonId: null, // Selected person for haplogroup view
            selectedHaplogroupIds: [], // Array of haplogroup IDs to display
            haplogroupLayerIds: [], // Track which haplogroup layers are currently on the map
            currentHaplogroupPopup: null, // Track current popup to close it when opening a new one
            currentDnaPopup: null, // Track current DNA popup to close it when opening a new one

            // Map style
            useSatelliteStyle: false,
            mapLayerMenuOpen: false,

            // Curated cinematic tour
            tourLauncherEnabled: false,
            tourActive: false,
            tourPaused: false,
            tourSelectedModeId: 'family-arc',
            tourCurrentIndex: 0,
            tourCurrentStepIndex: 0,
            tourPhase: 'approach',
            tourPlaces: [],
            tourPlacesLoaded: false,
            resolvedTourStops: [],
            tourAutoplayTimeout: null,
            tourTimelineTimers: [],
            tourSceneLayerIds: [],
            tourSceneSourceIds: [],
            tourSceneTerrainEnabled: false,
            tourStatsLoading: false,
            tourCurrentCounts: {},
            tourCurrentSubPlaces: [],
            tourStatsRequestId: 0,
        };
    },
    computed: {
        isSurnameMode() {
            return this.displayMode === 'surnames';
        },
        indonesiaPeriods() {
            return this.periods.filter(p => p.region === 'indonesia');
        },
        netherlandsPeriods() {
            return this.periods.filter(p => p.region === 'netherlands');
        },
        dateParams() {
            const p = {};
            const clamp = y => (y && String(y).match(/^\d{4}$/) ? String(y) : null);
            const yf = clamp(this.yearFrom);
            const yt = clamp(this.yearTo);
            if (yf) p.from = `${yf}-01-01`;
            if (yt) p.to = `${yt}-12-31`;
            return p;
        },
        placesWithCoordinates() {
            return this.places.filter(
                (place) =>
                    place.latitude !== "" &&
                    place.longitude !== "" &&
                    !isNaN(parseFloat(place.latitude)) &&
                    !isNaN(parseFloat(place.longitude))
            );
        },
        markerPlaces() {
            const places = this.useGlobeView
                ? this.placesWithCoordinates.filter((place) => !this.resolvePlaceAreaFeature(place))
                : this.placesWithCoordinates;

            return this.buildGroupedMarkerPlaces(places);
        },
        areaPlaces() {
            if (!this.useGlobeView) return [];

            const areaPlaces = this.placesWithCoordinates
                .map((place) => {
                    const areaFeature = this.resolvePlaceAreaFeature(place);
                    if (!areaFeature) return null;

                    return {
                        ...place,
                        areaFeature,
                    };
                })
                .filter(Boolean);

            const ungroupedAreaPlaces = [];
            const groupedAreaPlaces = new Map();

            areaPlaces.forEach((place) => {
                const placeGroupId = place.areaFeature?.properties?.placeGroupId;
                if (!placeGroupId) {
                    ungroupedAreaPlaces.push(place);
                    return;
                }

                if (!groupedAreaPlaces.has(placeGroupId)) {
                    groupedAreaPlaces.set(placeGroupId, []);
                }
                groupedAreaPlaces.get(placeGroupId).push(place);
            });

            groupedAreaPlaces.forEach((members, placeGroupId) => {
                const group = this.placeGroups.find((candidate) => candidate.id === placeGroupId);
                const groupedPlace = this.buildPlaceGroupMarker(group, members) || members[0];
                if (!groupedPlace) return;

                ungroupedAreaPlaces.push({
                    ...groupedPlace,
                    areaFeature: members[0].areaFeature,
                });
            });

            return ungroupedAreaPlaces;
        },
        mapBoundsPlaces() {
            return this.useGlobeView
                ? [...this.markerPlaces, ...this.areaPlaces]
                : this.markerPlaces;
        },
        placeInsightSourcePlaces() {
            const uniquePlaces = new Map();

            this.placesWithCoordinates.forEach((place) => {
                const key = String(place.ID || `${place.place}-${place.latitude}-${place.longitude}`);
                if (!uniquePlaces.has(key)) {
                    uniquePlaces.set(key, place);
                }
            });

            return Array.from(uniquePlaces.values());
        },
        placeContinentBreakdown() {
            const counts = this.placeInsightSourcePlaces.reduce((acc, place) => {
                const continent = this.classifyPlaceContinent(place);
                if (continent) {
                    acc[continent] = (acc[continent] || 0) + 1;
                }
                return acc;
            }, {});
            const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
            if (!total) return [];

            return Object.entries(counts)
                .map(([id, count]) => ({
                    id,
                    count,
                    label: this.$t(`placeMap.stats.continentNames.${id}`),
                    percentage: this.formatContinentPercentage(count, total),
                }))
                .sort((a, b) => b.count - a.count);
        },
        surnameContinentBreakdown() {
            if (this.surnameProfileVisible && (this.surnameProfileLoading || this.surnameProfileError)) return [];

            const meta = this.surnameProfileVisible
                ? this.surnameProfile?.meta
                : this.surnameLandscapeMeta;
            const continents = Array.isArray(meta?.continentBreakdown)
                ? meta.continentBreakdown
                : [];
            const total = Number(meta?.continentLinkCount)
                || continents.reduce((sum, continent) => sum + Number(continent.count || 0), 0);
            if (!total) return [];

            return continents
                .map((continent) => ({
                    id: continent.id,
                    count: Number(continent.count || 0),
                    label: this.$t(`placeMap.stats.continentNames.${continent.id}`),
                    percentage: this.formatContinentPercentage(Number(continent.count || 0), total),
                }))
                .filter(continent => continent.id && continent.count > 0)
                .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
        },
        activeContinentBreakdown() {
            return this.isSurnameMode
                ? this.surnameContinentBreakdown
                : this.placeContinentBreakdown;
        },
        continentStatsLabel() {
            if (!this.isSurnameMode) return this.$t('placeMap.stats.continents');

            const surname = this.surnameProfile?.summary?.surname || this.selectedSurnameLabel?.surname;
            if (this.surnameProfileVisible && surname) {
                return this.$t('placeMap.stats.selectedSurnameContinents', { surname });
            }
            return this.$t('placeMap.stats.surnameContinents');
        },
        showMapStatsLine() {
            if (this.hideFilters || this.tourActive || !this.activeContinentBreakdown.length) return false;
            if (!this.isSurnameMode) return !this.loading;

            return !this.surnameLoading
                && !this.surnameError
                && (!this.surnameProfileVisible || (!this.surnameProfileLoading && !this.surnameProfileError));
        },
        isMapSearchEmpty() {
            return !String(this.placeSearchQuery || '').trim();
        },
        topPlacePresets() {
            const presets = [];
            const seen = new Set();

            this.placePresetStats.forEach((row) => {
                const place = this.resolvePresetPlace(row.name);
                if (!place) return;

                const key = String(place.ID || `${place.latitude},${place.longitude},${place.place}`);
                if (seen.has(key)) return;
                seen.add(key);
                presets.push({
                    ...place,
                    eventCount: Number(row.count || 0).toLocaleString(this.$i18n?.locale || 'nl-NL'),
                });
            });

            return presets.slice(0, 5);
        },
        hasSearchPresets() {
            return this.searchPresetsLoading || this.topPlacePresets.length > 0 || this.presetPeople.length > 0;
        },
        showSearchPanel() {
            if (!this.placeSearchFocused) return false;

            if (this.isMapSearchEmpty) {
                return this.hasSearchPresets;
            }

            return this.showPlaceSuggestions &&
                (this.placeSuggestions.length > 0 || this.personSuggestions.length > 0);
        },
        canShowTourControls() {
            return this.tourLauncherEnabled && this.useGlobeView && Boolean(mapboxgl.accessToken);
        },
        tourModes() {
            return placeTourModes;
        },
        activeTourMode() {
            return this.tourModes.find((mode) => mode.id === this.tourSelectedModeId) || this.tourModes[0];
        },
        currentTourModeLabel() {
            if (!this.activeTourMode) return '';
            return this.$t(this.activeTourMode.labelKey);
        },
        currentTourModeDescription() {
            if (!this.activeTourMode) return '';
            return this.$t(this.activeTourMode.descriptionKey);
        },
        currentTourStop() {
            return this.resolvedTourStops[this.tourCurrentIndex] || null;
        },
        currentTourTitle() {
            if (!this.currentTourStop) return '';
            return this.$t(this.currentTourStop.titleKey) || this.currentTourStop.place?.place || '';
        },
        currentTourEra() {
            if (!this.currentTourStop?.eraKey) return '';
            return this.$t(this.currentTourStop.eraKey);
        },
        currentTourOldName() {
            if (!this.currentTourStop?.oldNameKey) return this.currentTourTitle;
            return this.$t(this.currentTourStop.oldNameKey);
        },
        currentTourSummary() {
            if (!this.currentTourStop) return '';
            return this.$t(this.currentTourStop.summaryKey);
        },
        currentTourStatsLabel() {
            if (!this.currentTourStop?.statsLabelKey) return this.$t('placeMap.tour.curatedHighlight');
            return this.$t(this.currentTourStop.statsLabelKey);
        },
        tourTotalEvents() {
            if (this.tourStatsLoading) return '...';
            const total = Object.values(this.tourCurrentCounts || {})
                .reduce((sum, count) => sum + Number(count || 0), 0);
            return total.toLocaleString('nl-NL');
        },
        tourSubPlaceCount() {
            if (this.tourStatsLoading) return '...';
            return Number(this.tourCurrentSubPlaces.length || 0).toLocaleString('nl-NL');
        },
        tourPhaseLabel() {
            return this.$t(`placeMap.tour.phases.${this.tourPhase || 'approach'}`);
        },
        tourAltitudeLabel() {
            const phaseAltitude = {
                approach: '12 km',
                dive: '2.4 km',
                diorama: '120 m',
                orbit: '90 m',
            };
            return phaseAltitude[this.tourPhase] || '120 m';
        },
        tourDistanceLabel() {
            if (!this.currentTourStop || this.resolvedTourStops.length < 2) return '0 km';
            const previousIndex = this.tourCurrentIndex === 0
                ? this.resolvedTourStops.length - 1
                : this.tourCurrentIndex - 1;
            const previousStop = this.resolvedTourStops[previousIndex];
            const currentCenter = this.getTourStopCenter(this.currentTourStop);
            const previousCenter = this.getTourStopCenter(previousStop);
            if (!currentCenter || !previousCenter) return '0 km';

            const distance = this.calculateDistanceKm(previousCenter, currentCenter);
            return `${Math.round(distance).toLocaleString('nl-NL')} km`;
        },
        tourProgressPct() {
            if (!this.resolvedTourStops.length) return 0;
            return ((this.tourCurrentIndex + 1) / this.resolvedTourStops.length) * 100;
        },
        isAllSelected() {
            return this.selectedType === "";
        },
        availableDNASubjects() {
            // Only show subjects who have at least one DNA result
            return this.dnaTestSubjects.filter(subject => {
                if (!this.authToken && subject.id === 'person_tata') {
                    return false;
                }

                return dnaData.results.some(result => result.testSubjectId === subject.id);
            });
        },
        availableDNACompanies() {
            // Only show companies that have results for the selected subject
            return this.dnaCompanies.filter(company => {
                return dnaData.results.some(result =>
                    result.testSubjectId === this.selectedDNASubjectId &&
                    result.company === company.id
                );
            });
        },
        availableHaplogroups() {
            return haplogroupData.haplogroups || [];
        },
        availablePeople() {
            // Extract unique people from haplogroups
            const peopleMap = new Map();
            this.availableHaplogroups.forEach(hg => {
                if (hg.personId && hg.personName) {
                    peopleMap.set(hg.personId, hg.personName);
                }
            });
            return Array.from(peopleMap, ([id, name]) => ({ id, name }));
        },
        filteredHaplogroups() {
            if (!this.selectedHaplogroupPersonId) return [];
            return this.availableHaplogroups.filter(hg =>
                hg.personId === this.selectedHaplogroupPersonId
            );
        },
        selectedDNABreakdown() {
            const results = dnaData.results.filter(r =>
                r.testSubjectId === this.selectedDNASubjectId &&
                r.company === this.selectedDNACompanyId
            );
            if (!results.length) return [];

            const result = results[0];
            let rows = [];

            if (Array.isArray(result.regions) && result.regions.length) {
                rows = result.regions.map((r, index, array) => {
                    // Determine tree character based on hierarchy
                    let treeChar = '';
                    if (r.level === 2) {
                        // Check if this is the last child of its parent
                        const isLastChild = !array.slice(index + 1).some(next =>
                            next.parentId === r.parentId && next.level === r.level
                        );
                        treeChar = isLastChild ? '└─ ' : '├─ ';
                    } else if (r.level === 3) {
                        // For level 3, add extra spacing and character
                        const isLastChild = !array.slice(index + 1).some(next =>
                            next.parentId === r.parentId && next.level === r.level
                        );
                        treeChar = '   ' + (isLastChild ? '└─ ' : '├─ ');
                    } else if (r.level === 4) {
                        const isLastChild = !array.slice(index + 1).some(next =>
                            next.parentId === r.parentId && next.level === r.level
                        );
                        treeChar = '      ' + (isLastChild ? '└─ ' : '├─ ');
                    }

                    return {
                        id: r.id,
                        label: r.label || r.name,
                        value: this.normalizeDnaPercentage(r.percentage),
                        countries: Array.isArray(r.countries) ? r.countries : [],
                        level: r.level || 1,
                        parentId: r.parentId || null,
                        treeChar
                    };
                });
            } else if (result.ethnicityGroups) {
                rows = Object.entries(result.ethnicityGroups).map(([key, pct]) => {
                    const group = dnaData.ethnicityGroups[key];
                    return {
                        id: key,
                        label: group ? group.label : key,
                        value: this.normalizeDnaPercentage(pct),
                        countries: group ? (group.countries || []) : [],
                        level: 1,
                        parentId: null,
                        treeChar: ''
                    };
                });
            }

            // Don't sort if using hierarchical regions - maintain order from JSON
            if (!result.regions || !result.regions[0]?.level) {
                rows.sort((a, b) => b.value - a.value);
            }

            const maxVal = rows.length ? Math.max(...rows.map(r => r.value)) : 1;
            return rows
                .filter(r => r.value > 0)
                .map(r => ({
                    ...r,
                    display: r.value < 1 ? '<1%' : r.value.toFixed(1) + '%',
                    barWidth: Math.max((r.value / maxVal) * 100, 2)
                }));
        },
        filterBannerVisible() {
            // Show banner if either DNA filter or person filter is active in globe view
            if (!this.useGlobeView) return false;
            return this.showDNAResults || this.personIDFilter.trim().length > 0;
        },
        filterBannerInfo() {
            if (this.showDNAResults && this.personIDFilter.trim().length > 0) {
                // Both filters active
                const dnaSubject = this.dnaTestSubjects.find(s => s.id === this.selectedDNASubjectId);
                const subjectName = dnaSubject ? dnaSubject.name : this.$t('placeMap.unknown');
                const company = this.dnaCompanies.find(c => c.id === this.selectedDNACompanyId);
                const companyName = company ? company.name : this.$t('placeMap.unknown');
                return {
                    title: this.$t('placeMap.multipleFiltersActive'),
                    description: this.$t('placeMap.filterBanner.bothFiltersDesc', { subject: subjectName, company: companyName, personId: this.personIDFilter.toUpperCase() })
                };
            } else if (this.showDNAResults) {
                // DNA filter active
                const dnaSubject = this.dnaTestSubjects.find(s => s.id === this.selectedDNASubjectId);
                const subjectName = dnaSubject ? dnaSubject.name : this.$t('placeMap.unknown');
                const company = this.dnaCompanies.find(c => c.id === this.selectedDNACompanyId);
                const companyName = company ? company.name : this.$t('placeMap.unknown');
                return {
                    title: this.$t('placeMap.dnaEthnicityShown'),
                    description: this.$t('placeMap.filterBanner.dnaFilterDesc', { subject: subjectName, company: companyName })
                };
            } else if (this.personIDFilter.trim().length > 0) {
                // Person filter active
                return {
                    title: this.$t('placeMap.lifeEventsFiltered'),
                    description: this.$t('placeMap.filterBanner.personFilterDesc', { personId: this.personIDFilter.toUpperCase() })
                };
            }
            return { title: '', description: '' };
        },
        localizedTypeLabels() {
            return {
                birth: this.$t('placeMap.types.birth'),
                death: this.$t('placeMap.types.death'),
                baptism: this.$t('placeMap.types.baptism'),
                education: this.$t('placeMap.types.education'),
                marriage: this.$t('placeMap.types.marriage'),
                burial: this.$t('placeMap.types.burial'),
                address: this.$t('placeMap.types.address'),
                residence: this.$t('placeMap.types.residence'),
            };
        },
        localizedPeriods() {
            return this.periods.map(p => ({
                ...p,
                label: this.$t(`placeMap.periods.${p.id}`)
            }));
        },
        localizedIndonesiaPeriods() {
            return this.localizedPeriods.filter(p => p.region === 'indonesia');
        },
        localizedNetherlandsPeriods() {
            return this.localizedPeriods.filter(p => p.region === 'netherlands');
        },
    },
    watch: {
        '$route.query.mode'(value) {
            const routeMode = value === 'surnames' ? 'surnames' : 'places';
            if (!this.hideFilters && routeMode !== this.displayMode) {
                this.setDisplayMode(routeMode, { updateRoute: false });
            }
        },
        async selectedType() {
            await this.refreshActiveRepresentation();
        },
        async onlyCorePeople() {
            await this.refreshActiveRepresentation();
        },
        dateParams: {
            deep: true,
            async handler() {
                clearTimeout(this.dateDebounce);
                this.dateDebounce = setTimeout(async () => {
                    await this.refreshActiveRepresentation();
                }, 300);
            },
        },
        async useGlobeView(newVal) {
            if (!newVal) {
                this.useGlobeProjection = false;
                this.useGlobeView = true;
                return;
            }

            await this.$nextTick();
            if (!this.mapInstance && this.$refs.mapContainer) {
                this.initializeMapbox();
            } else if (this.mapInstance) {
                this.updateMapboxMarkers();
                this.$nextTick(() => this.recenterAfterData());
            }
        },
        useGlobeProjection(newVal) {
            if (!this.forceGlobeView) {
                const currentView = this.$route.query.view;
                const targetView = newVal ? 'globe' : 'map';
                if (currentView !== targetView) {
                    this.$router.replace({
                        query: { ...this.$route.query, view: targetView }
                    }).catch(() => { });
                }
            }

            if (this.mapInstance) {
                this.mapInstance.setProjection(newVal ? 'globe' : 'mercator');
                if (newVal) {
                    this.setMapboxAtmosphere();
                }
                this.$nextTick(() => this.recenterAfterData());
            }
        },
        personIDFilter(newVal) {
            // Clear error message when user starts typing
            if (newVal && newVal.trim()) {
                this.personIDErrorMsg = "";
            }
        },
        sidePanelVisible(newVal) {
            // Stop rotation animation when panel opens
            if (newVal) {
                this.userInteracting = true;
                this.stopRotation();
                clearTimeout(this.idleTimeout);
            } else {
                if (!this.enableIdleRotation) return;

                // Resume rotation after panel closes with a short delay
                setTimeout(() => {
                    if (!this.userInteracting) {
                        this.userInteracting = false;
                        this.idleTimeout = setTimeout(() => {
                            this.startRotation();
                        }, this.idleDelay);
                    }
                }, 500);
            }
        },
        showDNAResults(newVal) {
            if (newVal && this.mapInstance) {
                this.loadDNALayers();
            } else {
                this.removeDNALayers();
            }
        },
        authToken() {
            // If Tata becomes unavailable after logout, switch to the first visible DNA subject.
            const visibleSubjectIds = this.availableDNASubjects.map(subject => subject.id);
            if (!visibleSubjectIds.includes(this.selectedDNASubjectId) && visibleSubjectIds.length > 0) {
                this.selectedDNASubjectId = visibleSubjectIds[0];
            }
            if (this.isSurnameMode) {
                // Never leave a profile from the previous privacy scope visible.
                this.closeSurnameProfile();
                this.loadSurnameLabels({ force: true }).then(() => this.renderActiveMapLayers());
            }
        },
        selectedDNASubjectId() {
            // Switch to a valid company if current company has no data for this subject
            const hasDataForCurrentCompany = dnaData.results.some(result =>
                result.testSubjectId === this.selectedDNASubjectId &&
                result.company === this.selectedDNACompanyId
            );

            if (!hasDataForCurrentCompany) {
                // Find first company with data for this subject
                const companyWithData = this.dnaCompanies.find(company => {
                    return dnaData.results.some(result =>
                        result.testSubjectId === this.selectedDNASubjectId &&
                        result.company === company.id
                    );
                });

                if (companyWithData) {
                    this.selectedDNACompanyId = companyWithData.id;
                }
            }

            if (this.showDNAResults && this.mapInstance) {
                this.loadDNALayers();
            }
        },
        selectedDNACompanyId() {
            if (this.showDNAResults && this.mapInstance) {
                this.loadDNALayers();
            }
        },
        selectedHaplogroupPersonId(newVal) {
            // Clear selected haplogroups when person changes
            this.selectedHaplogroupIds = [];

            if (newVal && this.mapInstance) {
                this.loadHaplogroupLayers();
            } else {
                this.removeHaplogroupLayers();
            }
        },
        selectedHaplogroupIds() {
            if (this.selectedHaplogroupPersonId && this.mapInstance) {
                this.loadHaplogroupLayers();
            }
        },
        useSatelliteStyle(newVal) {
            if (this.mapInstance && this.useGlobeView) {
                const newStyle = newVal
                    ? 'mapbox://styles/mapbox/satellite-v9'
                    : 'mapbox://styles/mapbox/standard';

                // Clear layer tracking since setStyle removes all layers
                this.dnaLayerIds = [];

                this.mapInstance.setStyle(newStyle);

                // Wait for style.load event - fires once when style is fully loaded and ready
                this.mapInstance.once('style.load', () => {
                    this.mapInstance.setProjection(this.useGlobeProjection ? 'globe' : 'mercator');
                    if (this.useGlobeProjection) {
                        this.setMapboxAtmosphere();
                    }

                    // Re-add the active place or surname representation first.
                    this.updateMapboxMarkers();

                    // Re-add DNA layers AFTER markers are added
                    // Wait for next tick to ensure updateMapboxMarkers completes
                    this.$nextTick(() => {
                        if (this.showDNAResults) {
                            this.loadDNALayers();
                        }
                        if (this.tourActive) {
                            this.ensureTourSceneLayers();
                            this.playTourStop(this.tourCurrentIndex);
                        }
                    });
                });
            }
        },
    },
    mounted() {
        // Initialize DNA data
        this.initializeDNAData();
        document.addEventListener('click', this.onMapLayerDocumentClick);

        // Initialize globe view if needed
        if (this.useGlobeView) {
            this.$nextTick(() => {
                this.initializeMapbox();
            });
        }
    },
    beforeUnmount() {
        clearTimeout(this.dateDebounce);
        clearTimeout(this.suggestionSearchTimeout);
        clearTimeout(this.surnameHoverTimer);
        this.surnameRequestController?.abort();
        this.surnameProfileRequestId += 1;
        this.surnameHoverRequestId += 1;
        this.clearTourAutoplay();
        this.stopRotation();
        clearTimeout(this.idleTimeout);
        if (this.mapInstance) {
            this.mapInstance.remove();
            this.mapInstance = null;
        }
        // Reset control references
        this.mapboxNavigationControl = null;
        this.mapboxFullscreenControl = null;
        this.mapboxAttributionControl = null;
        document.removeEventListener('click', this.onMapLayerDocumentClick);
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    },

    methods: {
        formatContinentPercentage(count, total) {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            if (percentage > 0 && percentage < 0.1) return '<0.1';

            return percentage.toLocaleString(this.$i18n?.locale || 'nl-NL', {
                maximumFractionDigits: percentage < 10 ? 1 : 0,
            });
        },

        normalizePlaceText(value) {
            return String(value || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
        },

        classifyPlaceContinent(place) {
            const lat = Number(place?.latitude);
            const lng = Number(place?.longitude);
            const name = this.normalizePlaceText(place?.place);

            const europeNamePattern = /(nederland|holland|gelderland|veluwe|amsterdam|rotterdam|belgie|belgium|brabant|frankrijk|france|alsace|elsas|duitsland|germany|reich|prussia|pruisen|england|scotland|ireland|italy|italie|spain|spanje|portugal|denmark|denemarken|norway|noorwegen|sweden|zweden)/;
            const asiaNamePattern = /(indie|indies|indonesia|java|jawa|batavia|jakarta|bandoeng|bandung|semarang|soerabaja|surabaya|malang|preanger|tj?imahi|cimahi|moluk|molucc|amboina|ambon|pasoeroean|pasuruan|salatiga|soerakarta|surakarta|djokja|jogja|yogyakarta|ceylon|sri lanka|india|china|japan|malaysia|singapore|thailand|philippines|filipijnen)/;
            const africaNamePattern = /(afrika|africa|kaap|cape|egypt|egypte|marokko|morocco|algeria|algerije|tunisia|tunesie|madagascar)/;
            const northAmericaNamePattern = /(united states|verenigde staten|canada|mexico|mexico|cuba|jamaica|haiti)/;
            const southAmericaNamePattern = /(brazil|brazilie|argentina|chile|peru|suriname|guyana|colombia|venezuela|ecuador|bolivia|uruguay|paraguay)/;
            const oceaniaNamePattern = /(australia|australie|new zealand|nieuw-zeeland|nieuw zeeland|polynesia|polynesie|melanesia|melanesie)/;

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                if (europeNamePattern.test(name)) return 'europe';
                if (asiaNamePattern.test(name)) return 'asia';
                if (africaNamePattern.test(name)) return 'africa';
                if (northAmericaNamePattern.test(name)) return 'northAmerica';
                if (southAmericaNamePattern.test(name)) return 'southAmerica';
                if (oceaniaNamePattern.test(name)) return 'oceania';
                return null;
            }

            if (lat <= -60) return 'antarctica';
            if (lat >= 35 && lat <= 72 && lng >= -25 && lng <= 45) {
                return 'europe';
            }
            if (lat >= -12 && lat <= 81 && lng >= 25 && lng <= 180) {
                return 'asia';
            }
            if (lat >= -35 && lat <= 38 && lng >= -18 && lng <= 52) {
                return 'africa';
            }
            if (lat >= 7 && lat <= 84 && lng >= -170 && lng <= -50) {
                return 'northAmerica';
            }
            if (lat >= -56 && lat <= 13 && lng >= -82 && lng <= -34) {
                return 'southAmerica';
            }
            if (lat >= -50 && lat <= 0 && lng >= 110 && lng <= 180) {
                return 'oceania';
            }

            if (europeNamePattern.test(name)) return 'europe';
            if (asiaNamePattern.test(name)) return 'asia';
            if (africaNamePattern.test(name)) return 'africa';
            if (northAmericaNamePattern.test(name)) return 'northAmerica';
            if (southAmericaNamePattern.test(name)) return 'southAmerica';
            if (oceaniaNamePattern.test(name)) return 'oceania';

            return null;
        },

        onFullscreenChange() {
            this.isFullscreen = !!document.fullscreenElement;
        },

        handleClusterClick(e) {
            if (!this.mapInstance) return;

            const feature = (e.features && e.features.length > 0)
                ? e.features[0]
                : this.mapInstance.queryRenderedFeatures(e.point, { layers: ['clusters', 'cluster-count'] })[0];

            if (!feature || !feature.properties) return;

            const clusterId = feature.properties.cluster_id;
            if (clusterId === undefined || clusterId === null) return;

            const source = this.mapInstance.getSource('places');
            if (!source || typeof source.getClusterExpansionZoom !== 'function') return;

            // Create ripple effect
            this.createRippleEffect(e.point);

            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                // Use flyTo for smooth animation
                this.mapInstance.flyTo({
                    center: feature.geometry.coordinates,
                    zoom: zoom,
                    bearing: this.mapInstance.getBearing(),
                    duration: 2000,
                    easing: (t) => {
                        // Custom easing curve for smooth deceleration
                        return t * (2 - t);
                    }
                });
            });
        },

        hasRenderedPointFeatureAtPoint(point) {
            if (!this.mapInstance || !point) return false;

            const pointLayers = ['unclustered-point', 'clusters', 'cluster-count']
                .filter((layerId) => this.mapInstance.getLayer(layerId));
            if (!pointLayers.length) return false;

            return this.mapInstance.queryRenderedFeatures(point, { layers: pointLayers }).length > 0;
        },

        isAreaLayerClickEnabled() {
            if (!this.mapInstance) return false;
            return this.mapInstance.getZoom() < this.areaLayerFadeStartZoom;
        },

        isPlaceAreaFeatureClickEnabled(feature) {
            if (!this.mapInstance || !feature) return false;
            const fadesOnZoom = feature.properties?.fadesOnZoom;
            if (fadesOnZoom === false || fadesOnZoom === 'false') return true;
            return this.isAreaLayerClickEnabled();
        },

        isMapboxCountryAreaFeatureClickEnabled(feature) {
            if (!this.mapInstance || !feature) return false;
            const iso3 = feature.properties?.iso_3166_1_alpha_3;
            const place = this.areaPlaces.find((candidate) => {
                return candidate.areaFeature?.properties?.sourceType === 'mapbox-country' &&
                    candidate.areaFeature?.properties?.mapboxCountryIso3 === iso3;
            });

            if (place && !this.shouldAreaFeatureFadeOnZoom(place.areaFeature)) return true;
            return this.isAreaLayerClickEnabled();
        },

        getAreaFillOpacityExpression(fadeCondition = ['boolean', ['get', 'fadesOnZoom'], true]) {
            const defaultOpacity = [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                0.32,
                0.16
            ];

            return [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                defaultOpacity,
                this.areaLayerFadeStartZoom,
                [
                    'case',
                    fadeCondition,
                    [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        0.2,
                        0.1
                    ],
                    defaultOpacity
                ],
                this.areaLayerFadeEndZoom,
                [
                    'case',
                    fadeCondition,
                    0,
                    defaultOpacity
                ]
            ];
        },

        getAreaLineOpacityExpression(fadeCondition = ['boolean', ['get', 'fadesOnZoom'], true]) {
            const defaultOpacity = [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                0.95,
                0.75
            ];

            return [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                defaultOpacity,
                this.areaLayerFadeStartZoom,
                [
                    'case',
                    fadeCondition,
                    [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        0.55,
                        0.4
                    ],
                    defaultOpacity
                ],
                this.areaLayerFadeEndZoom,
                [
                    'case',
                    fadeCondition,
                    0,
                    defaultOpacity
                ]
            ];
        },

        async handlePlaceAreaClick(e) {
            if (!e.features || e.features.length === 0) return;
            if (!this.isPlaceAreaFeatureClickEnabled(e.features[0])) return;
            if (this.hasRenderedPointFeatureAtPoint(e.point)) return;

            const placeID = e.features[0].properties.placeID;
            const place = this.areaPlaces.find((candidate) => String(candidate.ID) === String(placeID));
            if (!place) return;

            await this.openPlacePanel(place);
        },

        async handleMapboxCountryAreaClick(e) {
            if (!e.features || e.features.length === 0) return;
            if (!this.isMapboxCountryAreaFeatureClickEnabled(e.features[0])) return;
            if (this.hasRenderedPointFeatureAtPoint(e.point)) return;

            const iso3 = e.features[0].properties.iso_3166_1_alpha_3;
            const place = this.areaPlaces.find((candidate) => {
                return candidate.areaFeature?.properties?.sourceType === 'mapbox-country' &&
                    candidate.areaFeature?.properties?.mapboxCountryIso3 === iso3;
            });

            if (!place) return;
            await this.openPlacePanel(place);
        },

        handlePlaceAreaMouseMove(e) {
            if (!this.mapInstance || !e.features || e.features.length === 0) return;
            if (!this.isPlaceAreaFeatureClickEnabled(e.features[0])) {
                this.handlePlaceAreaMouseLeave();
                return;
            }

            this.mapInstance.getCanvas().style.cursor = 'pointer';
            const featureId = e.features[0].id || e.features[0].properties.areaFeatureId;
            if (!featureId) return;

            if (this.hoveredPlaceAreaFeatureId && this.hoveredPlaceAreaFeatureId !== featureId) {
                this.mapInstance.setFeatureState(
                    { source: 'place-areas', id: this.hoveredPlaceAreaFeatureId },
                    { hover: false }
                );
            }

            this.hoveredPlaceAreaFeatureId = featureId;
            this.mapInstance.setFeatureState(
                { source: 'place-areas', id: featureId },
                { hover: true }
            );
        },

        handlePlaceAreaMouseLeave() {
            if (!this.mapInstance) return;

            this.mapInstance.getCanvas().style.cursor = '';
            if (this.hoveredPlaceAreaFeatureId) {
                this.mapInstance.setFeatureState(
                    { source: 'place-areas', id: this.hoveredPlaceAreaFeatureId },
                    { hover: false }
                );
                this.hoveredPlaceAreaFeatureId = null;
            }
        },

        handleMapboxCountryAreaMouseMove(e) {
            if (!this.mapInstance || !e.features || e.features.length === 0) return;
            if (!this.isMapboxCountryAreaFeatureClickEnabled(e.features[0])) {
                this.handleMapboxCountryAreaMouseLeave();
                return;
            }

            this.mapInstance.getCanvas().style.cursor = 'pointer';
            const featureId = e.features[0].id || e.features[0].properties.iso_3166_1_alpha_3;
            if (!featureId) return;

            if (this.hoveredMapboxCountryFeatureId && this.hoveredMapboxCountryFeatureId !== featureId) {
                this.mapInstance.setFeatureState(
                    {
                        source: 'mapbox-country-boundaries',
                        sourceLayer: 'country_boundaries',
                        id: this.hoveredMapboxCountryFeatureId,
                    },
                    { hover: false }
                );
            }

            this.hoveredMapboxCountryFeatureId = featureId;
            this.mapInstance.setFeatureState(
                {
                    source: 'mapbox-country-boundaries',
                    sourceLayer: 'country_boundaries',
                    id: featureId,
                },
                { hover: true }
            );
        },

        handleMapboxCountryAreaMouseLeave() {
            if (!this.mapInstance) return;

            this.mapInstance.getCanvas().style.cursor = '';
            if (this.hoveredMapboxCountryFeatureId) {
                this.mapInstance.setFeatureState(
                    {
                        source: 'mapbox-country-boundaries',
                        sourceLayer: 'country_boundaries',
                        id: this.hoveredMapboxCountryFeatureId,
                    },
                    { hover: false }
                );
                this.hoveredMapboxCountryFeatureId = null;
            }
        },

        createRippleEffect(point) {
            // Create ripple element
            const ripple = document.createElement('div');
            ripple.className = 'map-ripple';
            const canvas = this.mapInstance.getCanvas();

            ripple.style.left = `${point.x}px`;
            ripple.style.top = `${point.y}px`;

            canvas.parentElement.appendChild(ripple);

            // Remove after animation
            setTimeout(() => {
                ripple.remove();
            }, 1000);
        },

        startRotation() {
            if (!this.enableIdleRotation || this.rotationAnimation || !this.mapInstance) return;

            const currentCenter = this.mapInstance.getCenter();

            // Zoom out to show full globe before starting rotation
            this.mapInstance.easeTo({
                zoom: this.immediateRotation ? 1.4 : 2.25,
                pitch: 0,
                center: [currentCenter.lng, 0], // Keep current longitude, center idle view on the equator
                duration: 2000,
                easing: (t) => t * (2 - t) // Smooth deceleration
            });

            // Wait for zoom out to complete before starting rotation
            setTimeout(() => {
                if (!this.userInteracting && this.mapInstance) {
                    let lastTime = performance.now();

                    const rotate = (currentTime) => {
                        if (!this.userInteracting && this.mapInstance) {
                            const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
                            lastTime = currentTime;

                            const center = this.mapInstance.getCenter();
                            // Rotate eastward by increasing longitude
                            let newLng = center.lng + (this.rotationSpeed * deltaTime);

                            // Wrap longitude to stay within -180 to 180
                            if (newLng > 180) newLng -= 360;

                            this.mapInstance.setCenter([newLng, center.lat]);
                            this.rotationAnimation = requestAnimationFrame(rotate);
                        }
                    };

                    this.rotationAnimation = requestAnimationFrame(rotate);
                }
            }, 2100); // Start rotation after zoom animation completes
        },

        stopRotation() {
            if (this.rotationAnimation) {
                cancelAnimationFrame(this.rotationAnimation);
                this.rotationAnimation = null;
            }
        },

        onUserInteraction() {
            this.stopRotation();
            clearTimeout(this.idleTimeout);

            if (!this.enableIdleRotation) {
                this.userInteracting = false;
                return;
            }

            this.userInteracting = true;

            // Resume rotation after idle delay
            this.idleTimeout = setTimeout(() => {
                this.userInteracting = false;
                this.startRotation();
            }, this.idleDelay);
        },

        setupRotationListeners() {
            if (!this.mapInstance || !this.enableIdleRotation) return;

            const events = ['mousedown', 'touchstart', 'wheel'];
            events.forEach(event => {
                this.mapInstance.on(event, () => this.onUserInteraction());
            });

            // Start initial rotation after intro and idle period.
            const hasPendingIntro = this.enableGlobeIntro && this.mapBoundsPlaces.length > 0 && !this.hasPlayedIntro;
            const initialRotationDelay = this.immediateRotation
                ? 0
                : this.idleDelay + (hasPendingIntro ? this.introDurationMs : 0);

            setTimeout(() => {
                if (!this.userInteracting) {
                    this.startRotation();
                }
            }, initialRotationDelay);
        },

        playCinematicIntro() {
            if (!this.enableGlobeIntro || this.hasPlayedIntro || !this.mapInstance || this.mapBoundsPlaces.length === 0) {
                return;
            }

            this.hasPlayedIntro = true;

            // One continuous camera timeline: outer space -> US -> NL -> Indonesia.
            const keyframes = [
                { t: 0.00, center: [-120.0, 25.0], zoom: 0.25 },
                { t: 0.34, center: [-98.5795, 39.8283], zoom: 2.0 },
                { t: 0.58, center: [5.3, 52.2], zoom: 3.0 },
                { t: 1.00, center: [118.0, -2.0], zoom: 3.0 }
            ];

            const easeInOutCubic = (t) => {
                if (t < 0.5) return 4 * t * t * t;
                return 1 - Math.pow(-2 * t + 2, 3) / 2;
            };

            const catmullRom = (p0, p1, p2, p3, t) => {
                const t2 = t * t;
                const t3 = t2 * t;
                return 0.5 * (
                    (2 * p1) +
                    (-p0 + p2) * t +
                    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
                    (-p0 + 3 * p1 - 3 * p2 + p3) * t3
                );
            };

            const normalizeLng = (lng) => {
                let normalized = lng;
                while (normalized > 180) normalized -= 360;
                while (normalized < -180) normalized += 360;
                return normalized;
            };

            // Unwrap longitudes so spline interpolation does not jump at the antimeridian.
            const unwrappedLngs = [keyframes[0].center[0]];
            for (let i = 1; i < keyframes.length; i += 1) {
                let lng = keyframes[i].center[0];
                const prev = unwrappedLngs[i - 1];
                while (lng - prev > 180) lng -= 360;
                while (lng - prev < -180) lng += 360;
                unwrappedLngs.push(lng);
            }

            const sampleKeyframes = (u) => {
                if (u <= 0) return keyframes[0];
                if (u >= 1) return keyframes[keyframes.length - 1];

                for (let i = 0; i < keyframes.length - 1; i += 1) {
                    const a = keyframes[i];
                    const b = keyframes[i + 1];
                    if (u >= a.t && u <= b.t) {
                        const localT = (u - a.t) / (b.t - a.t || 1);
                        const i0 = Math.max(i - 1, 0);
                        const i1 = i;
                        const i2 = i + 1;
                        const i3 = Math.min(i + 2, keyframes.length - 1);

                        const lng = normalizeLng(catmullRom(
                            unwrappedLngs[i0],
                            unwrappedLngs[i1],
                            unwrappedLngs[i2],
                            unwrappedLngs[i3],
                            localT
                        ));

                        const lat = catmullRom(
                            keyframes[i0].center[1],
                            keyframes[i1].center[1],
                            keyframes[i2].center[1],
                            keyframes[i3].center[1],
                            localT
                        );

                        const zoom = catmullRom(
                            keyframes[i0].zoom,
                            keyframes[i1].zoom,
                            keyframes[i2].zoom,
                            keyframes[i3].zoom,
                            localT
                        );

                        return {
                            center: [lng, Math.max(-80, Math.min(80, lat))],
                            zoom: Math.max(0.2, Math.min(6, zoom))
                        };
                    }
                }

                return keyframes[keyframes.length - 1];
            };

            const startTime = performance.now();
            const tick = (now) => {
                if (!this.mapInstance) return;

                const elapsed = now - startTime;
                const rawProgress = Math.min(elapsed / this.introDurationMs, 1);
                const easedProgress = easeInOutCubic(rawProgress);
                const state = sampleKeyframes(easedProgress);

                this.mapInstance.jumpTo({
                    center: state.center,
                    zoom: state.zoom,
                    pitch: 0,
                    bearing: 0
                });

                if (rawProgress < 1) {
                    requestAnimationFrame(tick);
                }
            };

            requestAnimationFrame(tick);
        },

        setClusterPointerCursor() {
            if (!this.mapInstance) return;
            this.mapInstance.getCanvas().style.cursor = 'pointer';
        },

        clearMapCursor() {
            if (!this.mapInstance) return;
            this.mapInstance.getCanvas().style.cursor = '';
        },

        normalizeEventType(type) {
            const value = String(type || '').trim().toLowerCase();
            if (['residence', 'resi', 'address', 'addr'].includes(value)) return 'address';
            if (['education', 'educ'].includes(value)) return 'education';
            return value;
        },

        prettifyEventType(type) {
            const normalized = this.normalizeEventType(type);
            return this.localizedTypeLabels[normalized] || normalized || this.$t('placeMap.unknown');
        },

        formatPopupCountsHtml(counts) {
            const entries = Object.entries(counts || {}).filter(([, n]) => Number(n) > 0);
            if (!entries.length) {
                return `<div class="counts"><div>${this.$t('placeMap.noEventCounts')}</div></div>`;
            }

            const preferredOrder = ['birth', 'death', 'burial', 'baptism', 'education', 'marriage', 'address'];
            const preferred = preferredOrder
                .filter((key) => Object.prototype.hasOwnProperty.call(counts, key))
                .map((key) => [key, counts[key]]);
            const extra = entries
                .filter(([key]) => !preferredOrder.includes(key))
                .sort((a, b) => a[0].localeCompare(b[0]));

            return `
                <div class="counts">
                    ${[...preferred, ...extra]
                    .map(([type, count]) => `<div>${this.prettifyEventType(type)}: ${count}</div>`)
                    .join('')}
                </div>
            `;
        },

        async getPlaceEventCounts(place, personID = null) {
            const placeIds = this.getPanelPlaceIds(place);
            if (placeIds.length > 1) {
                return this.getAggregatePlaceEventCounts(placeIds, personID);
            }

            // If filtering by person, use the loaded personEventLocations data
            if (personID && this.personEventLocations && this.personEventLocations.length > 0) {
                return this.getPersonSpecificEventCounts(place);
            }

            if (place?.counts && Object.keys(place.counts).length) {
                return place.counts;
            }

            const placeId = place?.ID;
            if (!placeId) return {};

            if (this.popupEventCountCache[placeId]) {
                return this.popupEventCountCache[placeId];
            }

            try {
                const events = await fetchPlaceEvents(placeId);
                const counts = (events || []).reduce((acc, event) => {
                    const key = this.normalizeEventType(event.eventType);
                    if (!key) return acc;
                    acc[key] = (acc[key] || 0) + 1;
                    return acc;
                }, {});

                this.popupEventCountCache = {
                    ...this.popupEventCountCache,
                    [placeId]: counts,
                };

                return counts;
            } catch (error) {
                console.error('Failed to fetch place events for popup counts:', error);
                return {};
            }
        },

        getPersonSpecificEventCounts(place) {
            // Filter events for this specific place from personEventLocations
            const placeEvents = this.personEventLocations.filter(event => {
                // Match by coordinates (more reliable than place name)
                const latMatch = Math.abs(parseFloat(event.latitude) - parseFloat(place.latitude)) < 0.0001;
                const lngMatch = Math.abs(parseFloat(event.longitude) - parseFloat(place.longitude)) < 0.0001;
                return latMatch && lngMatch;
            });

            // Count events by type
            const counts = placeEvents.reduce((acc, event) => {
                const key = this.normalizeEventType(event.type || event.eventType);
                if (!key) return acc;
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {});

            return counts;
        },

        getSurnameRequestOptions() {
            return {
                ...(this.selectedType ? { types: [this.selectedType] } : {}),
                ...(this.onlyCorePeople ? { personIDs: this.corePersonIds } : {}),
                ...this.dateParams,
                zoom: this.surnameZoomTier,
            };
        },

        getCurrentSurnameZoomTier() {
            const zoom = this.mapInstance ? this.mapInstance.getZoom() : this.surnameZoomTier;
            return Math.max(1, Math.min(10, Math.floor(Number(zoom) || 1)));
        },

        loadSurnameLabels({ force = false } = {}) {
            const targetZoom = this.getCurrentSurnameZoomTier();
            const requestOptions = {
                ...this.getSurnameRequestOptions(),
                zoom: targetZoom,
            };
            const requestSignature = JSON.stringify({
                ...requestOptions,
                privacyScope: this.authToken ? 'member' : 'public',
            });

            if (this.surnameLoading && this.surnameRequestSignature === requestSignature) {
                return this.surnameRequestPromise || Promise.resolve();
            }
            if (!force && this.surnameLoadedSignature === requestSignature) return Promise.resolve();

            this.surnameZoomTier = targetZoom;
            this.surnameRequestController?.abort();
            const controller = new AbortController();
            this.surnameRequestController = controller;
            this.surnameRequestSignature = requestSignature;
            this.surnameLoading = true;
            this.surnameError = null;

            const requestPromise = (async () => {
                try {
                    const payload = await fetchPlaceSurnames({
                        ...requestOptions,
                        signal: controller.signal,
                    });
                    if (controller.signal.aborted) return;

                    this.surnameLabels = Array.isArray(payload?.labels) ? payload.labels : [];
                    this.surnameLandscapeMeta = payload?.meta || null;
                    this.surnameLoadedSignature = requestSignature;
                    this.surnameLabelById = Object.fromEntries(
                        this.surnameLabels.map(label => [String(label.id), label])
                    );
                } catch (error) {
                    if (controller.signal.aborted || error?.code === 'ERR_CANCELED') return;
                    this.surnameLabels = [];
                    this.surnameLabelById = {};
                    this.surnameLandscapeMeta = null;
                    this.surnameLoadedSignature = null;
                    this.surnameError = error;
                } finally {
                    if (this.surnameRequestController === controller) {
                        this.surnameRequestController = null;
                        this.surnameRequestPromise = null;
                        this.surnameRequestSignature = null;
                        this.surnameLoading = false;
                    }
                }
            })();

            this.surnameRequestPromise = requestPromise;
            return requestPromise;
        },

        async refreshActiveRepresentation() {
            if (this.isSurnameMode) {
                const selected = this.selectedSurnameLabel;
                await this.loadSurnameLabels({ force: true });
                this.renderActiveMapLayers();
                if (selected?.surname) {
                    this.selectedSurnameLabel = this.findClosestSurnameMapLabel(selected) || selected;
                    await this.loadSurnameProfile(selected.surname);
                }
                return;
            }

            await this.loadPlaces();
            this.renderActiveMapLayers({ recenter: true });
        },

        renderActiveMapLayers({ recenter = false } = {}) {
            if (!this.useGlobeView || !this.mapInstance) return;

            const render = () => {
                this.updateMapboxMarkers();
                if (recenter && !this.isSurnameMode) this.recenterAfterData();
            };
            if (this.mapInstance.isStyleLoaded()) {
                render();
            } else {
                this.mapInstance.once('style.load', render);
            }
        },

        async setDisplayMode(mode, { updateRoute = true } = {}) {
            if (this.hideFilters) return;
            const nextMode = mode === 'surnames' ? 'surnames' : 'places';
            if (nextMode === this.displayMode) return;

            if (nextMode === 'surnames' && this.personIDFilter) {
                await this.clearPersonFilter();
            }
            if (this.tourActive) this.closePlaceTour();
            this.closeSidePanel();
            this.closeSurnameProfile();
            this.displayMode = nextMode;

            if (updateRoute) {
                const query = { ...this.$route.query };
                if (nextMode === 'surnames') query.mode = 'surnames';
                else delete query.mode;
                this.$router.replace({ query }).catch(() => { });
            }

            if (nextMode === 'surnames') {
                this.surnameZoomTier = this.getCurrentSurnameZoomTier();
                await this.loadSurnameLabels({ force: true });
                this.renderActiveMapLayers();
            } else {
                this.surnameRequestController?.abort();
                await this.loadPlaces();
                this.renderActiveMapLayers({ recenter: true });
            }
        },

        async handleSurnameZoomEnd() {
            if (!this.isSurnameMode) return;
            const nextZoomTier = this.getCurrentSurnameZoomTier();
            if (nextZoomTier === this.surnameZoomTier) return;

            this.clearSurnameHover();
            this.surnameZoomTier = nextZoomTier;
            await this.loadSurnameLabels({ force: true });
            this.renderActiveMapLayers();
        },

        handleSurnameLabelClick(event) {
            const feature = event?.features?.[0];
            const label = this.surnameLabelById[String(feature?.properties?.id || feature?.id || '')];
            if (label) this.openSurnameProfile(label);
        },

        handleSurnameLabelMouseEnter(event) {
            if (this.mapInstance) this.mapInstance.getCanvas().style.cursor = 'pointer';
            this.handleSurnameLabelMouseMove(event);
        },

        handleSurnameLabelMouseMove(event) {
            if (this.surnameProfileVisible) return;
            const feature = event?.features?.[0];
            const label = this.surnameLabelById[String(feature?.properties?.id || feature?.id || '')];
            if (!label || label.surnameKey === this.hoveredSurnameLabel?.surnameKey) return;
            this.queueSurnameHoverProfile(label);
        },

        handleSurnameLabelMouseLeave() {
            if (this.mapInstance) this.mapInstance.getCanvas().style.cursor = '';
            this.clearSurnameHover();
        },

        getSurnameProfileCacheKey(surname) {
            const options = this.getSurnameRequestOptions();
            return JSON.stringify({
                from: options.from || null,
                personIDs: options.personIDs || [],
                privacyScope: this.authToken ? 'member' : 'public',
                surname: String(surname || '').trim().toLocaleLowerCase(),
                to: options.to || null,
                types: options.types || [],
            });
        },

        fetchCachedSurnameProfile(surname) {
            const cacheKey = this.getSurnameProfileCacheKey(surname);
            if (this.surnameProfileCache.has(cacheKey)) {
                return Promise.resolve(this.surnameProfileCache.get(cacheKey));
            }
            if (this.surnameProfileInFlight.has(cacheKey)) {
                return this.surnameProfileInFlight.get(cacheKey);
            }

            const request = fetchPlaceSurnameProfile(surname, {
                ...this.getSurnameRequestOptions(),
                zoom: 10,
            }).then((profile) => {
                if (!this.surnameProfileCache.has(cacheKey) && this.surnameProfileCache.size >= 40) {
                    const oldestKey = this.surnameProfileCache.keys().next().value;
                    this.surnameProfileCache.delete(oldestKey);
                }
                this.surnameProfileCache.set(cacheKey, profile);
                return profile;
            }).finally(() => {
                if (this.surnameProfileInFlight.get(cacheKey) === request) {
                    this.surnameProfileInFlight.delete(cacheKey);
                }
            });

            this.surnameProfileInFlight.set(cacheKey, request);
            return request;
        },

        queueSurnameHoverProfile(label) {
            clearTimeout(this.surnameHoverTimer);
            this.surnameHoverTimer = null;
            this.surnameHoverRequestId += 1;
            const requestId = this.surnameHoverRequestId;
            this.hoveredSurnameLabel = label;
            this.surnameHoverProfile = null;
            this.updateSurnameProfileHighlights();

            const cached = this.surnameProfileCache.get(this.getSurnameProfileCacheKey(label.surname));
            if (cached) {
                this.surnameHoverProfile = cached;
                this.updateSurnameProfileHighlights();
                return;
            }

            this.surnameHoverTimer = setTimeout(async () => {
                this.surnameHoverTimer = null;
                try {
                    const profile = await this.fetchCachedSurnameProfile(label.surname);
                    if (
                        requestId !== this.surnameHoverRequestId
                        || this.surnameProfileVisible
                        || this.hoveredSurnameLabel?.surnameKey !== label.surnameKey
                    ) return;

                    this.surnameHoverProfile = profile;
                    this.updateSurnameProfileHighlights();
                } catch {
                    // Hover previews are optional; click still exposes the regular error state.
                }
            }, 220);
        },

        clearSurnameHover({ updateHighlights = true } = {}) {
            clearTimeout(this.surnameHoverTimer);
            this.surnameHoverTimer = null;
            this.surnameHoverRequestId += 1;
            this.hoveredSurnameLabel = null;
            this.surnameHoverProfile = null;
            if (updateHighlights) this.updateSurnameProfileHighlights();
        },

        async openSurnameProfile(label) {
            if (!label?.surname) return;
            this.closeSidePanel();
            this.clearSurnameHover({ updateHighlights: false });
            this.selectedSurnameLabel = label;
            this.surnameProfileVisible = true;
            this.surnameProfile = null;
            this.surnameProfileError = null;
            this.syncSurnameSelectionPaint();
            await this.loadSurnameProfile(label.surname);
        },

        findClosestSurnameMapLabel(referenceLabel) {
            const candidates = this.surnameLabels.filter(label => (
                label.surnameKey === referenceLabel?.surnameKey
            ));
            if (!candidates.length) return null;

            return [...candidates].sort((left, right) => {
                const leftDistance = ((Number(left.latitude) - Number(referenceLabel.latitude)) ** 2)
                    + ((Number(left.longitude) - Number(referenceLabel.longitude)) ** 2);
                const rightDistance = ((Number(right.latitude) - Number(referenceLabel.latitude)) ** 2)
                    + ((Number(right.longitude) - Number(referenceLabel.longitude)) ** 2);
                return leftDistance - rightDistance;
            })[0];
        },

        async loadSurnameProfile(surname) {
            const requestId = ++this.surnameProfileRequestId;
            this.surnameProfileLoading = true;
            this.surnameProfileError = null;

            try {
                const profile = await this.fetchCachedSurnameProfile(surname);
                if (requestId !== this.surnameProfileRequestId || !this.surnameProfileVisible) return;

                this.surnameProfile = profile;
                this.updateSurnameProfileHighlights();
                this.syncSurnameSelectionPaint();
            } catch (error) {
                if (requestId !== this.surnameProfileRequestId) return;
                this.surnameProfile = null;
                this.surnameProfileError = error;
                this.updateSurnameProfileHighlights();
            } finally {
                if (requestId === this.surnameProfileRequestId) {
                    this.surnameProfileLoading = false;
                }
            }
        },

        closeSurnameProfile() {
            this.surnameProfileRequestId += 1;
            this.clearSurnameHover({ updateHighlights: false });
            this.surnameProfileVisible = false;
            this.surnameProfileLoading = false;
            this.surnameProfileError = null;
            this.surnameProfile = null;
            this.selectedSurnameLabel = null;
            this.removeSurnameProfileHighlights();
            this.syncSurnameSelectionPaint();
        },

        focusSurnameArea(area) {
            if (!this.mapInstance || !area) return;
            const longitude = Number(area.longitude);
            const latitude = Number(area.latitude);
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
            this.mapInstance.flyTo({
                center: [longitude, latitude],
                zoom: Math.max(this.mapInstance.getZoom(), 6),
                duration: 1000,
                essential: true,
            });
        },

        syncSurnameSelectionPaint() {
            if (!this.mapInstance?.getLayer('surname-labels')) return;
            const surnameKey = this.selectedSurnameLabel?.surnameKey || '';
            this.mapInstance.setPaintProperty('surname-labels', 'text-color', surnameKey
                ? ['case', ['==', ['get', 'surnameKey'], surnameKey], '#111111', '#4f4a47']
                : '#111111');
            this.mapInstance.setPaintProperty('surname-labels', 'text-opacity', surnameKey
                ? ['case', ['==', ['get', 'surnameKey'], surnameKey], 1, 0.2]
                : 0.96);
        },

        removeSurnameProfileHighlights() {
            if (!this.mapInstance) return;
            if (this.mapInstance.getLayer('surname-profile-regions')) {
                this.mapInstance.removeLayer('surname-profile-regions');
            }
            if (this.mapInstance.getSource('surname-profile-regions')) {
                this.mapInstance.removeSource('surname-profile-regions');
            }
        },

        updateSurnameProfileHighlights() {
            if (!this.mapInstance) return;
            const activeProfile = this.surnameProfileVisible
                ? this.surnameProfile
                : this.surnameHoverProfile;
            const regions = Array.isArray(activeProfile?.regions) ? activeProfile.regions : [];
            const data = {
                type: 'FeatureCollection',
                features: regions.map(region => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [Number(region.longitude), Number(region.latitude)],
                    },
                    properties: {
                        localCount: Number(region.localCount || 0),
                    },
                })),
            };

            const source = this.mapInstance.getSource('surname-profile-regions');
            if (source) {
                source.setData(data);
                return;
            }
            if (!regions.length) return;

            this.mapInstance.addSource('surname-profile-regions', { type: 'geojson', data });
            this.mapInstance.addLayer({
                id: 'surname-profile-regions',
                type: 'circle',
                source: 'surname-profile-regions',
                paint: {
                    'circle-blur': 0.85,
                    'circle-color': '#c62828',
                    'circle-opacity': 0.28,
                    'circle-pitch-alignment': 'map',
                    'circle-radius': [
                        'interpolate', ['linear'], ['get', 'localCount'],
                        1, 18,
                        10, 30,
                        30, 45,
                        100, 64,
                    ],
                },
            }, this.mapInstance.getLayer('surname-labels') ? 'surname-labels' : undefined);
        },

        updateSurnameMapLayers() {
            if (!this.mapInstance) return;

            if (this.mapInstance.getLayer('surname-labels')) {
                this.mapInstance.off('click', 'surname-labels', this.handleSurnameLabelClick);
                this.mapInstance.off('mouseenter', 'surname-labels', this.handleSurnameLabelMouseEnter);
                this.mapInstance.off('mousemove', 'surname-labels', this.handleSurnameLabelMouseMove);
                this.mapInstance.off('mouseleave', 'surname-labels', this.handleSurnameLabelMouseLeave);
                this.mapInstance.removeLayer('surname-labels');
            }
            this.removeSurnameProfileHighlights();
            if (this.mapInstance.getSource('surname-labels')) {
                this.mapInstance.removeSource('surname-labels');
            }
            if (!this.surnameLabels.length) return;

            const data = {
                type: 'FeatureCollection',
                features: this.surnameLabels.map(label => ({
                    type: 'Feature',
                    id: label.id,
                    geometry: {
                        type: 'Point',
                        coordinates: [Number(label.longitude), Number(label.latitude)],
                    },
                    properties: {
                        id: label.id,
                        localCount: Number(label.localCount || 0),
                        prominenceScore: Number(label.prominenceScore || 0),
                        surname: label.surname,
                        surnameKey: label.surnameKey,
                    },
                })),
            };

            this.mapInstance.addSource('surname-labels', { type: 'geojson', data });
            this.mapInstance.addLayer({
                id: 'surname-labels',
                type: 'symbol',
                source: 'surname-labels',
                layout: {
                    'symbol-sort-key': ['*', -1, ['get', 'prominenceScore']],
                    'text-allow-overlap': false,
                    'text-field': ['get', 'surname'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
                    'text-ignore-placement': false,
                    'text-justify': 'auto',
                    'text-keep-upright': true,
                    'text-letter-spacing': 0.02,
                    'text-padding': 2,
                    'text-pitch-alignment': 'viewport',
                    'text-radial-offset': 0.35,
                    'text-rotation-alignment': 'viewport',
                    'text-size': [
                        'interpolate', ['linear'], ['zoom'],
                        1, [
                            'interpolate', ['linear'], ['get', 'localCount'],
                            1, 10,
                            3, 12,
                            10, 18,
                            30, 28,
                            100, 36,
                        ],
                        4, [
                            'interpolate', ['linear'], ['get', 'localCount'],
                            1, 11,
                            3, 13,
                            10, 19,
                            30, 28,
                            100, 36,
                        ],
                        7, [
                            'interpolate', ['linear'], ['get', 'localCount'],
                            1, 12,
                            3, 14,
                            10, 20,
                            30, 28,
                            100, 36,
                        ],
                    ],
                    'text-variable-anchor': [
                        'center', 'top', 'bottom', 'left', 'right',
                        'top-left', 'top-right', 'bottom-left', 'bottom-right',
                    ],
                },
                paint: {
                    'text-color': '#111111',
                    'text-halo-blur': 0.6,
                    'text-halo-color': 'rgba(255, 252, 247, 0.96)',
                    'text-halo-width': 1.5,
                    'text-opacity': 0.96,
                },
            });

            this.mapInstance.on('click', 'surname-labels', this.handleSurnameLabelClick);
            this.mapInstance.on('mouseenter', 'surname-labels', this.handleSurnameLabelMouseEnter);
            this.mapInstance.on('mousemove', 'surname-labels', this.handleSurnameLabelMouseMove);
            this.mapInstance.on('mouseleave', 'surname-labels', this.handleSurnameLabelMouseLeave);
            this.updateSurnameProfileHighlights();
            this.syncSurnameSelectionPaint();
        },

        toggleMapLayerMenu() {
            this.mapLayerMenuOpen = !this.mapLayerMenuOpen;
        },

        closeMapLayerMenu() {
            this.mapLayerMenuOpen = false;
        },

        onMapLayerDocumentClick(event) {
            if (!this.mapLayerMenuOpen) return;
            const control = this.$refs.mapLayerControl;
            if (control && control.contains(event.target)) return;
            this.closeMapLayerMenu();
        },

        selectMapLayerStyle(useSatelliteStyle) {
            this.useSatelliteStyle = useSatelliteStyle;

            this.closeMapLayerMenu();
        },

        setGlobeProjection(value) {
            this.useGlobeProjection = value;
        },

        async loadPlaces() {
            this.loading = true;
            this.personIDErrorMsg = "";
            try {
                const typesParam = this.selectedType ? [this.selectedType] : undefined;

                // Priority: if personIDFilter is set, use it; otherwise use onlyCorePeople
                let personIDsParam;
                if (this.personIDFilter) {
                    personIDsParam = [this.personIDFilter];
                } else if (this.onlyCorePeople) {
                    personIDsParam = this.corePersonIds;
                }

                const data = await fetchPlaces({
                    ...(typesParam ? { types: typesParam } : {}),
                    ...(personIDsParam ? { personIDs: personIDsParam } : {}),
                    ...this.dateParams,
                });

                const normalized = (data || []).map((p) => ({
                    ...p,
                    latitude: parseFloat(p.latitude),
                    longitude: parseFloat(p.longitude),
                    zoom: p.zoom ? parseInt(p.zoom, 10) : 10,
                }));

                if (normalized.length === 0) {
                    console.warn("No valid places found.");
                }

                this.places = normalized;
            } catch (error) {
                console.error("Failed to fetch places:", error);
                this.places = [];
            } finally {
                this.loading = false;
            }
        },

        async loadPlaceGroups() {
            try {
                const response = await fetch('/data/place-groups.json');
                if (!response.ok) return;

                const groups = await response.json();
                if (!Array.isArray(groups)) return;

                const byId = {};
                const byName = {};
                const byAlias = {};
                const normalizedGroups = groups
                    .filter((group) => group?.id && group?.displayName)
                    .map((group) => {
                        const memberPlaceIds = Array.from(new Set([
                            group.canonicalPlaceId,
                            ...(Array.isArray(group.memberPlaceIds) ? group.memberPlaceIds : []),
                        ]
                            .map((placeId) => String(placeId || '').trim())
                            .filter(Boolean)));

                        const memberPlaceNames = Array.from(new Set(
                            (Array.isArray(group.memberPlaceNames) ? group.memberPlaceNames : [])
                                .map((placeName) => String(placeName || '').trim())
                                .filter(Boolean)
                        ));

                        const aliases = Array.from(new Set([
                            group.displayName,
                            ...(Array.isArray(group.aliases) ? group.aliases : []),
                        ]
                            .map((alias) => String(alias || '').trim())
                            .filter(Boolean)));

                        return {
                            ...group,
                            canonicalPlaceId: group.canonicalPlaceId ? String(group.canonicalPlaceId).trim() : '',
                            memberPlaceIds,
                            memberPlaceNames,
                            aliases,
                        };
                    });

                normalizedGroups.forEach((group) => {
                    group.memberPlaceIds.forEach((placeId) => {
                        byId[placeId] = group;
                    });

                    group.memberPlaceNames.forEach((placeName) => {
                        const key = this.normalizePlaceGroupKey(placeName);
                        if (key) byName[key] = group;
                    });

                    [...group.aliases, ...group.memberPlaceNames].forEach((alias) => {
                        const key = this.normalizePlaceGroupKey(alias);
                        if (key) byAlias[key] = group;
                    });
                });

                this.placeGroups = normalizedGroups;
                this.placeGroupByPlaceId = byId;
                this.placeGroupByPlaceName = byName;
                this.placeGroupByAlias = byAlias;

                if (this.useGlobeView && this.mapInstance && this.mapInstance.isStyleLoaded()) {
                    this.updateMapboxMarkers();
                    this.recenterAfterData();
                }
            } catch (error) {
                console.warn('Failed to load curated place groups:', error);
            }
        },

        async loadPlaceAreas() {
            try {
                const response = await fetch('/data/place-areas.geojson');
                if (!response.ok) return;

                const geojson = await response.json();
                const features = (geojson.features || [])
                    .filter((feature) => feature?.geometry && feature?.properties)
                    .map((feature, index) => ({
                        ...feature,
                        properties: {
                            ...feature.properties,
                            areaId: feature.properties.areaId || `place-area-${index}`,
                        },
                    }));

                const byId = {};
                const byAlias = {};

                features.forEach((feature) => {
                    const props = feature.properties || {};
                    const placeIds = [
                        ...(Array.isArray(props.placeIds) ? props.placeIds : []),
                        ...(Array.isArray(props.placeIDs) ? props.placeIDs : []),
                    ];

                    placeIds.forEach((placeId) => {
                        const key = String(placeId || '').trim();
                        if (key) byId[key] = feature;
                    });

                    const aliases = [
                        props.name,
                        ...(Array.isArray(props.placeNameAliases) ? props.placeNameAliases : []),
                    ];

                    aliases.forEach((alias) => {
                        const key = this.normalizePlaceAreaKey(alias);
                        if (key) byAlias[key] = feature;
                    });
                });

                this.placeAreaFeatureCollection = { type: 'FeatureCollection', features };
                this.placeAreaFeatureById = byId;
                this.placeAreaFeatureByAlias = byAlias;
                this.placeAreaGeometryIndexCache.clear();

                if (this.useGlobeView && this.mapInstance && this.mapInstance.isStyleLoaded()) {
                    this.updateMapboxMarkers();
                    this.recenterAfterData();
                }
            } catch (error) {
                console.warn('Failed to load curated place areas:', error);
            }
        },

        normalizePlaceAreaKey(value) {
            return String(value || '')
                .trim()
                .replace(/\s+/g, ' ')
                .toLowerCase();
        },

        normalizePlaceGroupKey(value) {
            return this.normalizePlaceAreaKey(value);
        },

        resolvePlaceGroup(place) {
            if (!place) return null;

            const placeId = String(place.ID || '').trim();
            if (placeId && this.placeGroupByPlaceId[placeId]) {
                return this.placeGroupByPlaceId[placeId];
            }

            const placeNameKey = this.normalizePlaceGroupKey(place.place);
            return this.placeGroupByPlaceName[placeNameKey] || null;
        },

        getPlaceGroupMemberIds(group, memberPlaces = []) {
            if (!group) return [];

            return Array.from(new Set([
                ...(Array.isArray(group.memberPlaceIds) ? group.memberPlaceIds : []),
                ...memberPlaces.map((place) => place?.ID),
            ]
                .map((placeId) => String(placeId || '').trim())
                .filter(Boolean)));
        },

        buildGroupedMarkerPlaces(places) {
            if (!Array.isArray(places) || places.length === 0 || this.placeGroups.length === 0) {
                return places || [];
            }

            const markerPlaces = [];
            const groupedPlaces = new Map();

            places.forEach((place) => {
                const group = this.resolvePlaceGroup(place);
                if (!group) {
                    markerPlaces.push(place);
                    return;
                }

                if (!groupedPlaces.has(group.id)) {
                    groupedPlaces.set(group.id, {
                        group,
                        members: [],
                    });
                }

                groupedPlaces.get(group.id).members.push(place);
            });

            groupedPlaces.forEach(({ group, members }) => {
                markerPlaces.push(this.buildPlaceGroupMarker(group, members));
            });

            return markerPlaces
                .filter(Boolean)
                .sort((a, b) => String(a.place || '').localeCompare(String(b.place || '')));
        },

        buildPlaceGroupMarker(group, places) {
            if (!group || !Array.isArray(places) || places.length === 0) return null;

            const canonicalPlace = places.find((place) => (
                String(place.ID) === String(group.canonicalPlaceId)
            )) || places[0];
            if (!canonicalPlace) return null;

            const counts = places.reduce((merged, place) => {
                Object.entries(place.counts || {}).forEach(([type, count]) => {
                    merged[type] = (merged[type] || 0) + Number(count || 0);
                });
                return merged;
            }, {});

            return {
                ...canonicalPlace,
                ID: `group:${group.id}`,
                canonicalPlaceId: group.canonicalPlaceId || canonicalPlace.ID,
                place: group.displayName,
                counts: Object.keys(counts).length ? counts : canonicalPlace.counts,
                placeGroup: group,
                placeIds: this.getPlaceGroupMemberIds(group, places),
                memberPlaces: places,
            };
        },

        placeMatchesSearch(place, normalizedQuery) {
            if (!place?.place || !normalizedQuery) return false;

            const searchable = [
                place.place,
                ...(Array.isArray(place.memberPlaces) ? place.memberPlaces.map((member) => member.place) : []),
                ...(Array.isArray(place.placeGroup?.aliases) ? place.placeGroup.aliases : []),
                ...(Array.isArray(place.placeGroup?.memberPlaceNames) ? place.placeGroup.memberPlaceNames : []),
            ];

            return searchable.some((value) => (
                String(value || '').toLowerCase().includes(normalizedQuery)
            ));
        },

        placeGroupMatchesSearch(group, normalizedQuery) {
            if (!group || !normalizedQuery) return false;

            const searchable = [
                group.displayName,
                ...(Array.isArray(group.aliases) ? group.aliases : []),
                ...(Array.isArray(group.memberPlaceNames) ? group.memberPlaceNames : []),
            ];

            return searchable.some((value) => (
                String(value || '').toLowerCase().includes(normalizedQuery)
            ));
        },

        getLoadedPlacesForGroup(group) {
            if (!group) return [];

            return this.placesWithCoordinates.filter((place) => this.resolvePlaceGroup(place) === group);
        },

        getPlaceSuggestionSearchValues(place) {
            if (!place) return [];

            const group = place.placeGroup;
            if (!group) return [place.place];

            return [
                group.displayName,
                ...(Array.isArray(group.aliases) ? group.aliases : []),
                ...(Array.isArray(group.memberPlaceNames) ? group.memberPlaceNames : []),
            ];
        },

        getPlaceSuggestionMatchRank(place, normalizedQuery) {
            const values = this.getPlaceSuggestionSearchValues(place)
                .map((value) => String(value || '').trim().toLowerCase())
                .filter(Boolean);

            if (values.some((value) => value === normalizedQuery)) return 0;
            if (values.some((value) => value.startsWith(normalizedQuery))) return 1;
            if (values.some((value) => value.includes(normalizedQuery))) return 2;
            return 3;
        },

        comparePlaceSuggestions(a, b, normalizedQuery) {
            const groupRankA = this.isGroupedPlaceMarker(a) ? 0 : 1;
            const groupRankB = this.isGroupedPlaceMarker(b) ? 0 : 1;

            return groupRankA - groupRankB ||
                this.getPlaceSuggestionMatchRank(a, normalizedQuery) -
                    this.getPlaceSuggestionMatchRank(b, normalizedQuery) ||
                String(a.place || '').localeCompare(String(b.place || ''));
        },

        getPlaceSuggestionsForQuery(normalizedQuery) {
            const suggestions = [];
            const seen = new Set();

            const addSuggestion = (place) => {
                if (!place) return;
                const key = String(place.ID || `${place.latitude},${place.longitude},${place.place}`);
                if (seen.has(key)) return;
                seen.add(key);
                suggestions.push(place);
            };

            // Add curated groups first so deduplication keeps the fully aggregated
            // group marker rather than a partial marker/area representation.
            this.placeGroups
                .filter((group) => this.placeGroupMatchesSearch(group, normalizedQuery))
                .forEach((group) => {
                    addSuggestion(this.buildPlaceGroupMarker(group, this.getLoadedPlacesForGroup(group)));
                });

            this.markerPlaces
                .filter((place) => this.placeMatchesSearch(place, normalizedQuery))
                .forEach(addSuggestion);

            this.areaPlaces
                .filter((place) => this.placeMatchesSearch(place, normalizedQuery))
                .forEach(addSuggestion);

            return suggestions
                .sort((a, b) => this.comparePlaceSuggestions(a, b, normalizedQuery))
                .slice(0, 12);
        },

        resolvePresetPlace(placeName) {
            const normalizedName = this.normalizePlaceAreaKey(placeName);
            if (!normalizedName) return null;

            const candidates = this.mapBoundsPlaces.length
                ? this.mapBoundsPlaces
                : this.buildGroupedMarkerPlaces(this.placesWithCoordinates);

            const exactMatch = candidates.find((place) => {
                const names = [
                    place.place,
                    ...(Array.isArray(place.memberPlaces) ? place.memberPlaces.map((member) => member.place) : []),
                    ...(Array.isArray(place.placeGroup?.memberPlaceNames) ? place.placeGroup.memberPlaceNames : []),
                    ...(Array.isArray(place.placeGroup?.aliases) ? place.placeGroup.aliases : []),
                ];

                return names.some((name) => this.normalizePlaceAreaKey(name) === normalizedName);
            });

            if (exactMatch) return exactMatch;

            const shortName = String(placeName || '').split(',')[0]?.trim();
            if (!shortName || shortName.length < 2) return null;

            return this.getPlaceSuggestionsForQuery(shortName.toLowerCase())[0] || null;
        },

        getPanelPlaceIds(place) {
            if (Array.isArray(place?.placeIds) && place.placeIds.length) {
                return place.placeIds;
            }

            if (place?.ID === undefined || place?.ID === null || String(place.ID).startsWith('group:')) {
                return [];
            }

            return [place.ID];
        },

        isGroupedPlaceMarker(place) {
            return Boolean(place?.placeGroup || String(place?.ID || '').startsWith('group:'));
        },

        async getAggregatePlaceEventCounts(placeIds, personID = null) {
            const ids = Array.from(new Set(
                (placeIds || []).map((placeId) => String(placeId || '').trim()).filter(Boolean)
            ));
            if (!ids.length) return {};

            const cacheKey = `group:${ids.join('|')}${personID ? `:${personID}` : ''}`;
            if (this.popupEventCountCache[cacheKey]) {
                return this.popupEventCountCache[cacheKey];
            }

            try {
                const eventLists = await Promise.all(ids.map(async (placeId) => {
                    try {
                        return await fetchPlaceEvents(placeId);
                    } catch (error) {
                        console.warn(`Failed to fetch grouped place events for ${placeId}:`, error);
                        return [];
                    }
                }));

                const counts = eventLists.flat()
                    .filter((event) => !personID || event.personID === personID)
                    .reduce((acc, event) => {
                        const key = this.normalizeEventType(event.eventType);
                        if (!key) return acc;
                        acc[key] = (acc[key] || 0) + 1;
                        return acc;
                    }, {});

                this.popupEventCountCache = {
                    ...this.popupEventCountCache,
                    [cacheKey]: counts,
                };

                return counts;
            } catch (error) {
                console.error('Failed to fetch grouped place event counts:', error);
                return {};
            }
        },

        resolvePlaceAreaFeature(place) {
            if (!place) return null;

            const placeId = String(place.ID || '').trim();
            if (placeId && this.placeAreaFeatureById[placeId]) {
                return this.placeAreaFeatureById[placeId];
            }

            const placeNameKey = this.normalizePlaceAreaKey(place.place);
            return this.placeAreaFeatureByAlias[placeNameKey] || null;
        },

        getPlaceAreaSubPlaceSuffixes(areaFeature) {
            const props = areaFeature?.properties || {};
            if (Array.isArray(props.subPlaceNameSuffixes)) {
                return props.subPlaceNameSuffixes;
            }

            const aliases = [
                props.name,
                ...(Array.isArray(props.placeNameAliases) ? props.placeNameAliases : []),
            ];

            return aliases
                .filter((alias) => String(alias || '').includes(','))
                .filter((alias) => !String(alias || '').trim().toLowerCase().startsWith('state of '))
                .map((alias) => String(alias).trim());
        },

        shouldAreaFeatureFadeOnZoom(areaFeature) {
            return this.getPlaceAreaSubPlaceSuffixes(areaFeature).length > 0;
        },

        getPlaceAreaGeometryIndex(areaFeature) {
            if (!areaFeature?.geometry) return [];

            const areaId = areaFeature.properties?.areaId;
            if (areaId && this.placeAreaGeometryIndexCache.has(areaId)) {
                return this.placeAreaGeometryIndexCache.get(areaId);
            }

            const geometry = areaFeature.geometry;
            const polygonCoordinates = geometry.type === 'Polygon'
                ? [geometry.coordinates]
                : geometry.type === 'MultiPolygon'
                    ? geometry.coordinates
                    : [];

            const index = polygonCoordinates.map((coordinates) => {
                const polygonGeometry = { type: 'Polygon', coordinates };
                return {
                    bounds: this.getGeometryBounds(polygonGeometry),
                    feature: {
                        type: 'Feature',
                        properties: {},
                        geometry: polygonGeometry,
                    },
                };
            }).filter(({ bounds }) => Boolean(bounds));

            if (areaId) {
                this.placeAreaGeometryIndexCache.set(areaId, index);
            }

            return index;
        },

        isSubPlaceOfArea(place, areaFeature) {
            if (!place || !areaFeature?.geometry) return false;
            if (this.resolvePlaceAreaFeature(place) === areaFeature) return false;

            const longitude = Number(place.longitude);
            const latitude = Number(place.latitude);
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return false;

            return this.getPlaceAreaGeometryIndex(areaFeature).some(({ bounds, feature }) => {
                const pointIsWithinBounds = longitude >= bounds[0][0] &&
                    longitude <= bounds[1][0] &&
                    latitude >= bounds[0][1] &&
                    latitude <= bounds[1][1];

                return pointIsWithinBounds && booleanPointInPolygon(
                    [longitude, latitude],
                    feature,
                    { ignoreBoundary: false }
                );
            });
        },

        yieldToBrowser() {
            return new Promise((resolve) => setTimeout(resolve, 0));
        },

        async waitForPanelPaint() {
            await this.$nextTick();

            if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
                await this.yieldToBrowser();
                return;
            }

            await new Promise((resolve) => {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(resolve);
                });
            });
        },

        async getPlaceSubPlaceShells(
            place,
            sourcePlaces = this.placesWithCoordinates,
            shouldCancel = null
        ) {
            const areaFeature = this.resolvePlaceAreaFeature(place);
            if (!areaFeature) return [];

            const subPlaces = [];
            const batchSize = 25;

            for (let index = 0; index < sourcePlaces.length; index += 1) {
                if (shouldCancel?.()) return null;

                const candidate = sourcePlaces[index];
                if (this.isSubPlaceOfArea(candidate, areaFeature)) {
                    subPlaces.push({
                        ...candidate,
                        counts: candidate.counts || {},
                        totalEvents: Object.values(candidate.counts || {})
                            .reduce((sum, count) => sum + Number(count || 0), 0),
                    });
                }

                if ((index + 1) % batchSize === 0) {
                    await this.yieldToBrowser();
                }
            }

            return subPlaces.sort((a, b) => (
                String(a.place || '').localeCompare(String(b.place || ''))
            ));
        },

        async getPlaceSubPlaces(
            place,
            personID = this.personIDFilter,
            sourcePlaces = this.placesWithCoordinates,
            shouldCancel = null
        ) {
            const subPlaces = await this.getPlaceSubPlaceShells(place, sourcePlaces, shouldCancel);
            if (subPlaces === null) return null;

            const hydratedSubPlaces = [];

            for (let index = 0; index < subPlaces.length; index += 1) {
                if (shouldCancel?.()) return null;

                const subPlace = subPlaces[index];
                const counts = await this.getPlaceEventCounts(subPlace, personID);
                const totalEvents = Object.values(counts || {}).reduce((sum, count) => sum + Number(count || 0), 0);

                hydratedSubPlaces.push({
                    ...subPlace,
                    counts,
                    totalEvents,
                });

                if ((index + 1) % 25 === 0) {
                    await this.yieldToBrowser();
                }
            }

            return hydratedSubPlaces;
        },

        async openPlacePanel(place) {
            if (!place) return;

            const placeIds = this.getPanelPlaceIds(place);
            const placeId = place.canonicalPlaceId || place.ID;
            const requestId = this.selectedPlacePanelRequestId + 1;
            this.selectedPlacePanelRequestId = requestId;
            const areaFeature = this.resolvePlaceAreaFeature(place);

            this.openSidePanel(placeId, place.place, null, [], placeIds);
            this.selectedPlaceCountsLoading = true;
            this.selectedPlaceSubPlacesLoading = Boolean(areaFeature);

            await this.waitForPanelPaint();

            const shouldCancel = () => (
                this.selectedPlacePanelRequestId !== requestId ||
                !this.sidePanelVisible ||
                String(this.selectedPlaceId || '') !== String(placeId || '')
            );
            if (shouldCancel()) return;

            this.getPlaceEventCounts(place, this.personIDFilter).then((counts) => {
                if (this.selectedPlacePanelRequestId !== requestId || !this.sidePanelVisible) return;
                if (String(this.selectedPlaceId || '') !== String(placeId || '')) return;

                this.selectedPlaceCounts = counts;
                this.selectedPlaceCountsLoading = false;
            }).catch((error) => {
                console.error('Failed to hydrate place panel counts:', error);
                if (this.selectedPlacePanelRequestId === requestId) {
                    this.selectedPlaceCountsLoading = false;
                }
            });

            if (!areaFeature) return;

            this.getPlaceSubPlaces(place, this.personIDFilter, this.placesWithCoordinates, shouldCancel).then((subPlaces) => {
                if (subPlaces === null) return;
                if (this.selectedPlacePanelRequestId !== requestId || !this.sidePanelVisible) return;
                if (String(this.selectedPlaceId || '') !== String(placeId || '')) return;

                this.selectedPlaceSubPlaces = subPlaces;
                this.selectedPlaceSubPlacesLoading = false;
            }).catch((error) => {
                console.error('Failed to hydrate place panel sub-locations:', error);
                if (this.selectedPlacePanelRequestId === requestId) {
                    this.selectedPlaceSubPlacesLoading = false;
                }
            });
        },

        // Helper method to parse dates for sorting (from EventMap)
        parseDateToTimestamp(dateString) {
            if (!dateString || typeof dateString !== "string") return null;

            const trimmed = dateString.trim();
            if (!trimmed || trimmed === "0000-00-00") return null;

            const normalized = trimmed
                .replace(/^(?:[~±≈]|c\.?|ca\.?|circa)\s*/i, "")
                .replace(/\?/g, "")
                .trim();

            if (!normalized) return null;

            if (/^\d{4}$/.test(normalized)) {
                const year = Number(normalized);
                return Number.isFinite(year) ? Date.UTC(year, 0, 1) : null;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
                const [year, month, day] = normalized.split("-").map(Number);
                if (!year) return null;
                const safeMonth = month > 0 ? month - 1 : 0;
                const safeDay = day > 0 ? day : 1;
                return Date.UTC(year, safeMonth, safeDay);
            }

            const monthMap = {
                jan: 0, january: 0, janv: 0,
                feb: 1, februari: 1, february: 1,
                mrt: 2, maart: 2, mar: 2, march: 2,
                apr: 3, april: 3,
                mei: 4, may: 4,
                jun: 5, juni: 5, june: 5,
                jul: 6, juli: 6, july: 6,
                aug: 7, augustus: 7, august: 7,
                sep: 8, sept: 8, september: 8,
                okt: 9, october: 9, oktober: 9,
                nov: 10, november: 10,
                dec: 11, december: 11
            };

            const dayMonthYearMatch = normalized.match(/^(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})$/);
            if (dayMonthYearMatch) {
                const day = Number(dayMonthYearMatch[1]);
                const monthKey = dayMonthYearMatch[2].toLowerCase().replace(".", "");
                const year = Number(dayMonthYearMatch[3]);
                const month = monthMap[monthKey];
                if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
                    return Date.UTC(year, month, day);
                }
            }

            const monthYearMatch = normalized.match(/^([A-Za-z.]+)\s+(\d{4})$/);
            if (monthYearMatch) {
                const monthKey = monthYearMatch[1].toLowerCase().replace(".", "");
                const year = Number(monthYearMatch[2]);
                const month = monthMap[monthKey];
                if (Number.isFinite(year) && Number.isFinite(month)) {
                    return Date.UTC(year, month, 1);
                }
            }

            const parsed = Date.parse(normalized);
            return Number.isNaN(parsed) ? null : parsed;
        },

        getEventSortKey(event) {
            const dateCandidates = [event.date, event.dateText, event.eventdate, event.eventdatetr]
                .filter(Boolean);

            for (const candidate of dateCandidates) {
                const parsed = this.parseDateToTimestamp(candidate);
                if (parsed !== null) return parsed;
            }

            const fallbackBase = Number.MAX_SAFE_INTEGER - 1000000;
            return fallbackBase + (event.originalIndex || 0);
        },

        interpolateGreatCircle(start, end, numPoints) {
            // Interpolate points along a great circle for smooth 3D arcs
            const [lon1, lat1] = start;
            const [lon2, lat2] = end;

            // Convert to radians
            const toRad = deg => deg * Math.PI / 180;
            const toDeg = rad => rad * 180 / Math.PI;

            const lat1Rad = toRad(lat1);
            const lat2Rad = toRad(lat2);
            const lon1Rad = toRad(lon1);
            const lon2Rad = toRad(lon2);

            const dLon = lon2Rad - lon1Rad;

            // Calculate angular distance
            const d = Math.acos(
                Math.sin(lat1Rad) * Math.sin(lat2Rad) +
                Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon)
            );

            if (d === 0) return [[lon1, lat1], [lon2, lat2]];

            const points = [];
            for (let i = 0; i <= numPoints; i++) {
                const f = i / numPoints;

                // Spherical interpolation
                const a = Math.sin((1 - f) * d) / Math.sin(d);
                const b = Math.sin(f * d) / Math.sin(d);

                const x = a * Math.cos(lat1Rad) * Math.cos(lon1Rad) + b * Math.cos(lat2Rad) * Math.cos(lon2Rad);
                const y = a * Math.cos(lat1Rad) * Math.sin(lon1Rad) + b * Math.cos(lat2Rad) * Math.sin(lon2Rad);
                const z = a * Math.sin(lat1Rad) + b * Math.sin(lat2Rad);

                const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
                const lon = Math.atan2(y, x);

                points.push([toDeg(lon), toDeg(lat)]);
            }

            return points;
        },

        buildPersonChronologicalSegments() {
            if (!this.personEventLocations || this.personEventLocations.length === 0) {
                this.personChronologicalSegments = [];
                return;
            }

            // Sort events chronologically
            const sortable = this.personEventLocations
                .map(event => ({
                    ...event,
                    sortKey: this.getEventSortKey(event)
                }))
                .sort((a, b) => {
                    if (a.sortKey === b.sortKey) {
                        return (a.originalIndex || 0) - (b.originalIndex || 0);
                    }
                    return a.sortKey - b.sortKey;
                });

            // Build segments between consecutive events
            const segments = [];
            for (let index = 1; index < sortable.length; index++) {
                const from = sortable[index - 1];
                const to = sortable[index];

                if (!from || !to) continue;
                if (from.latitude === to.latitude && from.longitude === to.longitude) continue;

                segments.push({
                    from: [Number(from.latitude), Number(from.longitude)],
                    to: [Number(to.latitude), Number(to.longitude)],
                    fromEvent: from,
                    toEvent: to
                });
            }

            this.personChronologicalSegments = segments;
        },
        getGeometryBounds(geometry) {
            if (!geometry?.coordinates) return null;

            let minLng = Infinity;
            let maxLng = -Infinity;
            let minLat = Infinity;
            let maxLat = -Infinity;

            const visit = (coords) => {
                if (!Array.isArray(coords)) return;

                if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                    const [lng, lat] = coords;
                    minLng = Math.min(minLng, lng);
                    maxLng = Math.max(maxLng, lng);
                    minLat = Math.min(minLat, lat);
                    maxLat = Math.max(maxLat, lat);
                    return;
                }

                coords.forEach(visit);
            };

            visit(geometry.coordinates);

            if (![minLng, maxLng, minLat, maxLat].every(Number.isFinite)) {
                return null;
            }

            return [[minLng, minLat], [maxLng, maxLat]];
        },

        extendBounds(bounds, nextBounds) {
            if (!nextBounds) return bounds;
            if (!bounds) return nextBounds;

            return [
                [
                    Math.min(bounds[0][0], nextBounds[0][0]),
                    Math.min(bounds[0][1], nextBounds[0][1]),
                ],
                [
                    Math.max(bounds[1][0], nextBounds[1][0]),
                    Math.max(bounds[1][1], nextBounds[1][1]),
                ],
            ];
        },

        getPlaceBounds(place) {
            const areaFeature = place?.areaFeature || this.resolvePlaceAreaFeature(place);
            const areaBounds = this.getGeometryBounds(areaFeature?.geometry);
            if (areaBounds) return areaBounds;

            if (isNaN(Number(place?.longitude)) || isNaN(Number(place?.latitude))) {
                return null;
            }

            const lng = Number(place.longitude);
            const lat = Number(place.latitude);
            return [[lng, lat], [lng, lat]];
        },

        adjustMapBounds(places) {
            if (this.useGlobeView && this.mapInstance) {
                // Mapbox bounds adjustment
                if (!this.mapInstance || !places || places.length === 0) return;

                const combinedBounds = places.reduce((bounds, place) => (
                    this.extendBounds(bounds, this.getPlaceBounds(place))
                ), null);

                if (!combinedBounds) return;

                const isSinglePoint = combinedBounds[0][0] === combinedBounds[1][0] &&
                    combinedBounds[0][1] === combinedBounds[1][1];

                if (places.length === 1 && isSinglePoint) {
                    this.mapInstance.flyTo({
                        center: [places[0].longitude, places[0].latitude],
                        zoom: places[0].zoom || 10,
                        duration: 1000
                    });
                } else {
                    const sidePadding = 80;

                    this.mapInstance.fitBounds(combinedBounds, {
                        padding: {
                            top: 80,
                            bottom: 80,
                            left: sidePadding,
                            right: sidePadding
                        },
                        duration: 1000
                    });
                }
            }
        },

        selectTourMode(modeId) {
            if (!this.tourModes.some((mode) => mode.id === modeId)) return;

            this.tourSelectedModeId = modeId;

            if (this.tourActive) {
                this.clearTourAutoplay();
                this.resolvedTourStops = this.resolveTourStops();
                this.tourCurrentIndex = 0;
                this.playTourStop(0);
            }
        },

        async startPlaceTour() {
            if (!mapboxgl.accessToken) return;

            if (!this.useGlobeView) {
                this.useGlobeView = true;
                await this.$nextTick();
            }

            if (!this.mapInstance && this.$refs.mapContainer) {
                this.initializeMapbox();
            }

            await this.ensureTourPlaces();
            this.resolvedTourStops = this.resolveTourStops();

            if (!this.resolvedTourStops.length) return;

            if (this.sidePanelVisible) {
                this.closeSidePanel();
            }

            this.stopRotation();
            clearTimeout(this.idleTimeout);
            this.userInteracting = true;
            this.tourActive = true;
            this.tourPaused = false;
            this.tourCurrentIndex = 0;
            this.tourCurrentStepIndex = 0;
            this.tourPhase = 'approach';
            this.ensureTourSceneLayers();
            this.playTourStop(0);
        },

        closePlaceTour() {
            this.tourActive = false;
            this.tourPaused = false;
            this.clearTourAutoplay();
            this.removeTourSceneLayers();
            this.tourStatsLoading = false;
            this.tourCurrentCounts = {};
            this.tourCurrentSubPlaces = [];
            this.tourStatsRequestId += 1;
        },

        toggleTourPlayback() {
            if (!this.tourActive) return;

            this.tourPaused = !this.tourPaused;
            if (this.tourPaused) {
                this.clearTourAutoplay();
                return;
            }

            const timeline = this.currentTourStop?.timeline || [];
            const nextStep = Math.min(this.tourCurrentStepIndex + 1, timeline.length - 1);
            if (timeline[nextStep]) {
                this.runTourTimelineStep(nextStep);
            } else {
                this.scheduleNextTourStop(800);
            }
        },

        goToPreviousTourStop() {
            if (!this.resolvedTourStops.length) return;
            this.tourPaused = false;
            const nextIndex = (this.tourCurrentIndex - 1 + this.resolvedTourStops.length) % this.resolvedTourStops.length;
            this.playTourStop(nextIndex);
        },

        goToNextTourStop() {
            if (!this.resolvedTourStops.length) return;
            this.tourPaused = false;
            const nextIndex = (this.tourCurrentIndex + 1) % this.resolvedTourStops.length;
            this.playTourStop(nextIndex);
        },

        clearTourAutoplay() {
            if (this.tourAutoplayTimeout) {
                clearTimeout(this.tourAutoplayTimeout);
                this.tourAutoplayTimeout = null;
            }

            this.tourTimelineTimers.forEach((timerId) => clearTimeout(timerId));
            this.tourTimelineTimers = [];
        },

        scheduleNextTourStop(delay = 2200) {
            this.clearTourAutoplay();
            if (!this.tourActive || this.tourPaused || this.resolvedTourStops.length < 2) return;

            this.tourAutoplayTimeout = setTimeout(() => {
                this.goToNextTourStop();
            }, delay);
        },

        async ensureTourPlaces() {
            if (this.tourPlacesLoaded) return;

            try {
                const data = await fetchPlaces({});
                this.tourPlaces = (data || [])
                    .map((place) => ({
                        ...place,
                        latitude: parseFloat(place.latitude),
                        longitude: parseFloat(place.longitude),
                        zoom: place.zoom ? parseInt(place.zoom, 10) : 10,
                    }))
                    .filter((place) => (
                        !isNaN(Number(place.latitude)) &&
                        !isNaN(Number(place.longitude))
                    ));
                this.tourPlacesLoaded = true;
            } catch (error) {
                console.error('Failed to load unfiltered tour places:', error);
                this.tourPlaces = [];
                this.tourPlacesLoaded = true;
            }
        },

        resolveTourStops() {
            const activeStopIds = new Set(this.activeTourMode?.stopIds || placeTourStops.map((stop) => stop.id));
            return placeTourStops
                .filter((stop) => activeStopIds.has(stop.id))
                .map((stop) => ({
                    ...stop,
                    place: this.resolveTourStopPlace(stop),
                }));
        },

        resolveTourStopPlace(stop) {
            if (stop.placeGroupId) {
                const group = this.placeGroups.find((candidate) => candidate.id === stop.placeGroupId);
                const groupedPlace = this.buildPlaceGroupMarker(group, this.getTourPlacesForGroup(group));
                if (groupedPlace) return groupedPlace;
            }

            if (stop.placeName) {
                const normalizedName = this.normalizePlaceGroupKey(stop.placeName);
                const matchedPlace = this.tourPlaces.find((place) => (
                    this.normalizePlaceGroupKey(place.place) === normalizedName
                ));
                if (matchedPlace) return matchedPlace;
            }

            return {
                ID: `tour:${stop.id}`,
                place: stop.placeName || stop.id,
                longitude: stop.fallbackCenter?.[0],
                latitude: stop.fallbackCenter?.[1],
                counts: {},
                placeIds: [],
            };
        },

        getTourPlacesForGroup(group) {
            if (!group) return [];

            return this.tourPlaces.filter((place) => {
                const resolvedGroup = this.resolvePlaceGroup(place);
                if (resolvedGroup?.id === group.id) return true;

                const placeId = String(place.ID || '').trim();
                if (group.memberPlaceIds.includes(placeId)) return true;

                const placeNameKey = this.normalizePlaceGroupKey(place.place);
                return group.memberPlaceNames.some((name) => this.normalizePlaceGroupKey(name) === placeNameKey);
            });
        },

        playTourStop(index) {
            if (!this.tourActive || !this.mapInstance || !this.resolvedTourStops.length) return;

            const normalizedIndex = (index + this.resolvedTourStops.length) % this.resolvedTourStops.length;
            this.tourCurrentIndex = normalizedIndex;
            this.tourCurrentStepIndex = 0;
            const stop = this.resolvedTourStops[normalizedIndex];

            this.clearTourAutoplay();
            this.stopRotation();
            clearTimeout(this.idleTimeout);
            this.userInteracting = true;
            this.ensureTourSceneLayers();
            this.updateTourSceneLayers();
            this.hydrateTourStopStats(stop);
            this.runTourTimelineStep(0);
        },

        runTourTimelineStep(stepIndex) {
            if (!this.tourActive || this.tourPaused || !this.currentTourStop) return;

            const timeline = this.currentTourStop.timeline || [];
            const step = timeline[stepIndex];
            if (!step) {
                this.scheduleNextTourStop();
                return;
            }

            this.tourCurrentStepIndex = stepIndex;
            this.tourPhase = step.type || 'diorama';
            this.applyTourCameraStep(this.currentTourStop, step);

            const nextTimer = setTimeout(() => {
                if (!this.tourActive || this.tourPaused) return;
                if (stepIndex < timeline.length - 1) {
                    this.runTourTimelineStep(stepIndex + 1);
                } else {
                    this.scheduleNextTourStop();
                }
            }, Number(step.duration || 3000) + 550);

            this.tourTimelineTimers.push(nextTimer);
        },

        applyTourCameraStep(stop, step) {
            if (!this.mapInstance) return;

            const camera = this.getTourCamera(stop, step);
            const runCamera = () => {
                if (!this.mapInstance) return;

                if (step.type === 'orbit') {
                    const currentBearing = this.mapInstance.getBearing();
                    this.mapInstance.easeTo({
                        center: camera.center,
                        zoom: camera.zoom,
                        pitch: camera.pitch,
                        bearing: currentBearing + Number(step.bearingDelta || 24),
                        duration: camera.duration,
                        essential: true,
                        easing: (t) => t * t * (3 - 2 * t),
                    });
                    return;
                }

                this.mapInstance.flyTo({
                    center: camera.center,
                    zoom: camera.zoom,
                    pitch: camera.pitch,
                    bearing: camera.bearing,
                    duration: camera.duration,
                    essential: true,
                    easing: (t) => t * t * (3 - 2 * t),
                });
            };

            if (this.mapInstance.isStyleLoaded()) {
                runCamera();
            } else {
                this.mapInstance.once('style.load', runCamera);
            }
        },

        getTourCamera(stop, step = {}) {
            const stopCenter = this.getTourStopCenter(stop) || stop.fallbackCenter || [20, 10];
            const center = step.center || stopCenter;

            return {
                center,
                zoom: step.zoom || stop.closeZoom || Number(stop.place?.zoom) || 6,
                pitch: step.pitch ?? 66,
                bearing: step.bearing ?? this.mapInstance?.getBearing?.() ?? 0,
                duration: step.duration || 3600,
            };
        },

        getTourStopCenter(stop) {
            if (!stop) return null;
            const place = stop.place || {};
            const fromPlace = [
                Number(place.longitude),
                Number(place.latitude),
            ];
            if (fromPlace.every((value) => Number.isFinite(value))) return fromPlace;
            return Array.isArray(stop.fallbackCenter) ? stop.fallbackCenter : null;
        },

        calculateDistanceKm(fromCenter, toCenter) {
            if (!fromCenter || !toCenter) return 0;
            const toRad = (value) => (Number(value) * Math.PI) / 180;
            const earthRadiusKm = 6371;
            const dLat = toRad(toCenter[1] - fromCenter[1]);
            const dLng = toRad(toCenter[0] - fromCenter[0]);
            const lat1 = toRad(fromCenter[1]);
            const lat2 = toRad(toCenter[1]);
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
            return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        },

        async hydrateTourStopStats(stop) {
            const requestId = this.tourStatsRequestId + 1;
            this.tourStatsRequestId = requestId;
            this.tourStatsLoading = true;
            this.tourCurrentCounts = {};
            this.tourCurrentSubPlaces = [];
            this.updateTourSceneLayers();

            try {
                const [counts, subPlaces] = await Promise.all([
                    this.getPlaceEventCounts(stop.place, null),
                    this.getPlaceSubPlaces(stop.place, null, this.tourPlaces),
                ]);

                if (this.tourStatsRequestId !== requestId || !this.tourActive) return;

                this.tourCurrentCounts = counts || {};
                this.tourCurrentSubPlaces = subPlaces || [];
                this.updateTourSceneLayers();
            } catch (error) {
                console.error('Failed to hydrate tour stop stats:', error);
                if (this.tourStatsRequestId !== requestId) return;
                this.tourCurrentCounts = {};
                this.tourCurrentSubPlaces = [];
                this.updateTourSceneLayers();
            } finally {
                if (this.tourStatsRequestId === requestId) {
                    this.tourStatsLoading = false;
                }
            }
        },

        async viewCurrentTourStopEvents() {
            if (!this.currentTourStop?.place) return;

            this.tourPaused = true;
            this.clearTourAutoplay();

            if (this.tourStatsLoading) {
                await this.hydrateTourStopStats(this.currentTourStop);
            }

            const place = this.currentTourStop.place;
            this.openSidePanel(
                place.canonicalPlaceId || place.ID,
                place.place,
                this.tourCurrentCounts,
                this.tourCurrentSubPlaces,
                this.getPanelPlaceIds(place)
            );
        },

        ensureTourSceneLayers() {
            if (!this.mapInstance) return;

            if (!this.mapInstance.isStyleLoaded()) {
                this.mapInstance.once('style.load', () => {
                    if (this.tourActive) this.ensureTourSceneLayers();
                });
                return;
            }

            this.ensureTourTerrain();
            this.ensureTourBuildings();
            this.ensureTourGeoJsonSources();
            this.ensureTourOverlayLayers();
            this.updateTourSceneLayers();
        },

        ensureTourTerrain() {
            if (!this.mapInstance) return;

            try {
                if (!this.mapInstance.getSource('place-tour-dem')) {
                    this.mapInstance.addSource('place-tour-dem', {
                        type: 'raster-dem',
                        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                        tileSize: 512,
                        maxzoom: 14,
                    });
                    this.tourSceneSourceIds.push('place-tour-dem');
                }

                this.mapInstance.setTerrain({
                    source: 'place-tour-dem',
                    exaggeration: 1.25,
                });
                this.tourSceneTerrainEnabled = true;
                this.mapInstance.setFog({
                    color: 'rgb(186, 210, 235)',
                    'high-color': 'rgb(36, 92, 223)',
                    'horizon-blend': 0.08,
                    'space-color': 'rgb(4, 6, 16)',
                    'star-intensity': 0.9,
                });
            } catch (error) {
                console.warn('Failed to enable tour terrain:', error);
            }
        },

        ensureTourBuildings() {
            if (!this.mapInstance || this.mapInstance.getLayer('place-tour-3d-buildings')) return;
            if (!this.mapInstance.getSource('composite')) return;

            try {
                this.mapInstance.addLayer({
                    id: 'place-tour-3d-buildings',
                    source: 'composite',
                    'source-layer': 'building',
                    filter: ['==', ['get', 'extrude'], 'true'],
                    type: 'fill-extrusion',
                    minzoom: 14,
                    paint: {
                        'fill-extrusion-color': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            14,
                            '#c6b9a8',
                            16,
                            '#d8d0c5',
                        ],
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'min_height'],
                        'fill-extrusion-opacity': 0.72,
                    },
                });
                this.tourSceneLayerIds.push('place-tour-3d-buildings');
            } catch (error) {
                console.warn('Failed to add tour 3D buildings:', error);
            }
        },

        ensureTourGeoJsonSources() {
            [
                'place-tour-route',
                'place-tour-stops',
                'place-tour-current',
                'place-tour-pulses',
            ].forEach((sourceId) => {
                if (this.mapInstance.getSource(sourceId)) return;
                this.mapInstance.addSource(sourceId, {
                    type: 'geojson',
                    data: { type: 'FeatureCollection', features: [] },
                });
                this.tourSceneSourceIds.push(sourceId);
            });
        },

        ensureTourOverlayLayers() {
            const beforeLayer = this.mapInstance.getLayer('clusters') ? 'clusters' : undefined;
            const layerSpecs = [
                {
                    id: 'place-tour-route-glow',
                    type: 'line',
                    source: 'place-tour-route',
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: {
                        'line-color': '#ffffff',
                        'line-width': 9,
                        'line-opacity': 0.18,
                        'line-blur': 4,
                    },
                },
                {
                    id: 'place-tour-route-line',
                    type: 'line',
                    source: 'place-tour-route',
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: {
                        'line-color': '#f5c451',
                        'line-width': 3,
                        'line-opacity': 0.86,
                        'line-dasharray': [1.5, 1.1],
                    },
                },
                {
                    id: 'place-tour-current-spotlight',
                    type: 'circle',
                    source: 'place-tour-current',
                    paint: {
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            4,
                            18,
                            11,
                            56,
                            16,
                            112,
                        ],
                        'circle-color': '#f5c451',
                        'circle-opacity': 0.10,
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-opacity': 0.28,
                        'circle-stroke-width': 1.5,
                        'circle-pitch-alignment': 'map',
                    },
                },
                {
                    id: 'place-tour-stop-halo',
                    type: 'circle',
                    source: 'place-tour-stops',
                    paint: {
                        'circle-radius': ['case', ['get', 'active'], 13, 7],
                        'circle-color': '#ffffff',
                        'circle-opacity': ['case', ['get', 'active'], 0.34, 0.16],
                        'circle-blur': 0.25,
                        'circle-pitch-alignment': 'map',
                    },
                },
                {
                    id: 'place-tour-stop-core',
                    type: 'circle',
                    source: 'place-tour-stops',
                    paint: {
                        'circle-radius': ['case', ['get', 'active'], 6, 4],
                        'circle-color': ['case', ['get', 'active'], '#f5c451', '#ffffff'],
                        'circle-stroke-color': '#111827',
                        'circle-stroke-width': 1.4,
                        'circle-pitch-alignment': 'map',
                    },
                },
                {
                    id: 'place-tour-event-pulses',
                    type: 'circle',
                    source: 'place-tour-pulses',
                    paint: {
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['get', 'count'],
                            1,
                            6,
                            20,
                            13,
                            80,
                            22,
                        ],
                        'circle-color': [
                            'match',
                            ['get', 'type'],
                            'birth',
                            '#79c7ff',
                            'marriage',
                            '#f5c451',
                            'death',
                            '#ff867a',
                            'burial',
                            '#d5b8ff',
                            'baptism',
                            '#90d98f',
                            '#ffffff',
                        ],
                        'circle-opacity': 0.32,
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-opacity': 0.56,
                        'circle-stroke-width': 1,
                        'circle-pitch-alignment': 'map',
                    },
                },
                {
                    id: 'place-tour-stop-label',
                    type: 'symbol',
                    source: 'place-tour-stops',
                    layout: {
                        'text-field': ['concat', ['get', 'title'], '\n', ['get', 'oldName']],
                        'text-size': ['case', ['get', 'active'], 13, 11],
                        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                        'text-anchor': 'top',
                        'text-offset': [0, 1.2],
                        'text-allow-overlap': false,
                    },
                    paint: {
                        'text-color': '#ffffff',
                        'text-halo-color': '#111827',
                        'text-halo-width': 1.6,
                        'text-opacity': ['case', ['get', 'active'], 1, 0.62],
                    },
                },
            ];

            layerSpecs.forEach((spec) => {
                if (this.mapInstance.getLayer(spec.id)) return;
                this.mapInstance.addLayer(spec, beforeLayer);
                this.tourSceneLayerIds.push(spec.id);
            });
        },

        updateTourSceneLayers() {
            if (!this.mapInstance || !this.tourActive || !this.resolvedTourStops.length) return;

            const stopFeatures = this.resolvedTourStops
                .map((stop, index) => {
                    const center = this.getTourStopCenter(stop);
                    if (!center) return null;
                    return {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: center },
                        properties: {
                            id: stop.id,
                            title: this.$t(stop.titleKey),
                            oldName: stop.oldNameKey ? this.$t(stop.oldNameKey) : '',
                            active: index === this.tourCurrentIndex,
                        },
                    };
                })
                .filter(Boolean);

            const routeCoordinates = stopFeatures.map((feature) => feature.geometry.coordinates);
            const currentCenter = this.getTourStopCenter(this.currentTourStop);

            this.setTourSourceData('place-tour-stops', {
                type: 'FeatureCollection',
                features: stopFeatures,
            });

            this.setTourSourceData('place-tour-route', {
                type: 'FeatureCollection',
                features: routeCoordinates.length > 1
                    ? [{
                        type: 'Feature',
                        geometry: { type: 'LineString', coordinates: routeCoordinates },
                        properties: {},
                    }]
                    : [],
            });

            this.setTourSourceData('place-tour-current', {
                type: 'FeatureCollection',
                features: currentCenter
                    ? [{
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: currentCenter },
                        properties: {},
                    }]
                    : [],
            });

            this.setTourSourceData('place-tour-pulses', {
                type: 'FeatureCollection',
                features: currentCenter ? this.buildTourPulseFeatures(currentCenter) : [],
            });
        },

        setTourSourceData(sourceId, data) {
            const source = this.mapInstance?.getSource(sourceId);
            if (source?.setData) {
                source.setData(data);
            }
        },

        buildTourPulseFeatures(center) {
            const entries = Object.entries(this.tourCurrentCounts || {})
                .filter(([, count]) => Number(count) > 0)
                .slice(0, 6);

            if (!entries.length) return [];

            return entries.map(([type, count], index) => {
                const angle = (Math.PI * 2 * index) / entries.length;
                const offset = 0.006 + (index % 3) * 0.0025;
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [
                            center[0] + Math.cos(angle) * offset,
                            center[1] + Math.sin(angle) * offset,
                        ],
                    },
                    properties: {
                        type: this.normalizeEventType(type),
                        count: Number(count || 0),
                    },
                };
            });
        },

        removeTourSceneLayers() {
            if (!this.mapInstance) return;

            this.tourSceneLayerIds.slice().reverse().forEach((layerId) => {
                if (this.mapInstance.getLayer(layerId)) {
                    this.mapInstance.removeLayer(layerId);
                }
            });
            this.tourSceneLayerIds = [];

            this.tourSceneSourceIds.slice().reverse().forEach((sourceId) => {
                if (sourceId === 'place-tour-dem') return;
                if (this.mapInstance.getSource(sourceId)) {
                    this.mapInstance.removeSource(sourceId);
                }
            });
            this.tourSceneSourceIds = this.tourSceneSourceIds.filter((sourceId) => sourceId === 'place-tour-dem');

            if (this.tourSceneTerrainEnabled) {
                try {
                    this.mapInstance.setTerrain(null);
                } catch (error) {
                    console.warn('Failed to reset tour terrain:', error);
                }
                this.tourSceneTerrainEnabled = false;
            }
        },

        // MAPBOX MAP METHODS
        setMapboxAtmosphere() {
            if (!this.mapInstance) return;

            this.mapInstance.setFog({
                color: 'rgb(186, 210, 235)',
                'high-color': 'rgb(36, 92, 223)',
                'horizon-blend': 0.05,
                'space-color': 'rgb(8, 8, 20)',
                'star-intensity': 1.0
            });
        },

        syncBasemapLabelVisibility() {
            if (!this.mapInstance || this.useSatelliteStyle) return;

            const showBasemapLabels = !this.isSurnameMode;
            try {
                [
                    'showPlaceLabels',
                    'showPointOfInterestLabels',
                    'showRoadLabels',
                    'showTransitLabels',
                ].forEach((property) => {
                    this.mapInstance.setConfigProperty('basemap', property, showBasemapLabels);
                });
            } catch {
                // Standard's basemap import may still be settling during a style change.
            }
        },

        initializeMapbox() {
            if (!this.$refs.mapContainer) return;
            if (!mapboxgl.accessToken) {
                console.warn('VUE_APP_MAPBOX_TOKEN is required to initialize Mapbox.');
                this.loading = false;
                return;
            }

            const mapStyle = this.useSatelliteStyle
                ? 'mapbox://styles/mapbox/satellite-v9'
                : 'mapbox://styles/mapbox/standard';

            this.mapInstance = new mapboxgl.Map({
                container: this.$refs.mapContainer,
                style: mapStyle,
                center: [...this.initialCenter],
                zoom: this.initialZoom,
                pitch: 0,
                bearing: 0,
                projection: this.useGlobeProjection ? 'globe' : 'mercator',
                cooperativeGestures: this.cooperativeGestures,
                attributionControl: false,
                logoPosition: 'bottom-right',
            });
            this.mapInstance.on('zoomend', this.handleSurnameZoomEnd);

            this.mapContainerEl = this.$refs.mapContainer;
            document.addEventListener('fullscreenchange', this.onFullscreenChange);

            this.mapInstance.on('load', () => {
                if (this.useGlobeProjection) {
                    this.setMapboxAtmosphere();
                }

                // Only add controls if they haven't been added yet
                if (!this.mapboxAttributionControl) {
                    this.mapboxAttributionControl = new mapboxgl.AttributionControl({ compact: true });
                    this.mapInstance.addControl(this.mapboxAttributionControl, 'top-right');
                }
                if (!this.mapboxFullscreenControl) {
                    this.mapboxFullscreenControl = new mapboxgl.FullscreenControl();
                    this.mapInstance.addControl(this.mapboxFullscreenControl, 'bottom-right');
                }
                if (!this.mapboxNavigationControl) {
                    this.mapboxNavigationControl = new mapboxgl.NavigationControl();
                    this.mapInstance.addControl(this.mapboxNavigationControl, 'bottom-right');
                }

                this.updateMapboxMarkers();

                // Setup rotation listeners
                this.setupRotationListeners();

                if (this.forceGlobeView || this.mapBoundsPlaces.length === 0) {
                    // Already at the correct world view from initialization
                } else {
                    // Play cinematic intro
                    this.playCinematicIntro();

                    setTimeout(() => {
                        if (!this.hasPlayedIntro) {
                            this.adjustMapBounds(this.mapBoundsPlaces);
                        }
                    }, 200);
                }
            });

            // Note: style.load handlers are managed explicitly in watchers/methods
            // This persistent listener is removed to avoid race conditions
        },

        updateMapboxMarkers() {
            if (!this.mapInstance) return;

            this.syncBasemapLabelVisibility();

            if (this.mapInstance.getLayer('surname-labels')) {
                this.mapInstance.off('click', 'surname-labels', this.handleSurnameLabelClick);
                this.mapInstance.off('mouseenter', 'surname-labels', this.handleSurnameLabelMouseEnter);
                this.mapInstance.off('mousemove', 'surname-labels', this.handleSurnameLabelMouseMove);
                this.mapInstance.off('mouseleave', 'surname-labels', this.handleSurnameLabelMouseLeave);
            }

            const mapboxLayerIds = ['heatmap-layer', 'place-areas-fill', 'place-areas-sea-fill', 'place-areas-outline', 'place-areas-sea-outline', 'mapbox-country-areas-fill', 'mapbox-country-areas-outline', 'clusters', 'cluster-count', 'grouped-point-halo', 'unclustered-point', 'place-labels', 'place-circles', 'person-arcs', 'person-arcs-glow', 'person-arcs-shadow', 'surname-profile-regions', 'surname-labels'];
            mapboxLayerIds.forEach((layerId) => {
                if (this.mapInstance.getLayer(layerId)) {
                    this.mapInstance.removeLayer(layerId);
                }
            });

            if (this.mapInstance.getSource('place-areas')) {
                this.mapInstance.removeSource('place-areas');
            }
            this.hoveredPlaceAreaFeatureId = null;
            if (this.mapInstance.getSource('mapbox-country-boundaries')) {
                this.mapInstance.removeSource('mapbox-country-boundaries');
            }
            this.hoveredMapboxCountryFeatureId = null;

            if (this.mapInstance.getSource('places')) {
                this.mapInstance.removeSource('places');
            }
            if (this.mapInstance.getSource('person-arcs')) {
                this.mapInstance.removeSource('person-arcs');
            }
            if (this.mapInstance.getSource('surname-profile-regions')) {
                this.mapInstance.removeSource('surname-profile-regions');
            }
            if (this.mapInstance.getSource('surname-labels')) {
                this.mapInstance.removeSource('surname-labels');
            }

            this.mapboxMarkers.forEach(marker => marker.remove());
            this.mapboxMarkers = [];

            if (this.isSurnameMode) {
                this.updateSurnameMapLayers();
                return;
            }

            const places = this.markerPlaces;
            const areaPlaces = this.areaPlaces;
            if (places.length === 0 && areaPlaces.length === 0) return;

            const localAreaPlaces = areaPlaces.filter((place) => (
                place.areaFeature?.properties?.sourceType !== 'mapbox-country'
            ));
            const mapboxCountryAreaPlaces = areaPlaces.filter((place) => (
                place.areaFeature?.properties?.sourceType === 'mapbox-country' &&
                place.areaFeature?.properties?.mapboxCountryIso3
            ));
            const mapboxCountryIso3Values = Array.from(new Set(
                mapboxCountryAreaPlaces.map((place) => place.areaFeature.properties.mapboxCountryIso3)
            ));
            const mapboxCountryFadingIso3Values = Array.from(new Set(
                mapboxCountryAreaPlaces
                    .filter((place) => this.shouldAreaFeatureFadeOnZoom(place.areaFeature))
                    .map((place) => place.areaFeature.properties.mapboxCountryIso3)
            ));

            const areaGeojson = {
                type: 'FeatureCollection',
                features: localAreaPlaces.map((place) => {
                    const sourceFeature = place.areaFeature;
                    const areaFeatureId = `place-area-${String(place.ID || sourceFeature.properties.areaId)}`;

                    return {
                        type: 'Feature',
                        id: areaFeatureId,
                        geometry: sourceFeature.geometry,
                        properties: {
                            ...sourceFeature.properties,
                            areaFeatureId,
                            fadesOnZoom: this.shouldAreaFeatureFadeOnZoom(sourceFeature),
                            placeID: place.ID,
                            place: place.place,
                        },
                    };
                }),
            };

            const geojson = {
                type: 'FeatureCollection',
                features: places.map(place => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [place.longitude, place.latitude]
                    },
                    properties: {
                        id: place.ID,
                        place: place.place,
                        placeID: place.ID,
                        isGrouped: this.isGroupedPlaceMarker(place),
                        groupMemberCount: Array.isArray(place.memberPlaces) ? place.memberPlaces.length : 0,
                    }
                }))
            };

            if (mapboxCountryIso3Values.length > 0) {
                this.mapInstance.addSource('mapbox-country-boundaries', {
                    type: 'vector',
                    url: 'mapbox://mapbox.country-boundaries-v1',
                    promoteId: 'iso_3166_1_alpha_3',
                });

                const mapboxCountryFilter = [
                    'all',
                    ['==', ['get', 'disputed'], 'false'],
                    [
                        'any',
                        ['==', ['get', 'worldview'], 'all'],
                        ['in', 'US', ['get', 'worldview']]
                    ],
                    ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', mapboxCountryIso3Values]],
                ];
                const mapboxCountryFadeCondition = [
                    'in',
                    ['get', 'iso_3166_1_alpha_3'],
                    ['literal', mapboxCountryFadingIso3Values],
                ];

                this.mapInstance.addLayer({
                    id: 'mapbox-country-areas-fill',
                    type: 'fill',
                    source: 'mapbox-country-boundaries',
                    'source-layer': 'country_boundaries',
                    filter: mapboxCountryFilter,
                    paint: {
                        'fill-color': '#c62828',
                        'fill-opacity': this.getAreaFillOpacityExpression(mapboxCountryFadeCondition),
                    },
                });

                this.mapInstance.addLayer({
                    id: 'mapbox-country-areas-outline',
                    type: 'line',
                    source: 'mapbox-country-boundaries',
                    'source-layer': 'country_boundaries',
                    filter: mapboxCountryFilter,
                    paint: {
                        'line-color': '#c62828',
                        'line-opacity': this.getAreaLineOpacityExpression(mapboxCountryFadeCondition),
                        'line-width': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            3,
                            1.5
                        ],
                    },
                });
            }

            if (areaGeojson.features.length > 0) {
                const landAreaFilter = ['!', ['==', ['get', 'areaKind'], 'sea']];
                const seaAreaFilter = ['==', ['get', 'areaKind'], 'sea'];

                this.mapInstance.addSource('place-areas', {
                    type: 'geojson',
                    data: areaGeojson,
                    promoteId: 'areaFeatureId',
                });

                this.mapInstance.addLayer({
                    id: 'place-areas-fill',
                    type: 'fill',
                    source: 'place-areas',
                    filter: landAreaFilter,
                    paint: {
                        'fill-color': '#c62828',
                        'fill-opacity': this.getAreaFillOpacityExpression(),
                    },
                });

                this.mapInstance.addLayer({
                    id: 'place-areas-sea-fill',
                    type: 'fill',
                    source: 'place-areas',
                    filter: seaAreaFilter,
                    paint: {
                        'fill-color': '#c62828',
                        'fill-opacity': this.getAreaFillOpacityExpression(),
                    },
                });

                this.mapInstance.addLayer({
                    id: 'place-areas-outline',
                    type: 'line',
                    source: 'place-areas',
                    filter: landAreaFilter,
                    paint: {
                        'line-color': '#c62828',
                        'line-opacity': this.getAreaLineOpacityExpression(),
                        'line-width': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            3,
                            1.5
                        ],
                    },
                });

                this.mapInstance.addLayer({
                    id: 'place-areas-sea-outline',
                    type: 'line',
                    source: 'place-areas',
                    filter: seaAreaFilter,
                    paint: {
                        'line-color': '#c62828',
                        'line-opacity': this.getAreaLineOpacityExpression(),
                        'line-width': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            3,
                            1.5
                        ],
                        'line-dasharray': [2, 1.5],
                    },
                });
            }

            // Add source with clustering enabled
            this.mapInstance.addSource('places', {
                type: 'geojson',
                data: geojson,
                promoteId: 'placeID',
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 50
            });

            // Cluster circles layer
            this.mapInstance.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'places',
                filter: ['has', 'point_count'],
                maxzoom: 15,
                paint: {
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#c62828',
                        5, '#ad2323',
                        10, '#8e1b1b'
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        5, 28,
                        10, 35
                    ],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff',
                    'circle-opacity': 0.8,
                    'circle-pitch-alignment': 'map'
                }
            });

            // Cluster count labels
            this.mapInstance.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'places',
                filter: ['has', 'point_count'],
                maxzoom: 15,
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['DIN Offc Pro Medium'],
                    'text-size': 12
                },
                paint: {
                    'text-color': '#fff',
                    'text-halo-color': '#333',
                    'text-halo-width': 1
                }
            });

            // Subtle halo for curated grouped places.
            this.mapInstance.addLayer({
                id: 'grouped-point-halo',
                type: 'circle',
                source: 'places',
                filter: [
                    'all',
                    ['!', ['has', 'point_count']],
                    ['==', ['get', 'isGrouped'], true]
                ],
                paint: {
                    'circle-radius': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        20,
                        18
                    ],
                    'circle-color': '#fff7f2',
                    'circle-opacity': 0.92,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': 'rgba(198, 40, 40, 0.42)',
                    'circle-pitch-alignment': 'map'
                }
            });

            // Individual unclustered points layer
            this.mapInstance.addLayer({
                id: 'unclustered-point',
                type: 'circle',
                source: 'places',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-radius': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        [
                            'case',
                            ['==', ['get', 'isGrouped'], true],
                            15,
                            14
                        ],
                        [
                            'case',
                            ['==', ['get', 'isGrouped'], true],
                            13,
                            12
                        ]
                    ],
                    'circle-color': '#c62828',
                    'circle-opacity': 0.9,
                    'circle-stroke-width': [
                        'case',
                        ['==', ['get', 'isGrouped'], true],
                        3,
                        2
                    ],
                    'circle-stroke-color': [
                        'case',
                        ['==', ['get', 'isGrouped'], true],
                        '#fff7f2',
                        '#fff'
                    ],
                    'circle-pitch-alignment': 'map'
                }
            });

            // Individual point labels
            this.mapInstance.addLayer({
                id: 'place-labels',
                type: 'symbol',
                source: 'places',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'text-field': '',
                    'text-size': 12,
                    'text-offset': [0, 1.5],
                    'text-anchor': 'top'
                },
                paint: {
                    'text-color': '#202',
                    'text-halo-color': '#fff',
                    'text-halo-width': 1
                }
            });

            // Draw chronological arcs for person's life events (if filtering by person)
            if (this.personIDFilter && this.personChronologicalSegments && this.personChronologicalSegments.length > 0) {
                // Remove existing arc layers
                const arcLayerIds = ['person-arcs', 'person-arcs-glow', 'person-arcs-shadow'];
                arcLayerIds.forEach(id => {
                    if (this.mapInstance.getLayer(id)) {
                        this.mapInstance.removeLayer(id);
                    }
                });
                if (this.mapInstance.getSource('person-arcs')) {
                    this.mapInstance.removeSource('person-arcs');
                }

                // Create GeoJSON with interpolated great circle arcs for 3D effect
                const arcFeatures = this.personChronologicalSegments.map(segment => {
                    const coords = this.interpolateGreatCircle(
                        [segment.from[1], segment.from[0]],
                        [segment.to[1], segment.to[0]],
                        20 // number of intermediate points
                    );
                    return {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: coords
                        },
                        properties: {}
                    };
                });

                // Add arc source with lineMetrics for gradient support
                this.mapInstance.addSource('person-arcs', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: arcFeatures
                    },
                    lineMetrics: true
                });

                // Shadow layer (darkest, appears to be "on the globe")
                this.mapInstance.addLayer({
                    id: 'person-arcs-shadow',
                    type: 'line',
                    source: 'person-arcs',
                    paint: {
                        'line-color': '#4a0a0a',
                        'line-width': 5,
                        'line-opacity': 0.3,
                        'line-blur': 4
                    }
                });

                // Outer glow layer (creates depth)
                this.mapInstance.addLayer({
                    id: 'person-arcs-glow',
                    type: 'line',
                    source: 'person-arcs',
                    paint: {
                        'line-gradient': [
                            'interpolate',
                            ['linear'],
                            ['line-progress'],
                            0, 'rgba(198, 40, 40, 0.1)',
                            0.5, 'rgba(198, 40, 40, 0.4)', // Peak at midpoint
                            1, 'rgba(198, 40, 40, 0.1)'
                        ],
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['line-progress'],
                            0, 3,
                            0.5, 8, // Thickest at midpoint (simulating arc peak)
                            1, 3
                        ],
                        'line-blur': 2
                    }
                });

                // Main arc layer with gradient (brightest in middle = "higher")
                this.mapInstance.addLayer({
                    id: 'person-arcs',
                    type: 'line',
                    source: 'person-arcs',
                    paint: {
                        'line-gradient': [
                            'interpolate',
                            ['linear'],
                            ['line-progress'],
                            0, 'rgba(198, 40, 40, 0.4)',
                            0.5, 'rgba(244, 67, 54, 1)', // Brightest at peak
                            1, 'rgba(198, 40, 40, 0.4)'
                        ],
                        'line-width': [
                            'interpolate',
                            ['linear'],
                            ['line-progress'],
                            0, 1.5,
                            0.5, 3, // Thicker at peak
                            1, 1.5
                        ]
                    }
                });
            }

            // Create a map for individual place data
            const placeMap = new Map(places.map(place => [place.ID, place]));

            if (areaGeojson.features.length > 0) {
                ['place-areas-fill', 'place-areas-sea-fill'].forEach((layerId) => {
                    this.mapInstance.off('click', layerId, this.handlePlaceAreaClick);
                    this.mapInstance.off('mousemove', layerId, this.handlePlaceAreaMouseMove);
                    this.mapInstance.off('mouseleave', layerId, this.handlePlaceAreaMouseLeave);
                    this.mapInstance.on('click', layerId, this.handlePlaceAreaClick);
                    this.mapInstance.on('mousemove', layerId, this.handlePlaceAreaMouseMove);
                    this.mapInstance.on('mouseleave', layerId, this.handlePlaceAreaMouseLeave);
                });
            }

            if (mapboxCountryIso3Values.length > 0) {
                this.mapInstance.off('click', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaClick);
                this.mapInstance.off('mousemove', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaMouseMove);
                this.mapInstance.off('mouseleave', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaMouseLeave);
                this.mapInstance.on('click', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaClick);
                this.mapInstance.on('mousemove', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaMouseMove);
                this.mapInstance.on('mouseleave', 'mapbox-country-areas-fill', this.handleMapboxCountryAreaMouseLeave);
            }

            // Add click handlers for cluster circles and labels
            this.mapInstance.off('click', 'clusters', this.handleClusterClick);
            this.mapInstance.off('click', 'cluster-count', this.handleClusterClick);
            this.mapInstance.on('click', 'clusters', this.handleClusterClick);
            this.mapInstance.on('click', 'cluster-count', this.handleClusterClick);

            // Add click handler for individual unclustered points - open panel directly
            this.mapInstance.on('click', 'unclustered-point', async (e) => {
                const placeID = e.features[0].properties.placeID;
                const place = placeMap.get(placeID);

                if (place) {
                    await this.openPlacePanel(place);
                }
            });

            // Hover effects
            this.mapInstance.off('mouseenter', 'clusters', this.setClusterPointerCursor);
            this.mapInstance.off('mouseenter', 'cluster-count', this.setClusterPointerCursor);
            this.mapInstance.off('mouseleave', 'clusters', this.clearMapCursor);
            this.mapInstance.off('mouseleave', 'cluster-count', this.clearMapCursor);
            this.mapInstance.on('mouseenter', 'clusters', this.setClusterPointerCursor);
            this.mapInstance.on('mouseenter', 'cluster-count', this.setClusterPointerCursor);
            this.mapInstance.on('mouseleave', 'clusters', this.clearMapCursor);
            this.mapInstance.on('mouseleave', 'cluster-count', this.clearMapCursor);

            this.mapInstance.on('mousemove', 'unclustered-point', (e) => {
                this.mapInstance.getCanvas().style.cursor = 'pointer';
                if (!e.features || e.features.length === 0) return;

                const featureId = e.features[0].id;
                if (featureId === undefined || featureId === null) return;

                if (this.hoveredUnclusteredFeatureId !== null && this.hoveredUnclusteredFeatureId !== featureId) {
                    this.mapInstance.setFeatureState(
                        { source: 'places', id: this.hoveredUnclusteredFeatureId },
                        { hover: false }
                    );
                }

                this.hoveredUnclusteredFeatureId = featureId;
                this.mapInstance.setFeatureState(
                    { source: 'places', id: featureId },
                    { hover: true }
                );
            });
            this.mapInstance.on('mouseleave', 'unclustered-point', () => {
                this.mapInstance.getCanvas().style.cursor = '';
                if (this.hoveredUnclusteredFeatureId !== null) {
                    this.mapInstance.setFeatureState(
                        { source: 'places', id: this.hoveredUnclusteredFeatureId },
                        { hover: false }
                    );
                    this.hoveredUnclusteredFeatureId = null;
                }
            });
        },

        resetFilters() {
            this.selectedType = "";
        },

        async resetAllFilters() {
            // Reset all filter states
            this.selectedType = "";
            this.onlyCorePeople = false;
            this.selectedCentury = "";
            this.yearFrom = null;
            this.yearTo = null;
            this.personIDFilter = "";
            this.personSearchQuery = "";
            this.personIDErrorMsg = "";
            this.personEventLocations = [];
            this.personChronologicalSegments = [];
            this.personSuggestions = [];
            this.showPersonSuggestions = false;
            this.activeRegion = null;
            this.showDNAResults = false;
            this.selectedHaplogroupPersonId = null;
            this.selectedHaplogroupIds = [];

            // Reload places with no filters
            await this.loadPlaces();

            // Update markers on map views
            if (this.useGlobeView && this.mapInstance) {
                await this.$nextTick();
                if (this.mapInstance.isStyleLoaded()) {
                    this.updateMapboxMarkers();
                    this.recenterAfterData();
                } else {
                    this.mapInstance.once('style.load', () => {
                        this.updateMapboxMarkers();
                        this.recenterAfterData();
                    });
                }
            }
        },

        setRegionView(region) {
            if (this.useGlobeView && this.mapInstance) {
                const cfg = this.regionViews[region];
                if (!cfg) return;
                this.mapInstance.flyTo({
                    center: [cfg.center[0], cfg.center[1]],
                    zoom: cfg.zoom,
                    duration: 1000
                });
            }
        },

        recenterAfterData() {
            if (this.tourActive || this.isSurnameMode) return;

            if (this.activeRegion) {
                this.setRegionView(this.activeRegion);
            } else {
                this.adjustMapBounds(this.mapBoundsPlaces);
            }
        },

        onCenturyChange() {
            if (!this.selectedCentury) {
                this.resetYears();
                return;
            }

            const period = this.periods.find(p => p.id === this.selectedCentury);
            if (!period) return;

            this.yearFrom = period.fromYear;
            this.yearTo = period.toYear;

            if (period.region) {
                this.activeRegion = period.region;
                this.setRegionView(period.region);
            } else {
                this.activeRegion = null;
            }
        },

        resetYears() {
            this.yearFrom = null;
            this.yearTo = null;
            this.selectedCentury = '';
            this.activeRegion = null;
        },

        resetCentury() {
            this.selectedCentury = "";
            this.resetYears();
        },

        onPersonSearchInput() {
            const query = this.personSearchQuery;

            if (!query || query.length < 2) {
                this.personSuggestions = [];
                this.showPersonSuggestions = false;
                return;
            }

            // Clear previous timeout
            if (this.suggestionSearchTimeout) {
                clearTimeout(this.suggestionSearchTimeout);
            }

            // Debounce search to avoid too many API calls
            this.suggestionSearchTimeout = setTimeout(async () => {
                await this.fetchPersonSuggestions(query);
            }, 300);
        },

        async fetchPersonSuggestions(query) {
            try {
                const result = await searchPeopleSuggestions(query);
                this.personSuggestions = result.suggestions || [];
                this.showPersonSuggestions = this.personSuggestions.length > 0;
            } catch (error) {
                console.error('Error fetching person suggestions:', error);
                this.personSuggestions = [];
                this.showPersonSuggestions = false;
            }
        },

        formatPersonSuggestion(person) {
            const name = `${person.firstname} ${person.lastname}`.trim();
            const birthYear = person.birthdatetr ? new Date(person.birthdatetr).getFullYear() : '?';
            const deathYear = person.deathdatetr ? new Date(person.deathdatetr).getFullYear() : (person.living ? null : '?');

            if (deathYear) {
                return `${name} (${person.personID}) • ${birthYear}–${deathYear}`;
            } else if (deathYear === null) {
                return `${name} (${person.personID}) • b. ${birthYear}`;
            } else {
                return `${name} (${person.personID}) • ${birthYear}–`;
            }
        },

        formatPersonDates(person) {
            const birthYear = person?.birthdatetr ? new Date(person.birthdatetr).getFullYear() : null;
            const deathYear = person?.deathdatetr ? new Date(person.deathdatetr).getFullYear() : null;

            if (birthYear && deathYear) return `${birthYear}-${deathYear}`;
            if (birthYear && person?.living) return `${this.$t('placeMap.bornAbbr')} ${birthYear}`;
            if (birthYear) return `${birthYear}-?`;
            if (deathYear) return `?-${deathYear}`;
            return person?.personID || '';
        },

        getPersonEventYear(value) {
            if (!value) return null;

            const yearMatch = String(value).match(/\b\d{4}\b/);
            const year = yearMatch ? Number(yearMatch[0]) : new Date(value).getFullYear();
            return Number.isFinite(year) && year > 0 ? year : null;
        },

        hasCompleteLifespanYears(person) {
            return Boolean(
                this.getPersonEventYear(person?.birthdatetr) &&
                this.getPersonEventYear(person?.deathdatetr)
            );
        },

        async selectPersonFromSuggestion(person) {
            if (this.isSurnameMode) {
                await this.setDisplayMode('places');
            }
            if (this.sidePanelVisible) {
                this.closeSidePanel();
            }

            this.personIDFilter = person.personID;
            this.personSearchQuery = this.formatPersonSuggestion(person);
            this.placeSearchQuery = this.personSearchQuery;
            this.showPersonSuggestions = false;
            this.personSuggestions = [];
            this.placeSuggestions = [];
            this.showPlaceSuggestions = false;
            // Automatically apply the filter
            await this.applyPersonFilter();
        },

        closePersonSuggestions() {
            this.showPersonSuggestions = false;
        },

        async onMapSearchFocus() {
            this.placeSearchFocused = true;

            if (this.isMapSearchEmpty) {
                this.showPlaceSuggestions = true;
                await this.loadSearchPresets();
            }
        },

        onPlaceSearchInput() {
            const query = this.placeSearchQuery;

            if (!query || query.trim().length < 2) {
                this.placeSuggestions = [];
                this.personSuggestions = [];
                this.showPlaceSuggestions = true;
                this.loadSearchPresets();
                return;
            }

            if (this.placeSuggestionSearchTimeout) {
                clearTimeout(this.placeSuggestionSearchTimeout);
            }

            this.placeSuggestionSearchTimeout = setTimeout(async () => {
                await this.updateSearchSuggestions(query);
            }, 250);
        },

        onPlaceSearchBlur() {
            setTimeout(() => {
                if (!document.hasFocus()) {
                    this.placeSearchFocused = true;
                    return;
                }

                this.placeSearchFocused = false;
                this.closePlaceSuggestions();
            }, 200);
        },

        async updateSearchSuggestions(query) {
            const normalizedQuery = String(query || '').trim().toLowerCase();
            if (!normalizedQuery) {
                this.placeSuggestions = [];
                this.personSuggestions = [];
                this.showPlaceSuggestions = false;
                return;
            }

            this.placeSuggestions = this.getPlaceSuggestionsForQuery(normalizedQuery);
            await this.fetchPersonSuggestions(query);

            this.showPlaceSuggestions = this.placeSuggestions.length > 0 || this.personSuggestions.length > 0;
        },

        async loadSearchPresets() {
            if (this.searchPresetsLoaded || this.searchPresetsLoading) return;

            this.searchPresetsLoading = true;
            try {
                const [stats, people] = await Promise.all([
                    fetchPeopleDashboardStats().catch((error) => {
                        console.warn('Failed to load place search preset stats:', error);
                        return null;
                    }),
                    this.fetchRandomPeoplePresets(),
                ]);

                this.placePresetStats = Array.isArray(stats?.topPlaces) ? stats.topPlaces : [];
                this.presetPeople = people;
                this.searchPresetsLoaded = true;
            } finally {
                this.searchPresetsLoading = false;
            }
        },

        async fetchRandomPeoplePresets() {
            const seeds = this.shuffleArray(['th', 'ja', 'de', 'ma', 'an', 'jo', 'wi', 'he', 'el', 'la', 'ro'])
                .slice(0, 7);
            const results = await Promise.all(seeds.map((seed) => searchPeopleSuggestions(seed)));
            const people = [];
            const seen = new Set();

            results
                .flatMap((result) => Array.isArray(result?.suggestions) ? result.suggestions : [])
                .forEach((person) => {
                    if (!person?.personID || seen.has(person.personID)) return;
                    if (!this.hasCompleteLifespanYears(person)) return;
                    seen.add(person.personID);
                    people.push(person);
                });

            return this.shuffleArray(people).slice(0, 5);
        },

        shuffleArray(items) {
            return [...(items || [])].sort(() => Math.random() - 0.5);
        },

        closePlaceSuggestions() {
            this.showPlaceSuggestions = false;
            this.showPersonSuggestions = false;
        },

        async clearPlaceSearch() {
            const clearsSelectedPerson = this.personIDFilter &&
                this.personSearchQuery &&
                this.placeSearchQuery === this.personSearchQuery;

            this.placeSearchQuery = '';
            this.placeSuggestions = [];
            this.personSuggestions = [];
            this.showPlaceSuggestions = false;

            if (clearsSelectedPerson) {
                await this.clearPersonFilter();
            }
        },

        selectFirstSearchSuggestion() {
            if (this.placeSuggestions.length) {
                this.selectPlaceFromSuggestion(this.placeSuggestions[0]);
                return;
            }

            if (this.personSuggestions.length) {
                this.selectPersonFromSuggestion(this.personSuggestions[0]);
            }
        },

        selectFirstPlaceSuggestion() {
            if (!this.placeSuggestions.length) {
                this.selectFirstSearchSuggestion();
                return;
            }
            this.selectPlaceFromSuggestion(this.placeSuggestions[0]);
        },

        async selectPlaceFromSuggestion(place) {
            if (!place) return;

            this.placeSearchQuery = place.place || '';
            this.placeSuggestions = [];
            this.personSuggestions = [];
            this.showPlaceSuggestions = false;

            if (!this.useGlobeView || !this.mapInstance) return;
            if (isNaN(Number(place.longitude)) || isNaN(Number(place.latitude))) return;

            this.onUserInteraction();

            const focusZoom = Number.isFinite(Number(place.zoom)) ? Math.max(Number(place.zoom), 5) : 7;
            const areaFeature = this.resolvePlaceAreaFeature(place);
            const areaBounds = this.getGeometryBounds(areaFeature?.geometry);
            if (!this.isSurnameMode) {
                await this.openPlacePanel(place);
            }

            const flyToPlace = () => {
                if (areaBounds) {
                    this.mapInstance.fitBounds(areaBounds, {
                        padding: {
                            top: 80,
                            bottom: 80,
                            left: 80,
                            right: 80
                        },
                        duration: 1200,
                    });
                    return;
                }

                this.mapInstance.flyTo({
                    center: [Number(place.longitude), Number(place.latitude)],
                    zoom: focusZoom,
                    duration: 1200,
                    essential: true,
                });
            };

            if (this.mapInstance.isStyleLoaded()) {
                flyToPlace();
            } else {
                this.mapInstance.once('load', flyToPlace);
            }
        },

        async applyPersonFilter() {
            const filtered = this.personIDFilter.trim();
            if (!filtered) return;

            if (this.sidePanelVisible) {
                this.closeSidePanel();
            }

            // Validation: should match pattern like I1, I123, etc.
            if (!/^I\d+$/i.test(filtered)) {
                this.personIDErrorMsg = this.$t('placeMap.invalidFormat');
                return;
            }

            this.personIDErrorMsg = "";
            await this.loadPlaces();

            // Fetch event locations for the person to draw arcs
            try {
                const eventLocations = await getEventLocations(filtered);
                const allPersonEvents = [
                    ...eventLocations.personEventLocations,
                    ...eventLocations.eventLocations,
                    ...eventLocations.childrenBirthLocations,
                    ...eventLocations.childrenBaptismLocations,
                ];

                const eventsWithIndex = allPersonEvents.map((event, index) => ({
                    ...event,
                    originalIndex: index
                }));

                const validEvents = eventsWithIndex.filter(
                    event => !isNaN(event.latitude) && !isNaN(event.longitude)
                );

                this.personEventLocations = validEvents;
                this.buildPersonChronologicalSegments();

                // Extract unique places from event locations to ensure all markers show
                const eventPlaces = new Map();
                validEvents.forEach(event => {
                    const key = `${event.latitude},${event.longitude}`;
                    if (!eventPlaces.has(key)) {
                        eventPlaces.set(key, {
                            ID: event.placeID || event.ID || `place_${key}`,
                            place: event.place,
                            latitude: parseFloat(event.latitude),
                            longitude: parseFloat(event.longitude),
                            zoom: 10
                        });
                    }
                });

                // Merge event places with existing places (avoid duplicates)
                const existingPlaceKeys = new Set(
                    this.places.map(p => `${p.latitude},${p.longitude}`)
                );

                eventPlaces.forEach((place, key) => {
                    if (!existingPlaceKeys.has(key)) {
                        this.places.push(place);
                    }
                });
            } catch (error) {
                console.error('Failed to fetch event locations:', error);
                this.personEventLocations = [];
                this.personChronologicalSegments = [];
            }

            // Stop any ongoing idle rotation so flyTo/fitBounds can execute
            this.onUserInteraction();

            // Update markers on map views and zoom to person's places
            if (this.useGlobeView && this.mapInstance) {
                await this.$nextTick();
                if (this.mapInstance.isStyleLoaded()) {
                    this.updateMapboxMarkers();
                    this.adjustMapBounds(this.mapBoundsPlaces);
                } else {
                    // Wait for style to load before updating markers
                    this.mapInstance.once('style.load', () => {
                        this.updateMapboxMarkers();
                        this.adjustMapBounds(this.mapBoundsPlaces);
                    });
                }
            }
        },

        async clearPersonFilter() {
            if (this.placeSearchQuery === this.personSearchQuery) {
                this.placeSearchQuery = "";
            }

            this.personIDFilter = "";
            this.personSearchQuery = "";
            this.personIDErrorMsg = "";
            this.personEventLocations = [];
            this.personChronologicalSegments = [];
            this.personSuggestions = [];
            this.showPersonSuggestions = false;
            await this.loadPlaces();

            // Update markers on map views
            if (this.useGlobeView && this.mapInstance) {
                await this.$nextTick();
                if (this.mapInstance.isStyleLoaded()) {
                    this.updateMapboxMarkers();
                    this.recenterAfterData();
                } else {
                    // Wait for style to load before updating markers
                    this.mapInstance.once('style.load', () => {
                        this.updateMapboxMarkers();
                        this.recenterAfterData();
                    });
                }
            }
        },

        openSidePanel(placeId, placeName, counts = null, subPlaces = [], placeIds = []) {
            this.selectedPlaceId = placeId;
            this.selectedPlaceIds = Array.from(new Set(
                (placeIds || []).map((id) => String(id || '').trim()).filter(Boolean)
            ));
            this.selectedPlaceName = placeName;
            this.selectedPlaceCounts = counts;
            this.selectedPlaceSubPlaces = subPlaces;
            this.selectedPlaceCountsLoading = false;
            this.selectedPlaceSubPlacesLoading = false;
            this.sidePanelVisible = true;
        },

        closeSidePanel() {
            this.sidePanelVisible = false;
            this.selectedPlacePanelRequestId += 1;
            // Clear selected place after animation completes
            setTimeout(() => {
                this.selectedPlaceId = null;
                this.selectedPlaceIds = [];
                this.selectedPlaceName = '';
                this.selectedPlaceCounts = null;
                this.selectedPlaceSubPlaces = [];
                this.selectedPlaceCountsLoading = false;
                this.selectedPlaceSubPlacesLoading = false;
            }, 300);
        },

        closeBanner() {
            // This method is called when user closes the banner
            // The banner will hide but filters remain active
            // User can toggle filters manually to show banner again
        },

        // ==================== DNA ETHNICITY METHODS ====================

        initializeDNAData() {
            // Load test subjects
            this.dnaTestSubjects = dnaData.testSubjects.sort((a, b) => a.order - b.order);

            // Load companies
            const companies = [];
            for (const [id, company] of Object.entries(dnaData.companies)) {
                companies.push({ id, name: company.name, color: company.color });
            }
            this.dnaCompanies = companies;

            // Set default selections to first available subject/company with data
            const subjectsWithData = this.availableDNASubjects;

            if (subjectsWithData.length > 0) {
                this.selectedDNASubjectId = subjectsWithData[0].id;

                // Find first company with data for this subject
                const companiesForSubject = this.dnaCompanies.filter(company => {
                    return dnaData.results.some(result =>
                        result.testSubjectId === this.selectedDNASubjectId &&
                        result.company === company.id
                    );
                });

                if (companiesForSubject.length > 0) {
                    this.selectedDNACompanyId = companiesForSubject[0].id;
                }
            }

            // Load real country borders from existing outlines dataset.
            this.loadDNACountryBorders();
        },

        async loadDNACountryBorders() {
            try {
                const response = await fetch('/data/outlines.json');
                if (!response.ok) return;

                const geojson = await response.json();
                const nextMap = {};

                (geojson.features || []).forEach((feature) => {
                    const countryName = feature?.properties?.name;
                    const geometry = feature?.geometry;
                    if (!countryName || !geometry) return;
                    nextMap[String(countryName).toLowerCase()] = geometry;
                });

                this.dnaCountryGeometryByName = nextMap;

                if (this.showDNAResults && this.mapInstance) {
                    this.loadDNALayers();
                }
            } catch (error) {
                console.warn('Failed to load real DNA country borders:', error);
            }
        },

        resolveDNACountryGeometry(country) {
            if (!country) return null;

            const countryName = String(country.name || '').toLowerCase();
            const realBorder = this.dnaCountryGeometryByName[countryName];
            if (realBorder) return realBorder;

            // Fallback to legacy rectangle geometry when no border match is found.
            return country.geometry || null;
        },

        getDNACountryByRef(countryRef) {
            if (!countryRef) return null;

            const ref = String(countryRef).trim();
            if (!ref) return null;

            const byCode = dnaData.countries[ref];
            if (byCode) return byCode;

            const lowerRef = ref.toLowerCase();
            const byNameEntry = Object.entries(dnaData.countries).find(([, country]) => {
                return String(country?.name || '').toLowerCase() === lowerRef;
            });

            return byNameEntry ? byNameEntry[1] : null;
        },

        buildDnaFeaturesFromRegions(result, companyName) {
            const regions = Array.isArray(result?.regions) ? result.regions : [];
            if (!regions.length) return [];

            const features = [];

            regions.forEach((region, regionIndex) => {
                const label = region?.label || region?.name || `Region ${regionIndex + 1}`;
                const percentage = this.normalizeDnaPercentage(region?.percentage);
                if (percentage <= 0) return;

                const countries = Array.isArray(region?.countries) ? region.countries : [];
                countries.forEach((countryRef) => {
                    const country = this.getDNACountryByRef(countryRef);
                    if (!country) return;

                    const geometry = this.resolveDNACountryGeometry(country);
                    if (!geometry) return;

                    features.push({
                        type: 'Feature',
                        properties: {
                            groupId: region?.id || `${result.id}-region-${regionIndex}`,
                            groupLabel: label,
                            percentage,
                            company: companyName,
                            testDate: result.testDate,
                            resultId: result.id,
                            countryName: country.name || ''
                        },
                        geometry
                    });
                });
            });

            return features;
        },

        buildDnaFeaturesFromEthnicityGroups(result, companyName) {
            const groups = result?.ethnicityGroups || {};
            const features = [];

            Object.entries(groups).forEach(([groupId, percentage]) => {
                const group = dnaData.ethnicityGroups[groupId];
                if (!group) return;

                const normalizedPercentage = this.normalizeDnaPercentage(percentage);
                if (normalizedPercentage <= 0) return;

                (group.countries || []).forEach((countryCode) => {
                    const country = dnaData.countries[countryCode];
                    if (!country) return;

                    const geometry = this.resolveDNACountryGeometry(country);
                    if (!geometry) return;

                    features.push({
                        type: 'Feature',
                        properties: {
                            groupId,
                            groupLabel: group.label,
                            percentage: normalizedPercentage,
                            company: companyName,
                            testDate: result.testDate,
                            resultId: result.id,
                            countryName: country.name || ''
                        },
                        geometry
                    });
                });
            });

            return features;
        },

        loadDNALayers() {
            if (!this.mapInstance) return;

            // Remove existing DNA layers first
            this.removeDNALayers();

            // Get selected test results
            const selectedResults = dnaData.results.filter(result => {
                return result.testSubjectId === this.selectedDNASubjectId &&
                    result.company === this.selectedDNACompanyId;
            });

            if (selectedResults.length === 0) return;

            // Create a feature collection for each result with unified ethnicity groups
            selectedResults.forEach((result, resultIndex) => {
                const company = dnaData.companies[result.company];
                const companyName = company.name;
                const brandRedColor = '#c62828';

                // Prefer explicit region entries when present. Fallback to legacy ethnicity group mapping.
                const explicitRegionFeatures = this.buildDnaFeaturesFromRegions(result, companyName);
                const features = explicitRegionFeatures.length
                    ? explicitRegionFeatures
                    : this.buildDnaFeaturesFromEthnicityGroups(result, companyName);

                if (features.length === 0) return;

                const geojson = { type: 'FeatureCollection', features };
                const sourceId = `dna-regions-${resultIndex}`;
                const layerId = `dna-layer-${resultIndex}`;

                // Add source
                try {
                    this.mapInstance.addSource(sourceId, {
                        type: 'geojson',
                        data: geojson
                    });

                    // Determine where to insert DNA layers - before 'unclustered-point' if it exists
                    const referenceLayerId = this.mapInstance.getLayer('unclustered-point') ? 'unclustered-point' : undefined;

                    // Add fill layer with opacity based on percentage
                    this.mapInstance.addLayer({
                        id: layerId,
                        type: 'fill',
                        source: sourceId,
                        paint: {
                            'fill-color': brandRedColor,
                            'fill-opacity': [
                                'interpolate',
                                ['linear'],
                                ['get', 'percentage'],
                                1, 0.12,
                                5, 0.2,
                                20, 0.38,
                                40, 0.52,
                                70, 0.65
                            ]
                        }
                    }, referenceLayerId);

                    // Add outline layer
                    const outlineLayerId = `dna-outline-${resultIndex}`;
                    this.mapInstance.addLayer({
                        id: outlineLayerId,
                        type: 'line',
                        source: sourceId,
                        paint: {
                            'line-color': brandRedColor,
                            'line-width': 1.5,
                            'line-opacity': 0.75
                        }
                    }, referenceLayerId);

                    // Add hover effect
                    this.mapInstance.on('mouseenter', layerId, () => {
                        this.mapInstance.setFilter(outlineLayerId, [true]);
                        this.mapInstance.setPaintProperty(outlineLayerId, 'line-width', 2);
                        this.mapInstance.getCanvas().style.cursor = 'pointer';
                    });

                    this.mapInstance.on('mouseleave', layerId, () => {
                        this.mapInstance.setPaintProperty(outlineLayerId, 'line-width', 1);
                        this.mapInstance.getCanvas().style.cursor = '';
                    });

                    // Add popup on click
                    this.mapInstance.on('click', layerId, (e) => {
                        if (e.features.length === 0) return;
                        const feature = e.features[0];
                        new mapboxgl.Popup()
                            .setLngLat(e.lngLat)
                            .setHTML(`
                                <div style="font-size: 12px;">
                                    <strong>${feature.properties.groupLabel}</strong><br/>
                                    ${feature.properties.company}<br/>
                                    <strong>${this.formatDnaPercentage(feature.properties.percentage)}</strong>
                                </div>
                            `)
                            .addTo(this.mapInstance);
                    });

                    // Track layer IDs for cleanup
                    this.dnaLayerIds.push(layerId);
                    this.dnaLayerIds.push(outlineLayerId);
                    this.dnaLayerIds.push(sourceId);
                } catch (err) {
                    console.warn(`Failed to add DNA layer for result ${resultIndex}:`, err);
                }
            });
        },

        removeDNALayers() {
            if (!this.mapInstance) return;

            // Remove layers and sources
            this.dnaLayerIds.forEach(id => {
                // Check if it's a layer (not a source)
                if (this.mapInstance.getLayer(id)) {
                    this.mapInstance.removeLayer(id);
                } else if (this.mapInstance.getSource(id)) {
                    // Source will be removed when its layer is removed
                    this.mapInstance.removeSource(id);
                }
            });

            this.dnaLayerIds = [];
        },

        loadHaplogroupLayers() {
            if (!this.mapInstance) return;

            // Remove existing haplogroup layers first
            this.removeHaplogroupLayers();

            // Get selected haplogroups
            const selectedHaplogroups = this.availableHaplogroups.filter(hg =>
                this.selectedHaplogroupIds.includes(hg.id)
            );

            if (selectedHaplogroups.length === 0) return;

            selectedHaplogroups.forEach((haplogroup, index) => {
                const color = haplogroup.lineType === 'paternal' ? '#0085ca' : '#e0004d';

                // Create line features connecting haplogroup steps in chronological order
                const lineFeatures = [];
                for (let i = 0; i < haplogroup.steps.length - 1; i++) {
                    const current = haplogroup.steps[i];
                    const next = haplogroup.steps[i + 1];

                    lineFeatures.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [current.coordinates, next.coordinates]
                        },
                        properties: {
                            haplogroupId: haplogroup.id,
                            fromHaplogroup: current.haplogroup,
                            toHaplogroup: next.haplogroup,
                            fromAge: current.age,
                            toAge: next.age
                        }
                    });
                }

                // Create point features for each haplogroup step
                const pointFeatures = haplogroup.steps.map((step, stepIndex) => {
                    return {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: step.coordinates
                        },
                        properties: {
                            haplogroupId: haplogroup.id,
                            haplogroup: step.haplogroup,
                            age: step.age,
                            era: step.era,
                            location: step.location,
                            testedDescendants: step.testedDescendants,
                            ancientConnections: step.ancientConnections,
                            personName: haplogroup.personName,
                            lineType: haplogroup.lineType,
                            stepNumber: stepIndex + 1,
                            markerLabel: this.getHaplogroupMarkerLabel(step)
                        }
                    };
                });

                // Add line layer
                const lineSourceId = `haplogroup-lines-${index}`;
                const lineLayerId = `haplogroup-line-layer-${index}`;

                try {
                    this.mapInstance.addSource(lineSourceId, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: lineFeatures
                        }
                    });

                    // Add line layer above red markers
                    this.mapInstance.addLayer({
                        id: lineLayerId,
                        type: 'line',
                        source: lineSourceId,
                        paint: {
                            'line-color': color,
                            'line-width': 3,
                            'line-opacity': 0.6
                        },
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        }
                    });

                    // Add arrow symbols to show migration direction (youngest to oldest - reverse)
                    const arrowLayerId = `haplogroup-arrows-${index}`;
                    this.mapInstance.addLayer({
                        id: arrowLayerId,
                        type: 'symbol',
                        source: lineSourceId,
                        layout: {
                            'symbol-placement': 'line',
                            'symbol-spacing': 80,
                            'text-field': '←',
                            'text-size': 28,
                            'text-keep-upright': false,
                            'text-rotation-alignment': 'map',
                            'text-pitch-alignment': 'map'
                        },
                        paint: {
                            'text-color': color,
                            'text-opacity': 0.8,
                            'text-translate': [0, -10]
                        }
                    });

                    this.haplogroupLayerIds.push(arrowLayerId);

                    this.haplogroupLayerIds.push(lineLayerId);
                    this.haplogroupLayerIds.push(lineSourceId);
                } catch (err) {
                    console.warn(`Failed to add haplogroup line layer for ${haplogroup.id}:`, err);
                }

                // Add point layer
                const pointSourceId = `haplogroup-points-${index}`;
                const pointLayerId = `haplogroup-point-layer-${index}`;

                try {
                    this.mapInstance.addSource(pointSourceId, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: pointFeatures
                        }
                    });

                    const referenceLayerId = this.mapInstance.getLayer('unclustered-point') ? 'unclustered-point' : undefined;

                    // Add main marker layer
                    this.mapInstance.addLayer({
                        id: pointLayerId,
                        type: 'circle',
                        source: pointSourceId,
                        paint: {
                            'circle-radius': 12,
                            'circle-color': color,
                            'circle-stroke-width': 2,
                            'circle-stroke-color': '#ffffff',
                            'circle-opacity': 0.85,
                            'circle-pitch-alignment': 'map'
                        }
                    }, referenceLayerId);

                    // Add hover effect
                    this.mapInstance.on('mouseenter', pointLayerId, () => {
                        this.mapInstance.getCanvas().style.cursor = 'pointer';
                    });

                    this.mapInstance.on('mouseleave', pointLayerId, () => {
                        this.mapInstance.getCanvas().style.cursor = '';
                    });

                    // Add popup on click
                    this.mapInstance.on('click', pointLayerId, (e) => {
                        if (e.features.length === 0) return;
                        const feature = e.features[0];
                        const props = feature.properties;

                        // Format age to locale
                        const formattedAge = props.age.replace('BCE', this.$t('placeMap.bce')).replace('AD', this.$t('placeMap.ad'));
                        const translatedLocation = this.translateLocation(props.location);
                        const translatedEra = this.translateEra(props.era);
                        const lineType = props.lineType === 'paternal' ? 'y-dna' : 'mtdna';
                        const ftdnaUrl = `https://discover.familytreedna.com/${lineType}/${encodeURIComponent(props.haplogroup)}/story`;

                        new mapboxgl.Popup()
                            .setLngLat(e.lngLat)
                            .setHTML(`
                                <div style="font-size: 12px; max-width: 250px;">
                                    <strong>${props.haplogroup}</strong><br/>
                                    <strong>${formattedAge}</strong> (${translatedEra})<br/>
                                    <em>${translatedLocation}</em><br/>
                                    ${props.testedDescendants ? `<br/>${props.testedDescendants.toLocaleString()} ${this.$t('placeMap.testedDescendants')}` : ''}
                                    ${props.ancientConnections ? `<br/>${props.ancientConnections} ${this.$t('placeMap.ancientConnections')}` : ''}
                                    <br/><br/>
                                    <a href="${ftdnaUrl}" target="_blank" rel="noopener noreferrer" style="color: #c62828; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                                        ${this.$t('placeMap.moreInfo')}
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style="width: 12px; height: 12px; fill: #c62828;">
                                            <path d="M384 64C366.3 64 352 78.3 352 96C352 113.7 366.3 128 384 128L466.7 128L265.3 329.4C252.8 341.9 252.8 362.2 265.3 374.7C277.8 387.2 298.1 387.2 310.6 374.7L512 173.3L512 256C512 273.7 526.3 288 544 288C561.7 288 576 273.7 576 256L576 96C576 78.3 561.7 64 544 64L384 64zM144 160C99.8 160 64 195.8 64 240L64 496C64 540.2 99.8 576 144 576L400 576C444.2 576 480 540.2 480 496L480 416C480 398.3 465.7 384 448 384C430.3 384 416 398.3 416 416L416 496C416 504.8 408.8 512 400 512L144 512C135.2 512 128 504.8 128 496L128 240C128 231.2 135.2 224 144 224L224 224C241.7 224 256 209.7 256 192C256 174.3 241.7 160 224 160L144 160z"/>
                                        </svg>
                                    </a>
                                    <br/><br/>
                                    <small>${props.personName} • ${props.lineType === 'paternal' ? this.$t('placeMap.paternalLineDot') : this.$t('placeMap.maternalLineDot')}</small>
                                </div>
                            `)
                            .addTo(this.mapInstance);
                    });

                    // Add text labels showing haplogroup name and year
                    const labelLayerId = `haplogroup-label-layer-${index}`;
                    this.mapInstance.addLayer({
                        id: labelLayerId,
                        type: 'symbol',
                        source: pointSourceId,
                        layout: {
                            'text-field': ['get', 'markerLabel'],
                            'text-size': 11,
                            'text-allow-overlap': true,
                            'text-ignore-placement': false,
                            'text-pitch-alignment': 'map',
                            'text-max-width': 8,
                            'text-anchor': 'top',
                            'text-offset': [0, 1.7]
                        },
                        paint: {
                            'text-color': '#000000',
                            'text-halo-color': '#ffffff',
                            'text-halo-width': 2
                        }
                    });

                    this.haplogroupLayerIds.push(labelLayerId);
                    this.haplogroupLayerIds.push(pointLayerId);
                    this.haplogroupLayerIds.push(pointSourceId);
                } catch (err) {
                    console.warn(`Failed to add haplogroup point layer for ${haplogroup.id}:`, err);
                }
            });

        },

        removeHaplogroupLayers() {
            if (!this.mapInstance) return;

            // Remove layers and sources
            this.haplogroupLayerIds.forEach(id => {
                if (this.mapInstance.getLayer(id)) {
                    this.mapInstance.removeLayer(id);
                } else if (this.mapInstance.getSource(id)) {
                    this.mapInstance.removeSource(id);
                }
            });

            this.haplogroupLayerIds = [];
        },

        normalizeDnaPercentage(value) {
            if (typeof value === 'number') {
                return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
            }

            if (typeof value !== 'string') return 0;

            const trimmed = value.trim();
            if (!trimmed) return 0;

            // Accept manual formats like "61,3%" and "<1%".
            const cleaned = trimmed
                .replace('%', '')
                .replace(',', '.')
                .replace(/<\s*/g, '');

            const parsed = Number(cleaned);
            if (!Number.isFinite(parsed)) return 0;

            if (trimmed.includes('<')) {
                return Number((Math.max(parsed / 2, 0.1)).toFixed(2));
            }

            return Number(parsed.toFixed(2));
        },

        formatDnaPercentage(value) {
            const numeric = this.normalizeDnaPercentage(value);
            return `${numeric.toFixed(1)}%`;
        },

        formatHaplogroupAge(age) {
            if (!age) return '';
            // Convert BCE/AD to locale-appropriate format
            return age.replace('BCE', this.$t('placeMap.bce')).replace('AD', this.$t('placeMap.ad'));
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },

        getHaplogroupLineText(hg) {
            const lineText = hg.lineType === 'paternal' ? this.$t('placeMap.paternalLineMiddot') : this.$t('placeMap.maternalLineMiddot');
            return `${hg.personName} · ${lineText}`;
        },

        getHaplogroupMarkerLabel(step) {
            // Format: "I-BY62\n2250 v.Chr."
            const formattedAge = step.age.replace('BCE', this.$t('placeMap.bce')).replace('AD', this.$t('placeMap.ad')).replace(' ', '');
            return `${step.haplogroup}\n${formattedAge}`;
        },

        translateLocation(location) {
            const keyMap = {
                'Northern Europe': 'northernEurope',
                'Scandinavia': 'scandinavia',
                'Central Europe': 'centralEurope',
                'Balkans': 'balkans',
                'Eastern Mediterranean': 'easternMediterranean',
                'Middle East': 'middleEast',
                'West Asia': 'westAsia',
                'Northeast Africa': 'northeastAfrica',
                'East Africa': 'eastAfrica',
                'Japan': 'japan',
                'Korea': 'korea',
                'Southern China': 'southernChina',
                'Southwest China': 'southwestChina',
                'Northeast Asia': 'northeastAsia',
                'East Asia': 'eastAsia',
                'Southeast Asia': 'southeastAsia',
                'South Asia': 'southAsia',
                'East Africa/Middle East': 'eastAfricaMiddleEast'
            };
            const key = keyMap[location];
            if (key) return this.$t(`placeMap.locations.${key}`);
            return location;
        },

        translateEra(era) {
            const keyMap = {
                'Metal Ages': 'metalAges',
                'Stone Age': 'stoneAge',
                'Stone Age/Metal Ages': 'stoneAgeMetalAges',
                'Before Homo Sapiens': 'beforeHomoSapiens',
                'Before Homo Sapiens/Early Homo Sapiens': 'beforeHomoSapiensEarlyHomoSapiens',
                'Early Homo Sapiens/Stone Age': 'earlyHomoSapiensStoneAge'
            };
            const key = keyMap[era];
            if (key) return this.$t(`placeMap.eras.${key}`);
            return era;
        },

        flyToHaplogroupStep(step) {
            if (!this.mapInstance || !step.coordinates) return;

            // Stop rotation so the flyTo isn't overridden, then restart idle timer
            this.stopRotation();
            this.onUserInteraction();

            const [lng, lat] = step.coordinates;

            // Fly to the haplogroup step location
            this.mapInstance.flyTo({
                center: [lng, lat],
                zoom: 4,
                duration: 1500,
                essential: true
            });

            // Open popup after flying to location
            setTimeout(() => {
                // Close previous popup if it exists
                if (this.currentHaplogroupPopup) {
                    this.currentHaplogroupPopup.remove();
                    this.currentHaplogroupPopup = null;
                }

                // Find the haplogroup this step belongs to
                const haplogroup = this.availableHaplogroups.find(hg =>
                    hg.steps.some(s => s.haplogroup === step.haplogroup)
                );

                if (!haplogroup) return;

                const formattedAge = step.age.replace('BCE', this.$t('placeMap.bce')).replace('AD', this.$t('placeMap.ad'));
                const lineTypeText = haplogroup.lineType === 'paternal' ? this.$t('placeMap.paternalLineDot') : this.$t('placeMap.maternalLineDot');
                const translatedLocation = this.translateLocation(step.location);
                const translatedEra = this.translateEra(step.era);
                const lineType = haplogroup.lineType === 'paternal' ? 'y-dna' : 'mtdna';
                const ftdnaUrl = `https://discover.familytreedna.com/${lineType}/${encodeURIComponent(step.haplogroup)}/story`;

                this.currentHaplogroupPopup = new mapboxgl.Popup()
                    .setLngLat([lng, lat])
                    .setHTML(`
                        <div style="font-size: 12px; max-width: 250px;">
                            <strong>${step.haplogroup}</strong><br/>
                            <strong>${formattedAge}</strong> (${translatedEra})<br/>
                            <em>${translatedLocation}</em><br/>
                            ${step.testedDescendants ? `<br/>${step.testedDescendants.toLocaleString()} ${this.$t('placeMap.testedDescendants')}` : ''}
                            ${step.ancientConnections ? `<br/>${step.ancientConnections} ${this.$t('placeMap.ancientConnections')}` : ''}
                            <br/><br/>
                            <a href="${ftdnaUrl}" target="_blank" rel="noopener noreferrer" style="color: #c62828; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                                ${this.$t('placeMap.moreInfo')}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style="width: 12px; height: 12px; fill: #c62828;">
                                    <path d="M384 64C366.3 64 352 78.3 352 96C352 113.7 366.3 128 384 128L466.7 128L265.3 329.4C252.8 341.9 252.8 362.2 265.3 374.7C277.8 387.2 298.1 387.2 310.6 374.7L512 173.3L512 256C512 273.7 526.3 288 544 288C561.7 288 576 273.7 576 256L576 96C576 78.3 561.7 64 544 64L384 64zM144 160C99.8 160 64 195.8 64 240L64 496C64 540.2 99.8 576 144 576L400 576C444.2 576 480 540.2 480 496L480 416C480 398.3 465.7 384 448 384C430.3 384 416 398.3 416 416L416 496C416 504.8 408.8 512 400 512L144 512C135.2 512 128 504.8 128 496L128 240C128 231.2 135.2 224 144 224L224 224C241.7 224 256 209.7 256 192C256 174.3 241.7 160 224 160L144 160z"/>
                                </svg>
                            </a>
                            <br/><br/>
                            <small>${haplogroup.personName} • ${lineTypeText}</small>
                        </div>
                    `)
                    .addTo(this.mapInstance);
            }, 1600); // Slightly after the flyTo completes
        },

        flyToBreakdownRegion(row) {
            if (!this.mapInstance || !row.countries || row.countries.length === 0) return;

            // Close existing DNA popup before opening a new one
            if (this.currentDnaPopup) {
                this.currentDnaPopup.remove();
                this.currentDnaPopup = null;
            }

            // Stop rotation so the flyTo isn't overridden, then restart idle timer
            this.stopRotation();
            this.onUserInteraction();

            // Collect all geometries for these countries to compute a bbox
            const allLngs = [];
            const allLats = [];
            let hasGeometry = false;
            const countryNames = [];

            row.countries.forEach(code => {
                const country = dnaData.countries[code];
                if (!country) return;
                countryNames.push(country.name);

                // Try real border geometry first, then fallback rectangle
                const geom = this.dnaCountryGeometryByName[String(country.name || '').toLowerCase()]
                    || country.geometry;
                if (!geom) return;

                hasGeometry = true;
                // Extract all coordinates from the geometry
                const extractCoords = (coords) => {
                    if (typeof coords[0] === 'number') {
                        allLngs.push(coords[0]);
                        allLats.push(coords[1]);
                    } else {
                        coords.forEach(c => extractCoords(c));
                    }
                };
                extractCoords(geom.coordinates);
            });

            if (!hasGeometry || allLngs.length === 0 || allLats.length === 0) return;

            const minLat = Math.min(...allLats);
            const maxLat = Math.max(...allLats);
            const centerLat = (minLat + maxLat) / 2;

            // Dateline-safe longitude center/span for Pacific regions (Melanesia/Polynesia).
            const normalizedLngs = allLngs
                .map(lng => ((lng % 360) + 360) % 360)
                .sort((a, b) => a - b);

            let centerLng = 0;
            let lngSpan = 0;

            if (normalizedLngs.length === 1) {
                const only = normalizedLngs[0];
                centerLng = only > 180 ? only - 360 : only;
                lngSpan = 0;
            } else {
                let largestGap = -1;
                let largestGapIndex = -1;

                for (let i = 0; i < normalizedLngs.length; i++) {
                    const current = normalizedLngs[i];
                    const next = i === normalizedLngs.length - 1
                        ? normalizedLngs[0] + 360
                        : normalizedLngs[i + 1];
                    const gap = next - current;

                    if (gap > largestGap) {
                        largestGap = gap;
                        largestGapIndex = i;
                    }
                }

                const arcStart = normalizedLngs[(largestGapIndex + 1) % normalizedLngs.length];
                const arcEndRaw = normalizedLngs[largestGapIndex];
                // Only unwrap (add 360) when the arc crosses 0°/360°, i.e. arcEndRaw < arcStart.
                // For normal contiguous regions the wraparound gap is the largest, but arcEndRaw >= arcStart
                // so NO unwrapping is needed – adding 360 unconditionally was the bug.
                const arcEnd = arcEndRaw < arcStart ? arcEndRaw + 360 : arcEndRaw;
                let centerNorm = (arcStart + arcEnd) / 2;
                if (centerNorm >= 360) centerNorm -= 360;

                centerLng = centerNorm > 180 ? centerNorm - 360 : centerNorm;
                lngSpan = Math.max(360 - largestGap, 0);
            }

            // Compute a reasonable zoom based on the span
            const latSpan = maxLat - minLat;
            const maxSpan = Math.max(lngSpan, latSpan);
            let zoom = 3;
            if (maxSpan < 5) zoom = 5;
            else if (maxSpan < 15) zoom = 4;
            else if (maxSpan < 40) zoom = 3;
            else zoom = 2;

            this.mapInstance.flyTo({
                center: [centerLng, centerLat],
                zoom,
                duration: 1500,
                essential: true
            });

            // Open popup after camera motion starts
            setTimeout(() => {
                if (!this.mapInstance) return;
                const companyName = dnaData.companies[this.selectedDNACompanyId]?.name || 'DNA Test';
                this.currentDnaPopup = new mapboxgl.Popup({ closeButton: true })
                    .setLngLat([centerLng, centerLat])
                    .setHTML(`
                        <div style="font-size: 12px; max-width: 250px;">
                            <strong>${row.label}</strong><br/>
                            <strong>${row.display}</strong><br/>
                            <em>${countryNames.join(', ')}</em><br/>
                            <br/>
                            <small>${companyName}</small>
                        </div>
                    `)
                    .addTo(this.mapInstance);
            }, 800);
        },
    },

    async created() {
        if (!this.hideFilters && this.$route.query.mode === 'surnames') {
            this.displayMode = 'surnames';
        }

        if (!this.forceGlobeView) {
            // Override from URL query parameter if explicitly set
            const viewParam = this.$route.query.view;
            if (viewParam === 'map') {
                this.useGlobeProjection = false;
            } else if (viewParam === 'globe') {
                this.useGlobeProjection = true;
            }
            // Otherwise keep the default (true = globe)
        }

        await Promise.all([
            this.loadPlaceAreas(),
            this.loadPlaceGroups(),
            this.isSurnameMode ? this.loadSurnameLabels() : this.loadPlaces(),
        ]);

        this.renderActiveMapLayers();
        if (this.isSurnameMode) {
            // Keep place search available without delaying the first surname render.
            this.loadPlaces();
        }
    },
};
</script>

<style scoped>
.PlaceMap {
    height: 100vh !important;
    width: 100%;
    position: relative;
    overflow: hidden;
    z-index: 1;
}

.map {
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.map-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
}

.place-search-overlay {
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 995;
    width: auto;
}

.place-search-overlay .search-box {
    width: min(360px, calc(100vw - 48px));
    padding: 0 24px;
}

.place-search-overlay .search-box input {
    padding-top: 16px;
    padding-bottom: 16px;
}

.place-search-overlay .search-box .search-icon {
    margin-right: 12px;
}

.place-search-overlay .reset-button {
    position: static;
    transform: none;
    right: auto;
    top: auto;
}

.place-search-overlay .search-suggestions {
    top: 70px;
    width: 100%;
    max-height: min(calc(100vh - 145px - 24px), 300px);
}

.map-event-filter-overlay {
    position: absolute;
    bottom: 70px;
    left: 50%;
    z-index: 970;
    width: min(1120px, calc(100vw - 48px));
    transform: translateX(-50%);
    pointer-events: none;
}

.map-event-filter-row {
    display: flex;
    max-width: 100%;
    width: fit-content;
    margin: 0 auto;
    padding: 5px;
    gap: 12px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    border: 1px solid rgba(29, 29, 29, 0.08);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.14);
    pointer-events: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.map-event-filter-row::-webkit-scrollbar {
    display: none;
}

.map-event-filter-group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
}

.map-event-filter-group--scope {
    padding-right: 12px;
    border-right: 1px solid rgba(29, 29, 29, 0.1);
}

.map-event-filter-group--display {
    padding-right: 12px;
    border-right: 1px solid rgba(29, 29, 29, 0.1);
}

.map-event-filter-group--person {
    padding-left: 12px;
    border-left: 1px solid rgba(29, 29, 29, 0.1);
}

.map-filter-pill {
    position: relative;
    display: inline-flex;
    min-height: 30px;
    align-items: center;
    justify-content: center;
    padding: 6px 11px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: transparent;
    color: rgba(29, 29, 29, 0.76);
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
    white-space: nowrap;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        color 0.15s ease;
}

.map-filter-pill:hover {
    background: rgba(25, 83, 95, 0.08);
    color: var(--brand-color-1);
}

.map-filter-pill--active {
    border-color: var(--brand-color-1);
    background: var(--brand-color-1);
    color: #fff;
    box-shadow: 0 2px 7px rgba(25, 83, 95, 0.22);
}

.map-filter-pill__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.map-filter-pill:focus-within {
    outline: 3px solid rgba(25, 83, 95, 0.28);
    outline-offset: 2px;
}

.surname-landscape-state {
    position: absolute;
    top: 122px;
    left: 50%;
    z-index: 970;
    max-width: min(520px, calc(100vw - 40px));
    padding: 10px 15px;
    color: #4f4a47;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid rgba(29, 29, 29, 0.1);
    border-radius: var(--border-radius-md);
    box-shadow: 0 5px 18px rgba(0, 0, 0, 0.13);
    transform: translateX(-50%);
}

.map-person-filter-chip {
    display: inline-flex;
    max-width: 190px;
    min-height: 30px;
    align-items: center;
    gap: 7px;
    padding: 5px 7px 5px 11px;
    border: 1px solid rgba(25, 83, 95, 0.24);
    border-radius: 999px;
    background: rgba(25, 83, 95, 0.08);
    color: var(--brand-color-1);
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
}

.map-person-filter-chip span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.map-person-filter-chip button {
    display: inline-flex;
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(25, 83, 95, 0.12);
    color: inherit;
    cursor: pointer;
    font: inherit;
    line-height: 1;
}

.map-person-filter-chip button:hover,
.map-person-filter-chip button:focus-visible {
    background: var(--brand-color-1);
    color: #fff;
    outline: none;
}

.map-search-suggestions {
    overflow-x: hidden;
    overflow-y: auto;
}

.map-search-suggestions__separator {
    width: calc(100% - 48px);
    height: 0;
    border-top: 1px solid var(--brand-bg-2);
    margin: 14px auto;
    padding: 0;
    padding-bottom: 0;
    cursor: default;
}

.map-search-suggestions li {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 7px;
    color: #1D1D1D;
}

.map-search-suggestions li:not(.map-search-suggestions__heading):not(.map-search-suggestions__status):not(.map-search-suggestions__separator) {
    padding: 10px 24px;
}

.map-search-suggestions li:not(.map-search-suggestions__heading):not(.map-search-suggestions__status):not(.map-search-suggestions__separator):hover,
.map-search-suggestions li:not(.map-search-suggestions__heading):not(.map-search-suggestions__status):not(.map-search-suggestions__separator):focus {
    outline: none;
    background-color: #f0f0f0;
    text-decoration: underline;
}

.map-search-suggestions span {
    max-width: 100%;
    overflow: hidden;
    color: #1D1D1D;
    line-height: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.map-search-suggestions span:nth-of-type(1) {
    font-size: 16px;
    font-weight: 600;
}

.map-search-suggestions span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

.map-search-suggestions__heading,
.map-search-suggestions__status {
    cursor: default;
    padding: 7px 24px;
}

.map-search-suggestions__heading {
    color: #777;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.2;
    text-transform: uppercase;
}

.map-search-suggestions__status span {
    font-size: 13px;
    font-weight: 500;
    opacity: .6;
}

.map-search-suggestions__heading:hover,
.map-search-suggestions__status:hover {
    background: transparent;
    text-decoration: none;
}

.tour-launch-overlay {
    position: absolute;
    top: 54px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 995;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: min(620px, calc(100vw - 32px));
}

.tour-mode-picker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    max-width: 100%;
    padding: 4px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 8px;
    background: rgba(12, 16, 24, 0.74);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(12px);
}

.tour-mode-picker__button {
    min-height: 30px;
    padding: 7px 10px;
    border: none;
    border-radius: 5px;
    background: transparent;
    color: rgba(255, 255, 255, 0.72);
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    transition: background-color 0.18s ease, color 0.18s ease;
    white-space: nowrap;
}

.tour-mode-picker__button:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.10);
}

.tour-mode-picker__button--active {
    color: #111827;
    background: #f5c451;
}

.tour-launch-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 38px;
    padding: 9px 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 6px;
    background: rgba(20, 24, 32, 0.82);
    color: #fff;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(12px);
    transition: transform 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}

.tour-launch-button:hover {
    background: rgba(198, 40, 40, 0.9);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.34);
    transform: translateY(-1px);
}

.tour-launch-button img {
    width: 18px;
    height: 18px;
    filter: brightness(0) invert(1);
}

.place-tour-panel {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 996;
    width: min(520px, calc(100vw - 48px));
    padding: 18px;
    color: #fff;
    background:
        linear-gradient(135deg, rgba(245, 196, 81, 0.12), rgba(13, 17, 24, 0) 38%),
        rgba(13, 17, 24, 0.90);
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    box-shadow: 0 22px 60px rgba(0, 0, 0, 0.42);
    backdrop-filter: blur(16px);
}

.place-tour-panel__hud {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.place-tour-panel__mode,
.place-tour-panel__phase {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 4px 8px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
}

.place-tour-panel__mode {
    color: #111827;
    background: #f5c451;
}

.place-tour-panel__phase {
    margin-left: 6px;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
}

.place-tour-panel__readouts {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.1;
    text-transform: uppercase;
}

.place-tour-panel__kicker {
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1.2;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.place-tour-panel__title {
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.18;
    margin: 0 0 8px;
}

.place-tour-panel__placenames {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
}

.place-tour-panel__placenames span {
    display: inline-flex;
    min-height: 24px;
    align-items: center;
    padding: 4px 8px;
    border-radius: 5px;
    color: rgba(255, 255, 255, 0.78);
    background: rgba(255, 255, 255, 0.08);
    font-size: 11px;
    font-weight: 700;
}

.place-tour-panel__mode-description {
    color: rgba(245, 196, 81, 0.92);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
    margin: 0 0 8px;
}

.place-tour-panel__summary {
    color: rgba(255, 255, 255, 0.82);
    font-size: 14px;
    line-height: 1.45;
    margin: 0;
}

.place-tour-panel__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 16px;
}

.place-tour-panel__stat {
    min-width: 0;
    padding: 10px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
}

.place-tour-panel__stat-value,
.place-tour-panel__stat-label {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.place-tour-panel__stat-value {
    color: #fff;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.1;
}

.place-tour-panel__stat-label {
    color: rgba(255, 255, 255, 0.64);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    margin-top: 5px;
    text-transform: uppercase;
}

.place-tour-panel__route-bar {
    height: 5px;
    margin-top: 16px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    overflow: hidden;
}

.place-tour-panel__route-bar span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #f5c451, #fff0ba);
    transition: width 0.35s ease;
}

.place-tour-panel__progress {
    display: flex;
    gap: 6px;
    margin-top: 10px;
}

.place-tour-panel__dot {
    display: block;
    height: 3px;
    flex: 1;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.22);
}

.place-tour-panel__dot--active {
    background: #fff;
}

.place-tour-panel__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
}

.place-tour-panel__icon-button,
.place-tour-panel__primary-button,
.place-tour-panel__secondary-button {
    min-height: 36px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.place-tour-panel__icon-button {
    width: 38px;
    flex: 0 0 38px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
}

.place-tour-panel__primary-button {
    flex: 1 1 86px;
    border: 1px solid #fff;
    background: #fff;
    color: #111827;
}

.place-tour-panel__secondary-button {
    flex: 1 1 116px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
}

.place-tour-panel__icon-button:hover,
.place-tour-panel__secondary-button:hover {
    background: rgba(255, 255, 255, 0.16);
}

.place-tour-panel__primary-button:hover {
    background: #f1f1f1;
}

@media (max-width: 768px) {
    .tour-launch-overlay {
        top: 58px;
        width: calc(100vw - 24px);
    }

    .tour-mode-picker {
        width: 100%;
        overflow-x: auto;
        justify-content: flex-start;
    }

    .tour-launch-button {
        width: 100%;
    }

    .place-tour-panel {
        right: 12px;
        bottom: 12px;
        width: calc(100vw - 24px);
        padding: 14px;
    }

    .place-tour-panel__title {
        font-size: 20px;
    }

    .place-tour-panel__summary {
        font-size: 13px;
    }

    .place-tour-panel__hud {
        flex-direction: column;
    }

    .place-tour-panel__readouts {
        align-items: flex-start;
        flex-direction: row;
        flex-wrap: wrap;
    }

    .place-tour-panel__stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .place-tour-panel__stat--wide {
        grid-column: 1 / -1;
    }
}

.map-loader {
    height: calc(100vh - 110px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(90deg, #ccc 25%, #ddd 50%, #ccc 75%);
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
}

@media (max-width: 768px) {
    .place-search-overlay .search-box {
        width: calc(100vw - 40px);
    }

    .map-event-filter-overlay {
        bottom: 70px;
        width: calc(100vw - 40px);
    }

    .map-event-filter-row {
        width: 100%;
        margin: 0;
    }

    .map-filter-pill {
        min-height: 28px;
        padding: 6px 10px;
        font-size: 11px;
    }
}

/* Optional: counts inside popup */
.counts {
    margin-top: 8px;
    font-size: 12px;
    opacity: 0.85;
}

.toggle-switch {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
}

.toggle-switch input {
    display: none;
}

.toggle-slider {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    background-color: #ccc;
    border-radius: var(--border-radius-lg);
    transition: background-color 0.3s ease;
    flex-shrink: 0;
}

.toggle-switch input:checked+.toggle-slider {
    background-color: var(--brand-color-1, #1f6feb);
}

.toggle-slider::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: white;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked+.toggle-slider::after {
    left: 22px;
}

.toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: #555;
}

.map-mode-overlay {
    position: absolute;
    bottom: 24px;
    left: 22px;
    z-index: 940;
    width: 78px;
}

.map-mode-overlay--open {
    z-index: 960;
}

.map-mode-trigger {
    position: relative;
    display: flex;
    width: 68px;
    height: 64px;
    padding: 0;
    overflow: hidden;
    border: 2px solid transparent;
    border-radius: 6px;
    background: #fff;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
    color: #222;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.map-mode-trigger:hover {
    border-color: rgba(25, 83, 95, 0.55);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.18);
}

.map-mode-trigger:focus-visible,
.map-layer-option:focus-visible,
.map-layer-menu__toggle:focus-within {
    outline: 3px solid rgba(25, 83, 95, 0.34);
    outline-offset: 2px;
}

.map-mode-tile__preview {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    background: #e4e7e2;
}

.map-mode-tile__preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.map-mode-tile__preview--map::after {
    position: absolute;
    inset: 12px;
    border: 2px solid rgba(255, 255, 255, 0.72);
    border-radius: 2px;
    content: "";
    pointer-events: none;
}

.map-mode-tile__preview--globe img {
    height: 100%;
    width: 100%;
    box-shadow: inset 0 0 0 999px rgba(14, 20, 36, 0.12);
}

.map-mode-tile__label {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    min-height: 22px;
    padding: 4px 5px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.72));
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.15;
    text-align: center;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.72);
}

.map-layer-menu {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 0;
    width: 230px;
    padding: 12px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.28);
}

.map-layer-menu__title {
    margin: 0 0 10px;
    color: #333;
    font-size: 13px;
    font-weight: 700;
}

.map-layer-menu__tiles {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.map-layer-option {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0 0 7px;
    border: 2px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
    color: #333;
    cursor: pointer;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;
}

.map-layer-option:hover {
    border-color: #aaa;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.map-layer-option--active {
    border-color: var(--brand-color-1, #1f6feb);
    box-shadow: 0 0 0 1px var(--brand-color-1, #1f6feb);
}

.map-layer-option__preview {
    display: block;
    height: 62px;
    background: #e4e7e2;
}

.map-layer-option__preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.map-layer-menu__toggle {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e6e6e6;
    color: #333;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    user-select: none;
}

.map-layer-menu__toggle input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}

.map-layer-menu__toggle input:checked+.toggle-slider {
    background-color: var(--brand-color-1, #1f6feb);
}

.map-layer-menu__toggle input:checked+.toggle-slider::after {
    left: 22px;
}

/* Mapbox-specific styles */
:deep(.mapbox-popup) {
    font-family: var(--font-primary);
    font-size: 14px;
    padding: 8px;
}

:deep(.mapbox-popup .popup-text) {
    font-weight: 700;
    margin-bottom: 8px;
    margin-top: 0;
}

:deep(.mapbox-popup .popup-link) {
    color: var(--brand-color-1);
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    margin-top: 4px;
}

:deep(.mapbox-popup .popup-link:hover) {
    text-decoration: underline;
}

:deep(.mapbox-popup .popup-link--button) {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
    font-size: inherit;
    text-align: left;
}

:deep(.mapbox-popup .popup-link--button:hover) {
    text-decoration: underline;
}

:deep(.mapbox-popup .counts) {
    margin-top: 8px;
    font-size: 12px;
    opacity: 0.85;
}

:deep(.mapbox-popup .counts div) {
    margin-top: 4px;
}

.place-stats-line {
    position: absolute;
    right: 132px;
    bottom: 24px;
    left: 112px;
    z-index: 930;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0;
    color: rgba(255, 255, 255, 0.86);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    pointer-events: none;
    text-align: center;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.72);
}

.place-stats-line__label {
    color: rgba(255, 255, 255, 0.64);
    font-weight: 500;
}

.place-stats-line__label::after,
.place-stats-line__item:not(:last-child)::after {
    content: "·";
    padding: 0 7px;
    color: rgba(255, 255, 255, 0.56);
}

@media (max-width: 768px) {
    .map-mode-overlay {
        bottom: 24px;
        left: 12px;
        width: 68px;
    }

    .map-mode-trigger {
        width: 62px;
        height: 58px;
    }

    .map-layer-menu {
        width: min(230px, calc(100vw - 24px));
    }

    .map-mode-tile__label {
        font-size: 10px;
    }

    .place-stats-line {
        right: 12px;
        bottom: 24px;
        left: 88px;
        font-size: 12px;
    }
}

@media (max-width: 420px) {
    .place-stats-line {
        justify-content: flex-start;
        text-align: left;
    }
}

/* Marker styles */
.marker-pin {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.marker-pin:hover {
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
}

/* Shimmer loader animation */
@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

:deep(.mapboxgl-popup-content) {
    padding: 12px !important;
    border-radius: var(--border-radius-md);
}

:deep(.mapboxgl-popup-tip) {
    border-top-color: #fff;
}

/* DNA ETHNICITY FILTER STYLES */
.dna-filter {
    width: 100%;
    margin-top: 4px;
}

/* DNA BREAKDOWN TABLE */
.dna-breakdown-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 12px;
}

.dna-breakdown-table tr {
    height: 26px;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.dna-breakdown-table tr:hover {
    background-color: rgba(198, 40, 40, 0.08);
}

.dna-breakdown-label {
    padding: 2px 6px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 0;
}

.tree-char {
    font-family: monospace;
    color: #999;
    margin-right: 4px;
    white-space: pre;
}

.dna-level-1 .dna-breakdown-label {
    font-weight: 600;
}

.dna-level-2 .dna-breakdown-label {
    font-weight: 500;
    font-size: 11px;
}

.dna-level-3 .dna-breakdown-label {
    font-weight: 400;
    font-size: 10.5px;
    color: #555;
}

.dna-level-4 .dna-breakdown-label {
    font-weight: 400;
    font-size: 10px;
    color: #777;
}

.dna-breakdown-pct {
    padding: 2px 0 2px 4px;
    text-align: right;
    font-weight: 600;
    color: #555;
    white-space: nowrap;
    width: 56px;
}

/* HAPLOGROUP FILTER */
.haplogroup-filter {
    width: 100%;
    margin-top: 4px;
}

.haplogroup-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 0;
}

.haplogroup-meta {
    font-size: 12px;
    color: #666;
    margin: 0;
    line-height: 1.4;
}

.haplogroup-meta:first-child {
    color: #333;
    font-weight: 500;
}

/* HAPLOGROUP TABLE */
.haplogroup-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.haplogroup-table tr {
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.haplogroup-table tr:hover {
    background-color: rgba(198, 40, 40, 0.08);
}

.haplogroup-name {
    padding: 6px 4px 6px 0;
    color: #333;
    font-weight: 600;
    white-space: nowrap;
    font-size: 11px;
}

.haplogroup-age {
    padding: 6px 4px;
    color: #666;
    white-space: nowrap;
    font-size: 11px;
}

.haplogroup-location {
    display: none;
}


/* Ripple effect for cluster clicks */
.map-ripple {
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    pointer-events: none;
    animation: ripple-animation 1s ease-out;
    z-index: 1000;
}

@keyframes ripple-animation {
    0% {
        width: 20px;
        height: 20px;
        opacity: 1;
    }

    100% {
        width: 150px;
        height: 150px;
        opacity: 0;
    }
}

/* Enhanced marker glow */
:deep(.mapboxgl-canvas) {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
}

/* Atmospheric glow overlay */
.map-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    background: radial-gradient(circle at center, transparent 30%, rgba(36, 92, 223, 0.1) 70%, rgba(8, 8, 20, 0.4) 100%);
    z-index: 1;
}

/* Person filter autocomplete styles */
.person-filter {
    position: relative;
}

.person-filter-input-group {
    position: relative;
    display: flex;
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

.person-input {
    flex: 1;
    min-width: 0;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.2s ease;
}

.person-input:focus {
    outline: none;
    border-color: #c62828;
    box-shadow: 0 0 0 2px rgba(198, 40, 40, 0.1);
}

.person-suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
    padding: 0;
}

.person-suggestion-item {
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.15s ease;
}

.person-suggestion-item:last-child {
    border-bottom: none;
}

.person-suggestion-item:hover {
    background-color: #f5f5f5;
}

.person-suggestion-item:active {
    background-color: #efefef;
}

.person-name {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 2px;
}

.person-meta {
    font-size: 12px;
    color: #888;
    display: flex;
    gap: 8px;
    align-items: center;
}

.person-id {
    /* font-family: monospace; */
    background-color: #f0f0f0;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 500;
}

.person-dates {
    color: #666;
}
</style>
